// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Float, Stars, Text } from '@react-three/drei';
import * as THREE from 'three';
import type { SceneProps } from './sceneTypes';
import { rgb } from './sceneTypes';
import {
  AlbumTile,
  colorVector,
  ConnectionLine,
  musicalPulse,
  sceneJourney,
  sceneTuning,
  seededUnit,
} from './sceneUtils';
import { createCathedral3DProcessor } from './processors/cathedral3DProcessor';
import {
  drawCanvasVisualizer,
  makeCanvasVisualizerState,
  type CanvasVisualizerKind,
} from './canvasVisualizers';

function motionSpeedFor(tune: Record<string, number>, intensity = 0, cap = 0.72) {
  return Math.min(tune.speed ?? 1, cap + Math.min(1, intensity) * 0.08);
}

function motionCap(value: number, cap = 0.82) {
  return THREE.MathUtils.clamp(value, 0, cap);
}

const RAINBOW_TUNNEL_HUE_OFFSETS: Record<string, number> = {
  circle: 0,
  hex: 62,
  oct: 128,
  tri: 196,
  poly: 284,
};
const RAINBOW_TUNNEL_COLOR = new THREE.Color();

function setRainbowInstanceColor(mesh: THREE.InstancedMesh | null, index: number, hue: number, saturation = 0.92, lightness = 0.62) {
  if (!mesh) return;
  RAINBOW_TUNNEL_COLOR.setHSL(((hue % 360) + 360) / 360, saturation, lightness);
  mesh.setColorAt(index, RAINBOW_TUNNEL_COLOR);
}

function continuousBeat(beat: SceneProps['beat']) {
  return beat.beatIndex + beat.beatPhase;
}

function smoothLaneAccent(index: number, beatClock: number, lanes = 4) {
  const phase = beatClock * ((Math.PI * 2) / lanes) - index * 0.72;
  return 0.5 + Math.sin(phase) * 0.5;
}

function dampFrameValue(current: number, target: number, delta: number, drag = 0.0012) {
  return THREE.MathUtils.lerp(current, target, 1 - Math.pow(drag, Math.min(delta, 0.05)));
}

function useSmoothedFrameValues() {
  const values = useRef<Record<string, number>>({});
  return (key: string, target: number, delta: number, drag = 0.0012) => {
    const current = values.current[key] ?? target;
    const next = dampFrameValue(current, target, delta, drag);
    values.current[key] = next;
    return next;
  };
}

function useViewportContainTransform(contentWidth = 12, contentHeight = 6.75, fill = 0.98, z = -1.8) {
  const { camera, viewport } = useThree();
  const visible = viewport.getCurrentViewport(camera, new THREE.Vector3(0, 0, z));
  const scaleX = (visible.width / contentWidth) * fill;
  const scaleY = (visible.height / contentHeight) * fill;
  const uniform = Math.min(scaleX, scaleY);
  const visibleAspect = visible.width / visible.height;
  const contentAspect = contentWidth / contentHeight;
  const preserveAspect = Math.abs(Math.log(visibleAspect / contentAspect)) < 0.28;
  return preserveAspect ? { x: uniform, y: uniform } : { x: scaleX, y: scaleY };
}

function useViewportPlaneBounds(fill = 0.98, z = -1.8) {
  const { camera, viewport, size } = useThree();
  const visible = viewport.getCurrentViewport(camera, new THREE.Vector3(0, 0, z));
  return {
    width: visible.width * fill,
    height: visible.height * fill,
    pixelWidth: size.width,
    pixelHeight: size.height,
  };
}

function NeonTunnel(props: SceneProps) {
  const { palette, intensity, beat, section } = props;
  const isRainbowTunnel = props.params.tunnelColorMode === 'rainbow';
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const group = useRef<THREE.Group>(null);
  const frameRefs = useRef<Record<string, THREE.InstancedMesh | null>>({});
  const railRef = useRef<THREE.InstancedMesh>(null);
  const accentRef = useRef<THREE.InstancedMesh>(null);
  const particlesRef = useRef<THREE.Points>(null);
  const particlePositionsRef = useRef<Float32Array | null>(null);
  const shockRef = useRef<THREE.Mesh>(null);
  const travelRef = useRef(0);
  const beatPulseRef = useRef(0);
  const musicDriveRef = useRef(0);
  const surgeRef = useRef(0);
  const shockRefValue = useRef(0);
  const rainbowColorKeyRef = useRef('');
  const frameCount = Math.max(54, Math.round(76 * tune.density));
  const railCount = Math.max(14, Math.round(20 * tune.geometry));
  const railSegmentCount = 28;
  const particleCount = Math.max(5200, Math.round(7600 * tune.density));
  const accentCount = Math.max(8, Math.round(16 * tune.density));
  const tunnelDepth = 92 * tune.depth;
  const matrix = useMemo(() => new THREE.Matrix4(), []);
  const q = useMemo(() => new THREE.Quaternion(), []);
  const v = useMemo(() => new THREE.Vector3(), []);
  const pointA = useMemo(() => new THREE.Vector3(), []);
  const pointB = useMemo(() => new THREE.Vector3(), []);
  const pointMid = useMemo(() => new THREE.Vector3(), []);
  const pointDir = useMemo(() => new THREE.Vector3(), []);
  const particlePoint = useMemo(() => new THREE.Vector3(), []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const frameSets = useMemo(() => {
    const keys = ['circle', 'hex', 'oct', 'tri', 'poly'];
    const data = keys.reduce((acc, key) => ({ ...acc, [key]: [] }), {} as Record<string, any[]>);
    for (let i = 0; i < frameCount; i++) {
      const key = keys[i % keys.length];
      data[key].push({
        i,
        raw: (i / frameCount) * tunnelDepth,
        scale: 0.82 + seededUnit(i, 2) * 0.38,
        twist: (seededUnit(i, 3) - 0.5) * 0.8,
        spacing: (seededUnit(i, 5) - 0.5) * 0.9,
      });
    }
    return data;
  }, [frameCount, tunnelDepth]);
  const rails = useMemo(() => Array.from({ length: railCount }, (_, i) => i), [railCount]);
  const railSegments = useMemo(() => Array.from({ length: railSegmentCount }, (_, i) => i), []);
  const accents = useMemo(() => Array.from({ length: accentCount }, (_, i) => ({
    raw: seededUnit(i, 11) * tunnelDepth,
    angle: seededUnit(i, 12) * Math.PI * 2,
    radius: 0.62 + seededUnit(i, 13) * 1.25,
    scale: 0.08 + seededUnit(i, 14) * 0.16,
    spin: 0.3 + seededUnit(i, 15) * 0.9,
  })), [accentCount, tunnelDepth]);
  const particleSeeds = useMemo(() => Array.from({ length: particleCount }, (_, i) => ({
    raw: seededUnit(i, 21) * tunnelDepth,
    angle: seededUnit(i, 22) * Math.PI * 2,
    radius: 0.18 + Math.pow(seededUnit(i, 23), 0.65) * 3.9,
    speed: 0.35 + Math.pow(seededUnit(i, 24), 2) * 2.8,
    drift: seededUnit(i, 25) * Math.PI * 2,
    streak: seededUnit(i, 26) > 0.72 ? 1 : 0,
  })), [particleCount, tunnelDepth]);
  const particlePositions = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    particlePositionsRef.current = positions;
    return positions;
  }, [particleCount]);
  const tunnelBufferKey = `${props.visualStyle}-${isRainbowTunnel ? 'rainbow' : 'palette'}-${frameCount}-${railCount}-${particleCount}-${accentCount}-${Math.round(tunnelDepth * 100)}`;
  const stage = section === 'drop'
    ? 1
    : section === 'build'
      ? 0.74
      : section === 'chorus'
        ? 0.62
        : section === 'intro' || section === 'outro'
          ? 0.24
          : 0.42;

  useFrame((state, delta) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    if (isRainbowTunnel && rainbowColorKeyRef.current !== tunnelBufferKey) {
      Object.entries(frameSets).forEach(([key, frames]) => {
        const mesh = frameRefs.current[key];
        if (!mesh) return;
        frames.forEach((frame, instanceIndex) => {
          setRainbowInstanceColor(
            mesh,
            instanceIndex,
            frame.raw * 5.4 + frame.i * 9 + (RAINBOW_TUNNEL_HUE_OFFSETS[key] ?? 0),
            0.95,
            0.62
          );
        });
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      });

      if (railRef.current) {
        let railColorIndex = 0;
        rails.forEach((_, spokeIndex) => {
          railSegments.forEach((_, segmentIndex) => {
            const rawA = 1.2 + (segmentIndex / railSegmentCount) * (tunnelDepth - 8);
            setRainbowInstanceColor(railRef.current, railColorIndex, rawA * 6.8 + spokeIndex * 18, 1, 0.62);
            railColorIndex += 1;
          });
        });
        if (railRef.current.instanceColor) railRef.current.instanceColor.needsUpdate = true;
      }

      if (accentRef.current) {
        accents.forEach((accent, i) => {
          setRainbowInstanceColor(accentRef.current, i, accent.raw * 7.6 + i * 31, 1, 0.66);
        });
        if (accentRef.current.instanceColor) accentRef.current.instanceColor.needsUpdate = true;
      }

      rainbowColorKeyRef.current = tunnelBufferKey;
    }
    const beatClock = continuousBeat(beat);
    const micEnergy = typeof props.params.micEnergy === 'number' ? props.params.micEnergy : 0;
    const micOnset = typeof props.params.micOnset === 'number' ? props.params.micOnset : 0;
    const beatAttack = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.35) * intensity;
    const beatTarget = Math.max(
      music.groove * 0.65,
      music.impact,
      music.phrase * 0.9,
      beatAttack * 0.42,
      micEnergy * 1.25,
      micOnset * 2.2
    );
    beatPulseRef.current = THREE.MathUtils.lerp(beatPulseRef.current, beatTarget, 1 - Math.pow(0.0007, delta));
    musicDriveRef.current = THREE.MathUtils.lerp(
      musicDriveRef.current,
      Math.min(1.65, beatTarget * (0.9 + stage * 0.24)),
      1 - Math.pow(0.00045, delta)
    );
    surgeRef.current = THREE.MathUtils.lerp(
      surgeRef.current,
      Math.max(music.impact * 0.9, music.phrase * 1.1, micOnset * 1.8),
      1 - Math.pow(0.00028, delta)
    );
    const pulse = beatPulseRef.current * tune.flash;
    const musicDrive = musicDriveRef.current * tune.flash;
    const surge = surgeRef.current * tune.flash;
    const dropHit = section === 'drop' ? pulse : 0;
    const motionSpeed = motionSpeedFor(tune, intensity, 0.56);
    const motionDrive = motionCap(musicDrive, 0.72);
    const motionSurge = motionCap(surge, 0.58);
    const motionDrop = motionCap(dropHit, 0.5);
    const speedState = 0.36 + stage * 0.82 + journey.peak * 0.24 + motionDrop * 0.34 + motionDrive * 0.16 + motionSurge * 0.12;
    travelRef.current = (travelRef.current + delta * speedState * (3.25 + intensity * 1.2) * motionSpeed) % tunnelDepth;
    shockRefValue.current = THREE.MathUtils.lerp(shockRefValue.current, dropHit, 1 - Math.pow(0.0004, delta));
    const shock = shockRefValue.current;
    const bend = (0.08 + journey.reveal * 0.24 + stage * 0.18 + shock * 0.42 + musicDrive * 0.11) * tune.depth;
    const widthState = 0.92 + stage * 0.18 + shock * 0.78 + musicDrive * 0.1 + surge * 0.16 - (section === 'build' ? journey.chapterProgress * 0.12 : 0);
    const pointOnTunnel = (raw: number, angle: number, radiusBoost = 1, target = pointA) => {
      const near = 1 - raw / tunnelDepth;
      const turnA = Math.sin(raw * 0.062 + journey.turn * 1.2);
      const turnB = Math.cos(raw * 0.047 + journey.turn * 0.86);
      const farBias = 0.12 + raw / tunnelDepth;
      const centerX = turnA * bend * farBias;
      const centerY = turnB * bend * 0.62 * farBias;
      const radius = (1.45 * (0.36 + near * 1.42) + 0.18) * widthState * radiusBoost;
      return target.set(
        centerX + Math.cos(angle) * radius,
        centerY + Math.sin(angle) * radius,
        3.4 - raw
      );
    };
    const centerOnTunnel = (raw: number, target = pointA) => {
      const turnA = Math.sin(raw * 0.062 + journey.turn * 1.2);
      const turnB = Math.cos(raw * 0.047 + journey.turn * 0.86);
      const farBias = 0.12 + raw / tunnelDepth;
      return target.set(
        turnA * bend * farBias,
        turnB * bend * 0.62 * farBias,
        3.4 - raw
      );
    };

    Object.entries(frameSets).forEach(([key, frames]) => {
      const mesh = frameRefs.current[key];
      if (!mesh) return;
      frames.forEach((frame, instanceIndex) => {
        const raw = (frame.raw + frame.spacing - travelRef.current + tunnelDepth) % tunnelDepth;
        const p = centerOnTunnel(raw, pointA);
        const near = 1 - raw / tunnelDepth;
        const farFocus = THREE.MathUtils.smoothstep(raw, 9, tunnelDepth * 0.68);
        const fadeFar = 1 - THREE.MathUtils.smoothstep(raw, tunnelDepth - 20, tunnelDepth - 1.5);
        const localWave = Math.sin(raw * 0.23 - beatClock * 0.42 + frame.i * 0.21) * 0.5 + 0.5;
        const impactLane = 0.28 + smoothLaneAccent(frame.i, beatClock, 4) * 0.72;
        const framePulse = 1 + shock * 0.18 + musicDrive * (0.055 + localWave * 0.035) + surge * impactLane * 0.08;
        dummy.position.copy(p);
        dummy.rotation.set(
          Math.sin(raw * 0.033 + journey.turn + musicDrive * 0.28) * (0.12 + musicDrive * 0.025),
          Math.cos(raw * 0.027 + journey.turn + surge * 0.18) * (0.15 + musicDrive * 0.03),
          frame.twist + t * (0.055 + stage * 0.025 + motionDrive * 0.012) * motionSpeed + raw * 0.016 + motionSurge * 0.018 * impactLane
        );
        const passedCamera = raw < 0.08;
        const frameScale = passedCamera
          ? 0
          : (0.12 + near * 4.85) * widthState * frame.scale * framePulse * fadeFar * (1 - farFocus * 0.24);
        dummy.scale.setScalar(frameScale);
        dummy.updateMatrix();
        mesh.setMatrixAt(instanceIndex, dummy.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      mesh.material.opacity = Math.min(0.86, 0.44 + intensity * 0.14 + stage * 0.1 + shock * 0.14 + musicDrive * 0.11 + surge * 0.08);
    });

    let railIndex = 0;
    rails.forEach((rail, spokeIndex) => {
      const angle = (spokeIndex / rails.length) * Math.PI * 2;
      railSegments.forEach((_, segmentIndex) => {
        if (!railRef.current) return;
        const rawA = 1.2 + (segmentIndex / railSegmentCount) * (tunnelDepth - 8);
        const rawB = 1.2 + ((segmentIndex + 1) / railSegmentCount) * (tunnelDepth - 8);
        const beatTravel = beatClock * (1.05 + musicDrive * 0.18);
        const waveA = Math.sin(rawA * 1.08 - beatTravel + spokeIndex * 0.5);
        const waveB = Math.sin(rawB * 1.08 - beatTravel + spokeIndex * 0.5);
        const snapA = Math.max(0, Math.cos(rawA * 0.55 - beatTravel));
        const envelope = Math.max(0, 1 - rawA / (tunnelDepth * 0.9));
        const soundWave = 0.018 + musicDrive * (0.12 + stage * 0.12) * (0.3 + snapA * 0.7) + surge * 0.07;
        const railGainA = soundWave * envelope;
        const railGainB = soundWave * envelope;
        const a = pointOnTunnel(rawA, angle + waveA * railGainA, 1.08, pointA);
        const b = pointOnTunnel(rawB, angle + waveB * railGainB, 1.08, pointB);
        const mid = pointMid.copy(a).add(b).multiplyScalar(0.5);
        const dir = pointDir.copy(b).sub(a);
        matrix.compose(
          mid,
          q.setFromUnitVectors(v.set(0, 1, 0), dir.normalize()),
          v.set(
            0.035 + pulse * 0.018 + snapA * musicDrive * 0.04 + surge * 0.018,
            dir.length(),
            0.035 + pulse * 0.018 + snapA * musicDrive * 0.04 + surge * 0.018
          )
        );
        railRef.current.setMatrixAt(railIndex, matrix);
        railIndex += 1;
      });
    });
    if (railRef.current) {
      railRef.current.instanceMatrix.needsUpdate = true;
      railRef.current.material.opacity = Math.min(0.82, 0.28 + intensity * 0.13 + stage * 0.1 + shock * 0.14 + musicDrive * 0.18 + surge * 0.08);
    }

    if (accentRef.current) {
      accents.forEach((accent, i) => {
        const raw = (accent.raw - travelRef.current * (0.32 + accent.spin * 0.12) + tunnelDepth) % tunnelDepth;
        const p = pointOnTunnel(raw, accent.angle + Math.sin(t * 0.18 + i) * 0.18, accent.radius, pointA);
        const near = 1 - raw / tunnelDepth;
        const fade = THREE.MathUtils.smoothstep(raw, 2.2, 7) * (1 - THREE.MathUtils.smoothstep(raw, tunnelDepth - 12, tunnelDepth));
        dummy.position.copy(p);
        dummy.rotation.set(t * 0.09 * motionSpeed * accent.spin, raw * 0.04 + t * 0.055 * motionSpeed, accent.angle + t * 0.07 * motionSpeed);
        dummy.scale.setScalar(accent.scale * (0.8 + near * 1.6) * fade * (1 + musicDrive * 0.22 + surge * 0.16));
        dummy.updateMatrix();
        accentRef.current.setMatrixAt(i, dummy.matrix);
      });
      accentRef.current.instanceMatrix.needsUpdate = true;
      accentRef.current.material.opacity = Math.min(0.52, 0.18 + stage * 0.12 + musicDrive * 0.08);
    }

    const positions = particlePositionsRef.current;
    if (positions && particlesRef.current) {
      particleSeeds.forEach((pSeed, i) => {
        const raw = (pSeed.raw - travelRef.current * pSeed.speed * (0.48 + stage * 0.36 + motionDrop * 0.22 + motionDrive * 0.18 + motionSurge * 0.14) + tunnelDepth) % tunnelDepth;
        const near = 1 - raw / tunnelDepth;
        const streak = pSeed.streak ? 1 + stage * 1.4 + shock * 1.8 + musicDrive * 1.25 + surge * 1.1 : 1 + musicDrive * 0.22;
        const drift = Math.sin(t * (0.25 + pSeed.speed * 0.08) + pSeed.drift) * 0.24 * (1 - stage * 0.35);
        const p = pointOnTunnel(raw, pSeed.angle + drift, pSeed.radius * (0.55 + near * 0.8 + shock * 0.4), particlePoint);
        positions[i * 3] = p.x;
        positions[i * 3 + 1] = p.y;
        positions[i * 3 + 2] = p.z + pSeed.streak * near * streak * 0.6;
      });
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
      particlesRef.current.material.opacity = Math.min(0.86, 0.32 + intensity * 0.12 + stage * 0.13 + shock * 0.14 + musicDrive * 0.14 + surge * 0.1);
      particlesRef.current.material.size = 0.02 + stage * 0.012 + shock * 0.012 + musicDrive * 0.006 + surge * 0.006;
    }

    group.current.position.set(
      Math.sin(t * 0.12 * motionSpeed + journey.turn + motionDrive * 0.08) * (0.06 + stage * 0.045 + motionDrive * 0.025),
      Math.cos(t * 0.1 * motionSpeed + journey.turn * 0.7 + motionSurge * 0.05) * (0.05 + stage * 0.035 + motionDrive * 0.02),
      0
    );
    group.current.rotation.z = Math.sin(journey.turn * 1.15 + t * 0.07 * motionSpeed) * (0.04 + stage * 0.055 + motionDrop * 0.025 + motionDrive * 0.025);
    group.current.rotation.x = Math.sin(journey.turn * 0.48) * 0.045;
    group.current.rotation.y = Math.cos(journey.turn * 0.42) * 0.045;
    group.current.scale.setScalar(0.9 + journey.reveal * 0.12 + stage * 0.08 + shock * 0.12 + musicDrive * 0.04 + surge * 0.05);

    if (state.camera && 'fov' in state.camera) {
      const targetFov = section === 'drop'
        ? 70 + shock * 7 + musicDrive * 2.5
        : section === 'build'
          ? 58 - journey.chapterProgress * 3 + musicDrive * 1.6
          : 60 + stage * 5 + musicDrive * 1.8;
      state.camera.fov = THREE.MathUtils.lerp(state.camera.fov, targetFov, 0.018);
      state.camera.updateProjectionMatrix();
    }
    if (shockRef.current) {
      const s = 0.8 + shock * 8.5 + surge * 2.2;
      shockRef.current.scale.setScalar(s);
      shockRef.current.material.opacity = Math.min(0.58, shock * 0.45 + surge * 0.16);
    }
  });

  return (
    <>
      <group ref={group}>
        <instancedMesh key={`circle-${tunnelBufferKey}`} ref={(node) => { frameRefs.current.circle = node; }} args={[undefined, undefined, frameSets.circle.length]}>
          <torusGeometry args={[1.25, 0.022, 4, 80]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.primary)} transparent opacity={0.55} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`hex-${tunnelBufferKey}`} ref={(node) => { frameRefs.current.hex = node; }} args={[undefined, undefined, frameSets.hex.length]}>
          <torusGeometry args={[1.18, 0.026, 4, 6]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.secondary)} transparent opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`oct-${tunnelBufferKey}`} ref={(node) => { frameRefs.current.oct = node; }} args={[undefined, undefined, frameSets.oct.length]}>
          <torusGeometry args={[1.34, 0.02, 4, 8]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.glow)} transparent opacity={0.46} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`tri-${tunnelBufferKey}`} ref={(node) => { frameRefs.current.tri = node; }} args={[undefined, undefined, frameSets.tri.length]}>
          <torusGeometry args={[1.15, 0.024, 4, 3]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.accent)} transparent opacity={0.48} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`poly-${tunnelBufferKey}`} ref={(node) => { frameRefs.current.poly = node; }} args={[undefined, undefined, frameSets.poly.length]}>
          <torusGeometry args={[1.06, 0.016, 4, 5]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.primary)} transparent opacity={0.36} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`rails-${tunnelBufferKey}`} ref={railRef} args={[undefined, undefined, railCount * railSegmentCount]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.glow)} transparent opacity={0.48} blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <instancedMesh key={`accents-${tunnelBufferKey}`} ref={accentRef} args={[undefined, undefined, accents.length]}>
          <octahedronGeometry args={[1, 0]} />
          <meshBasicMaterial color={isRainbowTunnel ? '#ffffff' : rgb(palette.accent)} transparent opacity={0.45} wireframe blending={THREE.AdditiveBlending} depthWrite={false} />
        </instancedMesh>
        <points key={`particles-${tunnelBufferKey}`} ref={particlesRef}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={particlePositions.length / 3} array={particlePositions} itemSize={3} />
          </bufferGeometry>
          <pointsMaterial
            size={0.025}
            color={rgb(palette.glow)}
            transparent
            opacity={0.44}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            sizeAttenuation
          />
        </points>
        {[0, 1, 2, 3].map((i) => (
          <mesh key={`haze-${i}`} position={[0, 0, -10 - i * 12]}>
            <circleGeometry args={[8 + i * 2.6, 96]} />
            <meshBasicMaterial
              color={isRainbowTunnel ? new THREE.Color().setHSL(((i * 82 + beat.barIndex * 18) % 360) / 360, 1, 0.55) : rgb(i % 2 ? palette.primary : palette.secondary)}
              transparent
              opacity={(0.035 + stage * 0.018 + beatPulseRef.current * 0.012) * (1 - i * 0.14)}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        ))}
      </group>
      <mesh ref={shockRef} position={[0, 0, -1.8]}>
        <torusGeometry args={[1.0, 0.04, 8, 120]} />
        <meshBasicMaterial color={isRainbowTunnel ? '#fff176' : rgb(palette.accent)} transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>
    </>
  );
}

function RainbowTunnel(props: SceneProps) {
  return <NeonTunnel {...props} params={{ ...props.params, tunnelColorMode: 'rainbow' }} />;
}

function ParticleGalaxy(props: SceneProps) {
  const { palette, intensity, params } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const points = useRef<THREE.Points>(null);
  const core = useRef<THREE.Mesh>(null);
  const smooth = useSmoothedFrameValues();
  const seed = Number(params.seed ?? 1);
  const particleCount = Math.max(2600, Math.round((4300 + journey.chapter * 900) * tune.density));
  const positions = useMemo(() => {
    const arr = new Float32Array(particleCount * 3);
    const arms = props.visualStyle === 'space' ? 7 : props.visualStyle === 'minimal' ? 3 : 5;
    for (let i = 0; i < particleCount; i++) {
      const arm = i % arms;
      const lane = arm * ((Math.PI * 2) / arms);
      const p = ((seed + i * 41) % 10000) / 10000;
      const r = Math.sqrt(p) * (6.2 + journey.reveal * 4.2 + journey.peak * 1.8) * tune.depth;
      const theta = lane + r * (0.28 + journey.progress * 0.34) * tune.geometry + (((seed + i * 17) % 1000) / 1000 - 0.5) * (0.5 + journey.wave);
      const z = (((seed + i * 23) % 1000) / 1000 - 0.5) * (1.2 + journey.wave * 2.4) * tune.depth - 5;
      arr[i * 3] = Math.cos(theta) * r;
      arr[i * 3 + 1] = Math.sin(theta) * r * 0.62;
      arr[i * 3 + 2] = z - p * 3;
    }
    return arr;
  }, [journey.peak, journey.progress, journey.reveal, journey.wave, particleCount, props.visualStyle, seed, tune.depth, tune.geometry]);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    if (points.current) {
      points.current.rotation.z = t * (0.045 + intensity * 0.08 + journey.peak * 0.045) * motionSpeed + journey.turn * 0.35;
      points.current.rotation.x = Math.sin(t * 0.12 * motionSpeed + journey.turn) * (0.08 + journey.wave * 0.18) * tune.depth;
      points.current.scale.setScalar(0.72 + journey.reveal * 0.32 + journey.peak * 0.18 + (groove * 0.08 + impact * 0.16 + phrase * 0.08) * tune.flash);
    }
    if (core.current) {
      core.current.scale.setScalar(0.7 + intensity * 0.25 * tune.geometry + (impact * 0.34 + phrase * 0.22) * tune.flash);
      core.current.rotation.z = -t * 0.2 * motionSpeed;
    }
  });

  return (
    <>
      <points ref={points}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          size={0.03 + intensity * 0.07 * tune.geometry}
          color={rgb(palette.glow, 0.95)}
          transparent
          opacity={Math.min(1, 0.5 + intensity * 0.34 * tune.contrast)}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          sizeAttenuation
        />
      </points>
      <mesh ref={core}>
        <icosahedronGeometry args={[0.9, 2]} />
        <meshBasicMaterial color={rgb(palette.accent, 0.75)} transparent opacity={0.45} wireframe />
      </mesh>
    </>
  );
}

function KineticTypography(props: SceneProps) {
  const { palette, intensity, lyric } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const group = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  useFrame((_, delta) => {
    if (!group.current || !lyric) return;
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    const pulse = 0.82 + journey.reveal * 0.2 + journey.peak * 0.18 + (groove * 0.05 + impact * 0.16 + phrase * 0.1) * tune.flash;
    group.current.scale.setScalar(pulse);
    group.current.rotation.set(0, Math.sin(journey.turn) * 0.1 * journey.wave, Math.sin(journey.turn * 0.5) * 0.08);
  });
  if (!lyric) return null;
  const pulse = 0.82 + journey.reveal * 0.2 + journey.peak * 0.18;
  const trackingBase = props.visualStyle === 'minimal' ? 0.04 : props.visualStyle === 'aggressive' ? 0.18 : 0.12;
  const tracking = trackingBase + journey.chapter * 0.018;
  const split = journey.chapter >= 2;
  return (
    <group ref={group} scale={[pulse, pulse, pulse]} rotation={[0, Math.sin(journey.turn) * 0.1 * journey.wave, Math.sin(journey.turn * 0.5) * 0.08]}>
      <Float speed={(1.15 + intensity * 0.28) * motionSpeedFor(tune, intensity)} rotationIntensity={0.12 * tune.geometry} floatIntensity={0.24 * tune.depth}>
        <Text
          fontSize={(0.78 + intensity * 0.52) * tune.lyric}
          color={rgb(palette.accent)}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.025}
          outlineColor={rgb(palette.glow, 0.9)}
          maxWidth={7.5}
          textAlign="center"
          letterSpacing={tracking}
        >
          {lyric}
        </Text>
        {split && (
          <Text
            position={[-0.1, 0.32 + journey.peak * 0.22, -0.08]}
            fontSize={(0.34 + intensity * 0.22) * tune.lyric}
            color={rgb(palette.primary, 0.62)}
            anchorX="center"
            anchorY="middle"
            maxWidth={8}
            textAlign="center"
            letterSpacing={tracking * 0.6}
          >
            {lyric}
          </Text>
        )}
        <Text
          position={[0.08, -0.08, -0.04]}
          fontSize={(0.78 + intensity * 0.52) * tune.lyric}
          color={rgb(palette.secondary, 0.34)}
          anchorX="center"
          anchorY="middle"
          maxWidth={7.5}
          textAlign="center"
          letterSpacing={tracking}
        >
          {lyric}
        </Text>
      </Float>
    </group>
  );
}

function LaserGrid(props: SceneProps) {
  const { palette, intensity, beat, section } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const tunnel = useRef<THREE.Group>(null);
  const gateTrack = useRef<THREE.Group>(null);
  const railTrack = useRef<THREE.Group>(null);
  const wallTrack = useRef<THREE.Group>(null);
  const targets = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const smoothPulseRef = useRef(0);
  const travelRef = useRef(0);
  const gateCount = Math.max(16, Math.round((18 + journey.chapter * 3) * tune.density));
  const railCount = Math.max(10, Math.round((12 + journey.chapter * 2) * tune.geometry));
  const targetCount = Math.max(7, Math.round((9 + journey.chapter * 2) * tune.density));
  const tunnelDepth = 54 * tune.depth;
  const gateSpacing = tunnelDepth / gateCount;
  const wallSpacing = 3.15;
  const wallLineCount = Math.max(15, Math.round(tunnelDepth / wallSpacing));
  const wallLines = useMemo(() => Array.from({ length: wallLineCount }, (_, i) => i), [wallLineCount]);
  const wallLanes = useMemo(() => [-2.8, -1.85, -0.95, 0.95, 1.85, 2.8], []);
  const sideLanes = useMemo(() => [-1.62, -0.82, 0, 0.82, 1.62], []);
  const gates = useMemo(() => Array.from({ length: gateCount }, (_, i) => ({
    i,
    raw: i * gateSpacing,
    radius: 2.05 + seededUnit(i, 73) * 1.1,
    tilt: (seededUnit(i, 74) - 0.5) * 0.1,
    twist: seededUnit(i, 75) * Math.PI * 2,
    sides: seededUnit(i, 76) > 0.62 ? 6 : seededUnit(i, 77) > 0.55 ? 8 : 96,
  })), [gateCount, gateSpacing]);
  const rails = useMemo(() => Array.from({ length: railCount }, (_, i) => ({
    angle: (i / railCount) * Math.PI * 2,
    radius: 2.45 + (i % 3) * 0.38,
    phase: seededUnit(i, 88) * Math.PI * 2,
    length: tunnelDepth * (0.82 + seededUnit(i, 89) * 0.28),
  })), [railCount, tunnelDepth]);
  const targetNodes = useMemo(() => Array.from({ length: targetCount }, (_, i) => ({
    angle: seededUnit(i, 122) * Math.PI * 2,
    radius: 2.7 + seededUnit(i, 123) * 1.15,
    z: -5.5 - seededUnit(i, 124) * 24,
    scale: 0.22 + seededUnit(i, 125) * 0.34,
    spin: seededUnit(i, 126) > 0.5 ? 1 : -1,
  })), [targetCount]);
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const beatClock = continuousBeat(beat);
    const motionSpeed = motionSpeedFor(tune, intensity, 0.58);
    const impact = smooth('impact', motionCap(music.impact * tune.flash, 0.7), delta, 0.0016);
    const phrase = smooth('phrase', motionCap(music.phrase * tune.flash, 0.65), delta, 0.0016);
    const groove = smooth('groove', motionCap(music.groove * tune.flash, 0.45), delta, 0.002);
    smoothPulseRef.current = smooth(
      'pulse',
      motionCap((music.groove * 0.8 + music.impact * 1.3 + music.phrase) * tune.flash, 1),
      delta,
      0.0014
    );
    const cruise = (1.0 + intensity * 0.3 + journey.peak * 0.18 + impact * 0.14) * motionSpeed;
    travelRef.current = (travelRef.current + delta * cruise * 4.1) % tunnelDepth;
    const nearZ = 2.2;

    if (tunnel.current) {
      tunnel.current.position.x = Math.sin(t * 0.18 * motionSpeed + journey.turn) * (0.08 + phrase * 0.05);
      tunnel.current.position.y = Math.cos(t * 0.14 * motionSpeed + journey.turn) * (0.06 + groove * 0.04);
      tunnel.current.rotation.z = Math.sin(t * 0.08 * motionSpeed + journey.turn) * (0.025 + journey.wave * 0.025);
      tunnel.current.rotation.x = Math.sin(t * 0.11 * motionSpeed + journey.turn) * 0.035;
    }

    if (wallTrack.current) {
      wallTrack.current.position.z = travelRef.current % wallSpacing;
      wallTrack.current.rotation.z = Math.sin(t * 0.06 * motionSpeed + journey.turn) * 0.012;
      wallTrack.current.position.x = Math.sin(t * 0.12 * motionSpeed + journey.turn) * 0.04;
      wallTrack.current.position.y = Math.cos(t * 0.1 * motionSpeed + journey.turn) * 0.03;
    }

    if (gateTrack.current) {
      gateTrack.current.children.forEach((child, i) => {
        const spec = gates[i % gates.length];
        const wrapped = (spec.raw - travelRef.current + tunnelDepth) % tunnelDepth;
        const z = nearZ - wrapped;
        const near = 1 - THREE.MathUtils.clamp(wrapped / tunnelDepth, 0, 1);
        const beatLane = groove + impact * smoothLaneAccent(i, beatClock, 4);
        const tunnelRadius = spec.radius * (1 + near * 0.12 + phrase * 0.05);
        child.position.set(
          Math.sin(wrapped * 0.07 + journey.turn) * 0.22 * (1 - near),
          Math.cos(wrapped * 0.055 + journey.turn) * 0.16 * (1 - near),
          z
        );
        child.rotation.set(
          spec.tilt + Math.sin(t * 0.16 * motionSpeed + i) * 0.035,
          spec.tilt * 0.7 + Math.cos(t * 0.13 * motionSpeed + i) * 0.03,
          spec.twist + t * (0.06 + beatLane * 0.035) * motionSpeed
        );
        child.scale.setScalar(tunnelRadius * (1 + beatLane * 0.035));
      });
    }

    if (railTrack.current) {
      railTrack.current.children.forEach((child, i) => {
        const rail = rails[i % rails.length];
        const wobble = Math.sin(t * 0.5 * motionSpeed + rail.phase + journey.turn) * 0.12;
        const radius = rail.radius + wobble + phrase * 0.18;
        child.position.set(Math.cos(rail.angle) * radius, Math.sin(rail.angle) * radius, -tunnelDepth * 0.45);
        child.rotation.set(Math.PI / 2, 0, rail.angle + Math.PI / 2);
        child.scale.set(1, 1 + impact * 0.08, 1);
      });
    }

    if (targets.current) {
      targets.current.rotation.y = Math.sin(t * 0.08 * motionSpeed + journey.turn) * 0.045;
      targets.current.children.forEach((child, i) => {
        const node = targetNodes[i % targetNodes.length];
        const beatLane = groove + impact * smoothLaneAccent(i, beatClock, 4);
        child.position.x = Math.cos(node.angle + t * 0.06 * node.spin) * node.radius;
        child.position.y = Math.sin(node.angle + t * 0.06 * node.spin) * node.radius;
        child.rotation.z = t * 0.18 * motionSpeed * node.spin + i * 0.4;
        child.scale.setScalar(node.scale * (1 + beatLane * 0.5 + phrase * 0.18));
      });
    }
  });
  const pulse = smoothPulseRef.current || motionCap((music.groove * 0.8 + music.impact * 1.3 + music.phrase) * tune.flash, 1);
  const dropMode = section === 'drop' || section === 'chorus';
  const beatClock = continuousBeat(beat);
  return (
    <group ref={tunnel}>
      <mesh position={[0, 0, -22]}>
        <ringGeometry args={[5.4 + journey.reveal * 1.2, 6.4 + journey.reveal * 1.5 + pulse * 0.3, 128]} />
        <meshBasicMaterial color={rgb(palette.background, 0.42)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>

      <group ref={wallTrack}>
        {wallLines.map((i) => {
          const z = 2.6 - i * wallSpacing;
          const lanePulse = pulse * smoothLaneAccent(i, beatClock, 5);
          const crossOpacity = 0.035 + lanePulse * 0.055 + (dropMode ? 0.018 : 0);
          const wallColor = i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.glow;
          return (
            <group key={`laser-wall-cross-${i}`}>
              <ConnectionLine from={[-3.55, -2.12, z]} to={[3.55, -2.12, z]} color={rgb(wallColor)} opacity={crossOpacity} />
              <ConnectionLine from={[-3.55, 2.48, z]} to={[3.55, 2.48, z]} color={rgb(wallColor)} opacity={crossOpacity * 0.82} />
              <ConnectionLine from={[-3.55, -2.12, z]} to={[-3.55, 2.48, z]} color={rgb(wallColor)} opacity={crossOpacity * 0.95} />
              <ConnectionLine from={[3.55, -2.12, z]} to={[3.55, 2.48, z]} color={rgb(wallColor)} opacity={crossOpacity * 0.95} />
            </group>
          );
        })}
        {wallLanes.map((x, i) => {
          const laneOpacity = 0.045 + pulse * (0.035 + smoothLaneAccent(i, beatClock, 3) * 0.035);
          const farX = x * 0.24;
          return (
            <group key={`laser-wall-long-floor-ceiling-${i}`}>
              <ConnectionLine from={[x, -2.12, 3]} to={[farX, -2.12, -tunnelDepth]} color={rgb(i % 2 ? palette.accent : palette.glow)} opacity={laneOpacity} />
              <ConnectionLine from={[x, 2.48, 3]} to={[farX, 2.48, -tunnelDepth]} color={rgb(i % 2 ? palette.primary : palette.secondary)} opacity={laneOpacity * 0.72} />
            </group>
          );
        })}
        {sideLanes.map((y, i) => {
          const laneOpacity = 0.04 + pulse * (0.03 + smoothLaneAccent(i + 2, beatClock, 4) * 0.035);
          const farY = y * 0.26;
          return (
            <group key={`laser-wall-long-sides-${i}`}>
              <ConnectionLine from={[-3.55, y, 3]} to={[-3.55, farY, -tunnelDepth]} color={rgb(i % 2 ? palette.primary : palette.glow)} opacity={laneOpacity} />
              <ConnectionLine from={[3.55, y, 3]} to={[3.55, farY, -tunnelDepth]} color={rgb(i % 2 ? palette.accent : palette.secondary)} opacity={laneOpacity} />
            </group>
          );
        })}
      </group>

      <group ref={railTrack}>
        {rails.map((rail, i) => (
          <mesh key={`laser-tunnel-rail-${i}`}>
            <boxGeometry args={[0.018 + tune.geometry * 0.006, rail.length, 0.018 + tune.geometry * 0.006]} />
            <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.glow : i % 3 === 1 ? palette.primary : palette.accent, 0.22 + pulse * 0.18)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>

      <group ref={gateTrack}>
        {gates.map((gate, i) => {
          const laneAccent = smoothLaneAccent(i, beatClock, 4) * pulse;
          const color = i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.accent : palette.glow;
          return (
            <group key={`laser-tunnel-gate-${i}`}>
              <mesh>
                <torusGeometry args={[1, 0.012 + tune.geometry * 0.005 + laneAccent * 0.005, 5, gate.sides]} />
                <meshBasicMaterial color={rgb(color, 0.3 + laneAccent * 0.2)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
              <mesh rotation={[0, 0, Math.PI / 4]}>
                <torusGeometry args={[1.24, 0.006 + tune.geometry * 0.003, 4, gate.sides === 96 ? 96 : gate.sides]} />
                <meshBasicMaterial color={rgb(palette.glow, 0.12 + laneAccent * 0.16)} transparent blending={THREE.AdditiveBlending} depthWrite={false} wireframe toneMapped={false} />
              </mesh>
              {(i % 4 === 0 || dropMode) && (
                <mesh>
                  <ringGeometry args={[0.78, 0.8 + laneAccent * 0.04, gate.sides === 96 ? 96 : Math.max(12, gate.sides * 2)]} />
                  <meshBasicMaterial color={rgb(palette.secondary, 0.08 + laneAccent * 0.09)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
                </mesh>
              )}
            </group>
          );
        })}
      </group>

      <group ref={targets}>
        {targetNodes.map((node, i) => (
          <Float key={`laser-target-${i}`} speed={(0.38 + seededUnit(i, 133) * 0.22) * motionSpeedFor(tune, intensity, 0.6)} rotationIntensity={0.08} floatIntensity={0.18}>
            <group position={[Math.cos(node.angle) * node.radius, Math.sin(node.angle) * node.radius, node.z]} scale={[node.scale, node.scale, node.scale]}>
              <mesh>
                <octahedronGeometry args={[1, 0]} />
                <meshBasicMaterial color={rgb(i % 2 ? palette.secondary : palette.accent, 0.32 + pulse * 0.2)} transparent blending={THREE.AdditiveBlending} depthWrite={false} wireframe toneMapped={false} />
              </mesh>
              <mesh scale={[0.52, 0.52, 0.52]}>
                <sphereGeometry args={[1, 12, 8]} />
                <meshBasicMaterial color={rgb(palette.glow, 0.28 + (dropMode ? pulse * 0.3 : pulse * 0.16))} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            </group>
          </Float>
        ))}
      </group>

      <mesh position={[0, 0, -18]}>
        <ringGeometry args={[4.42 + journey.reveal * 1.05, 4.8 + journey.reveal * 1.2 + pulse * 0.25, 96]} />
        <meshBasicMaterial color={rgb(palette.primary, 0.04 + pulse * 0.05)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>
      <fog attach="fog" args={[rgb(palette.background), 5 * tune.softness, 30 * tune.depth]} />
    </group>
  );
}

function StarfieldWarp(props: SceneProps) {
  const { palette, intensity } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const tunnel = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const starSpeedRef = useRef(0);
  const streakCount = Math.max(32, Math.round((42 + journey.chapter * 8) * tune.density));
  const streaks = useMemo(() => Array.from({ length: streakCount }, (_, i) => ({
    angle: seededUnit(i, 401) * Math.PI * 2,
    radius: 0.45 + seededUnit(i, 402) * (5.6 + journey.reveal * 2.2),
    z: -1.2 - seededUnit(i, 403) * 44,
    length: 1.8 + seededUnit(i, 404) * (5.4 + journey.peak * 3.2),
    skew: (seededUnit(i, 405) - 0.5) * 0.85,
  })), [journey.peak, journey.reveal, streakCount]);
  useFrame((state, delta) => {
    if (!tunnel.current) return;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const impact = smooth('impact', motionCap(music.impact, 0.65), delta);
    const phrase = smooth('phrase', motionCap(music.phrase, 0.65), delta);
    starSpeedRef.current = smooth(
      'starSpeed',
      (0.42 + intensity * 0.45 + journey.peak * 0.38 + impact * 0.32 + phrase * 0.18) * motionSpeedFor(tune, intensity, 0.62),
      delta,
      0.0018
    );
    tunnel.current.rotation.z = state.clock.elapsedTime * (0.035 + intensity * 0.03 + journey.peak * 0.035) * motionSpeed + journey.turn * 0.2;
    tunnel.current.rotation.x = Math.sin(journey.turn) * 0.18 * journey.wave;
    tunnel.current.scale.setScalar(0.82 + journey.reveal * 0.22 + journey.peak * 0.26 + (impact * 0.08 + phrase * 0.06) * tune.flash);
  });
  return (
    <>
      <Stars
        radius={95 * tune.depth}
        depth={55 * tune.depth}
        count={Math.round((4200 + journey.chapter * 1100) * tune.density)}
        factor={2.8 + journey.reveal * 2 + intensity * 5 * tune.geometry + journey.peak * 3}
        saturation={0}
        fade
        speed={starSpeedRef.current || (0.42 + intensity * 0.45 + journey.peak * 0.38) * motionSpeedFor(tune, intensity, 0.62)}
      />
      <group ref={tunnel}>
        {streaks.map((streak, i) => {
          const x = Math.cos(streak.angle) * streak.radius;
          const y = Math.sin(streak.angle) * streak.radius * 0.62;
          const farScale = 0.18 + Math.abs(streak.z) / 48;
          const from: [number, number, number] = [x * 0.2, y * 0.2, streak.z - streak.length];
          const to: [number, number, number] = [x + streak.skew * farScale, y, streak.z];
          return (
            <ConnectionLine
              key={`warp-streak-${i}`}
              from={from}
              to={to}
              color={rgb(i % 3 === 0 ? palette.glow : i % 3 === 1 ? palette.primary : palette.secondary)}
              opacity={0.08 + intensity * 0.12 + journey.peak * 0.08}
            />
          );
        })}
        {Array.from({ length: 7 }).map((_, i) => {
          const a = (i / 7) * Math.PI * 2 + journey.turn * 0.4;
          const r = 1.2 + i * 0.55;
          return (
            <mesh key={`warp-dust-bank-${i}`} position={[Math.cos(a) * r, Math.sin(a) * r * 0.45, -5.5 - i * 4.2]} rotation={[0, 0, a]}>
              <planeGeometry args={[0.8 + i * 0.16, 0.018 + intensity * 0.012]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.glow, 0.06 + intensity * 0.08)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          );
        })}
      </group>
      <mesh>
        <sphereGeometry args={[42 * tune.depth, 32, 32]} />
        <meshBasicMaterial color={rgb(palette.background)} side={THREE.BackSide} />
      </mesh>
    </>
  );
}

function SacredGeometry(props: SceneProps) {
  const { palette, intensity } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const group = useRef<THREE.Group>(null);
  const scaleTarget = useMemo(() => new THREE.Vector3(), []);
  const smooth = useSmoothedFrameValues();
  const mandalaCount = Math.max(9, Math.round((12 + journey.chapter * 4) * tune.geometry));
  const mandala = useMemo(() => Array.from({ length: mandalaCount }, (_, i) => i), [mandalaCount]);
  useFrame((state, delta) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    group.current.rotation.x = Math.sin(t * 0.1 * motionSpeed + journey.turn) * (0.18 + journey.wave * 0.22) * tune.depth;
    group.current.rotation.y = t * (0.09 + intensity * 0.08 + journey.peak * 0.08) * motionSpeed;
    group.current.rotation.z = -t * 0.045 * motionSpeed + journey.turn * 0.12;
    scaleTarget.setScalar(0.78 + journey.reveal * 0.25 + journey.peak * 0.18 + (groove * 0.04 + impact * 0.16 + phrase * 0.08) * tune.flash);
    group.current.scale.lerp(scaleTarget, 1 - Math.pow(0.001, delta));
  });
  return (
    <group ref={group}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} rotation={[0, (Math.PI * 2 * i) / 3, 0]}>
          <torusKnotGeometry args={[0.9 + journey.reveal * 0.22 + i * 0.22 * tune.depth, 0.052 + journey.wave * 0.026 + i * 0.035 * tune.geometry, Math.round((120 + journey.chapter * 26) * tune.geometry), 18, 2 + i + (journey.chapter % 2), 3]} />
          <meshStandardMaterial
            color={rgb(i === 0 ? palette.primary : i === 1 ? palette.secondary : palette.accent)}
            emissive={rgb(palette.glow, 0.35 + intensity * 0.45)}
            wireframe
            metalness={0.6}
            roughness={0.18}
          />
        </mesh>
      ))}
      {mandala.map((i) => {
        const a = (i / mandala.length) * Math.PI * 2;
        return (
          <mesh key={`petal-${i}`} position={[Math.cos(a) * 1.9 * tune.depth, Math.sin(a) * 1.9 * tune.depth, -0.35]} rotation={[0, 0, a]}>
            <torusGeometry args={[0.42 * tune.geometry, 0.01 + intensity * 0.004, 5, 40]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.glow, 0.35 + intensity * 0.35)} transparent />
          </mesh>
        );
      })}
    </group>
  );
}

function BassCrystal(props: SceneProps) {
  const { palette, intensity } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const mesh = useRef<THREE.Mesh>(null);
  const smooth = useSmoothedFrameValues();
  const shardImpactRef = useRef(0);
  const shardCount = Math.max(14, Math.round((20 + journey.chapter * 7) * tune.density));
  const shards = useMemo(() => Array.from({ length: shardCount }, (_, i) => i), [shardCount]);
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    if (!mesh.current) return;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    shardImpactRef.current = impact;
    mesh.current.rotation.y = t * (0.22 + intensity * 0.22 + journey.peak * 0.18) * motionSpeed + journey.turn * 0.2;
    mesh.current.rotation.x = Math.sin(t * 0.34 * motionSpeed + journey.turn) * (0.18 + journey.wave * 0.3) * tune.depth;
    const s = 0.74 + journey.reveal * 0.28 + journey.peak * 0.2 + (groove * 0.06 + impact * 0.28 + phrase * 0.16) * tune.flash;
    mesh.current.scale.setScalar(s);
  });
  return (
    <>
      <mesh ref={mesh}>
        <octahedronGeometry args={[1.45 * tune.geometry, props.visualStyle === 'minimal' ? 0 : 1]} />
        <meshPhysicalMaterial
          color={rgb(palette.primary)}
          emissive={rgb(palette.glow, 0.4 + intensity * 0.55)}
          metalness={0.85}
          roughness={0.08}
          transmission={props.visualStyle === 'cinematic' || props.visualStyle === 'emotional' ? 0.55 : 0.35}
          thickness={1.2}
          clearcoat={1}
        />
      </mesh>
      {shards.map((i) => {
        const a = (i / shards.length) * Math.PI * 2;
        const r = (1.4 + journey.reveal * 0.9 + journey.chapter * 0.38) * tune.depth + ((i * 29) % 100) / 100 * (1.2 + journey.wave * 1.8) * tune.depth + shardImpactRef.current * 0.42 * tune.flash;
        return (
          <mesh key={i} position={[Math.cos(a) * r, Math.sin(a * 1.7) * 0.6, Math.sin(a) * r - 1.8]} rotation={[a, a * 0.6, a * 0.3]}>
            <tetrahedronGeometry args={[0.1 + intensity * 0.06 * tune.geometry, 0]} />
            <meshStandardMaterial color={rgb(i % 2 ? palette.secondary : palette.accent)} emissive={rgb(palette.glow, 0.35)} />
          </mesh>
        );
      })}
    </>
  );
}

function FestivalCrowd(props: SceneProps) {
  const { palette, intensity, beat, progressMs } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const crowd = useRef<THREE.Group>(null);
  const beatLiftRef = useRef(0);
  const columns = props.visualStyle === 'minimal' ? 10 : props.visualStyle === 'aggressive' ? 20 : 16 + journey.chapter * 2;
  const rows = Math.max(4, Math.round((5 + journey.chapter) * tune.density));
  const lights = useMemo(() => Array.from({ length: columns * rows }, (_, i) => i), [columns, rows]);
  const sweepCount = Math.max(3, Math.round(8 * tune.density));
  const sweeps = useMemo(() => Array.from({ length: sweepCount }, (_, i) => i), [sweepCount]);
  useFrame((state, delta) => {
    const target = Math.max(music.groove * 0.45, music.impact, music.phrase * 0.8);
    beatLiftRef.current = THREE.MathUtils.lerp(beatLiftRef.current, target, 1 - Math.pow(0.0008, delta));
    if (!crowd.current) return;
    const t = state.clock.elapsedTime;
    crowd.current.rotation.y = Math.sin(t * 0.08 * motionSpeedFor(tune, intensity) + journey.turn) * 0.06 * journey.wave;
    crowd.current.position.y = -2.25 + beatLiftRef.current * 0.06 * intensity;
  });
  return (
    <>
      <group ref={crowd} position={[0, -2.25, -0.4]}>
        {lights.map((i) => {
          const row = Math.floor(i / columns);
          const col = i % columns;
          const beatLift = beatLiftRef.current;
          const beatClock = continuousBeat(beat);
          const crowdPhase = (progressMs / 1000) * (0.58 + journey.chapter * 0.08);
          const wave = Math.sin((col / columns) * Math.PI * 2 + crowdPhase + row * 0.55 + journey.turn);
          const rowDelay = Math.sin(row * 0.9 + beatClock * 0.45) * 0.5 + 0.5;
          const x = (col - (columns - 1) / 2) * 0.58;
          const z = row * (0.42 + journey.progress * 0.16) * tune.depth - 2.1;
          const h = 0.2 + journey.reveal * 0.12 + (0.5 + wave * 0.5) * (0.48 + journey.wave * 0.28 + intensity * tune.geometry * 0.72) + beatLift * rowDelay * 0.2 * intensity * tune.flash;
          const alpha = 0.38 + intensity * 0.34 + beatLift * 0.14 * rowDelay;
          return (
            <mesh key={i} position={[x, h / 2, z]}>
              <boxGeometry args={[0.16 + 0.03 * tune.geometry, h, 0.16 + 0.03 * tune.geometry]} />
              <meshBasicMaterial
                color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.accent, alpha)}
              />
            </mesh>
          );
        })}
      </group>
      {sweeps.map((i) => (
        <mesh key={`sweep-${i}`} position={[Math.sin(journey.turn + i) * 0.7 * journey.wave, 0.15 + journey.peak * 0.35 + beatLiftRef.current * 0.12, -3.5 - i * 0.4 * tune.depth]} rotation={[0.35 + i * 0.03, 0, -0.7 + i * 0.2 + Math.sin(journey.turn + i * 0.2) * 0.32]}>
          <boxGeometry args={[0.025 + 0.018 * tune.geometry, 9.5 * tune.depth, 0.025 + 0.018 * tune.geometry]} />
          <meshBasicMaterial color={rgb(i % 2 ? palette.glow : palette.accent, 0.12 + intensity * 0.12 + beatLiftRef.current * 0.08)} transparent />
        </mesh>
      ))}
    </>
  );
}

function CyberpunkCity(props: SceneProps) {
  const { palette, intensity, beat, section } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const city = useRef<THREE.Group>(null);
  const signs = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const glowRef = useRef(0);
  const travelRef = useRef(0);
  const depth = 58 * tune.depth;
  const spacing = 2.65;
  const buildingCount = Math.max(40, Math.round((54 + journey.chapter * 10) * tune.density));
  const signCount = Math.max(8, Math.round((10 + journey.chapter * 2) * tune.density));
  const buildings = useMemo(
    () =>
      Array.from({ length: buildingCount }, (_, i) => ({
        side: i % 2 ? 1 : -1,
        lane: Math.floor(i / 2),
        offset: (seededUnit(i, 151) - 0.5) * 0.72,
        h: 1.6 + seededUnit(i, 152) * 4.8 * (1 + journey.wave * 0.26 + intensity * 0.24),
        w: 0.52 + seededUnit(i, 153) * 0.72,
        d: 0.9 + seededUnit(i, 154) * 1.35,
        setback: seededUnit(i, 155) * 1.2,
        windowPhase: seededUnit(i, 156) * Math.PI * 2,
      })),
    [buildingCount, intensity, journey.wave]
  );
  const overheadSigns = useMemo(
    () =>
      Array.from({ length: signCount }, (_, i) => ({
        raw: i * (depth / signCount) + seededUnit(i, 181) * 2.4,
        y: 1.15 + seededUnit(i, 182) * 1.1,
        width: 1.2 + seededUnit(i, 183) * 1.4,
        height: 0.16 + seededUnit(i, 184) * 0.22,
        tilt: (seededUnit(i, 185) - 0.5) * 0.16,
      })),
    [depth, signCount]
  );
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const beatClock = continuousBeat(beat);
    const motionSpeed = motionSpeedFor(tune, intensity, 0.58);
    const impact = smooth('impact', motionCap(music.impact * tune.flash, 0.72), delta, 0.0014);
    const phrase = smooth('phrase', motionCap(music.phrase * tune.flash, 0.62), delta, 0.0015);
    const groove = smooth('groove', motionCap(music.groove * tune.flash, 0.5), delta, 0.002);
    glowRef.current = smooth('glow', (impact * 0.55 + phrase * 0.32 + groove * 0.18) * tune.contrast, delta);
    travelRef.current = (travelRef.current + delta * (3.4 + intensity * 1.1 + journey.peak * 0.6) * motionSpeed) % depth;

    if (city.current) {
      city.current.position.x = Math.sin(t * 0.14 * motionSpeed + journey.turn) * (0.12 + phrase * 0.08);
      city.current.position.y = -1.9 + Math.sin(journey.turn) * 0.14 * journey.wave + impact * 0.04;
      city.current.rotation.y = Math.sin(journey.turn * 0.5 + t * 0.07 * motionSpeed) * 0.045;
      city.current.rotation.z = Math.sin(t * 0.06 * motionSpeed + journey.turn) * 0.018;
      city.current.children.forEach((child, i) => {
        const b = buildings[i % buildings.length];
        const raw = (b.lane * spacing + b.offset - travelRef.current + depth) % depth;
        const z = 2.8 - raw;
        const sidePush = b.side * (2.15 + b.setback + phrase * 0.12);
        const laneBeat = smoothLaneAccent(i, beatClock, 4);
        child.position.set(sidePush, b.h / 2 - 0.15, z);
        child.scale.set(1 + impact * laneBeat * 0.025, 1 + impact * laneBeat * 0.08, 1);
      });
    }

    if (signs.current) {
      signs.current.children.forEach((child, i) => {
        const s = overheadSigns[i % overheadSigns.length];
        const raw = (s.raw - travelRef.current * 0.92 + depth) % depth;
        const z = 2.2 - raw;
        child.position.set(Math.sin(s.raw * 0.12 + journey.turn) * 0.42, s.y + phrase * 0.16, z);
        child.rotation.set(0, Math.sin(t * 0.1 * motionSpeed + i) * 0.06, s.tilt + Math.sin(t * 0.18 * motionSpeed + i) * 0.04);
        child.scale.setScalar(1 + impact * smoothLaneAccent(i, beatClock, 4) * 0.08);
      });
    }
  });
  const glow = glowRef.current || (music.impact * 0.42 + music.phrase * 0.22) * tune.contrast;
  const pulse = Math.min(1, (music.groove * 0.7 + music.impact + music.phrase * 0.8) * tune.flash);
  const dropMode = section === 'drop' || section === 'chorus';
  return (
    <>
      <group ref={city} position={[0, -1.9, -8]}>
        {buildings.map((b, i) => {
          return (
            <group key={`cyber-building-${i}`}>
              <mesh>
                <boxGeometry args={[b.w, b.h, b.d]} />
                <meshStandardMaterial
                  color={rgb(palette.background)}
                  emissive={rgb(i % 2 ? palette.primary : palette.secondary, 0.16 * tune.contrast + intensity * 0.22 * tune.contrast + glow * 0.55)}
                  metalness={0.38}
                  roughness={0.3}
                />
              </mesh>
              {Array.from({ length: 5 }, (_, strip) => (
                <mesh
                  key={`window-strip-${strip}`}
                  position={[b.side * (-b.w / 2 - 0.004), -b.h * 0.32 + strip * b.h * 0.16, 0.02]}
                  rotation={[0, Math.PI / 2, 0]}
                >
                  <planeGeometry args={[b.d * (0.56 + seededUnit(i + strip, 163) * 0.28), 0.025 + pulse * 0.016]} />
                  <meshBasicMaterial
                    color={rgb((strip + i) % 3 === 0 ? palette.accent : (strip + i) % 3 === 1 ? palette.glow : palette.primary, 0.22 + pulse * 0.2 + glow * 0.08)}
                    transparent
                    blending={THREE.AdditiveBlending}
                    depthWrite={false}
                    toneMapped={false}
                  />
                </mesh>
              ))}
              {(i % 5 === 0 || dropMode) && (
                <mesh position={[b.side * (-b.w / 2 - 0.012), b.h * 0.18, 0]} rotation={[0, Math.PI / 2, 0]}>
                  <planeGeometry args={[b.d * 0.9, 0.18 + pulse * 0.05]} />
                  <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.glow, 0.16 + pulse * 0.24)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
                </mesh>
              )}
            </group>
          );
        })}
      </group>
      <group ref={signs}>
        {overheadSigns.map((s, i) => (
          <group key={`cyber-overhead-${i}`} position={[0, s.y, -i * 4]}>
            <mesh>
              <boxGeometry args={[s.width, s.height, 0.045]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.primary, 0.26 + pulse * 0.24)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh position={[0, -0.18, 0]}>
              <boxGeometry args={[0.045, 0.42, 0.035]} />
              <meshBasicMaterial color={rgb(palette.glow, 0.18 + glow * 0.12)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>
      <mesh position={[0, -1.95, -12]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[5.2, 70]} />
        <meshBasicMaterial color={rgb(palette.background, 0.44)} transparent depthWrite={false} />
      </mesh>
      {[-0.9, 0, 0.9].map((x, i) => (
        <ConnectionLine
          key={`cyber-road-line-${i}`}
          from={[x, -1.86, 2.2]}
          to={[x * 0.2, -1.86, -52]}
          color={rgb(i === 1 ? palette.glow : palette.secondary)}
          opacity={0.08 + pulse * 0.08}
        />
      ))}
      <fog attach="fog" args={[rgb(palette.background), 4 * tune.softness, 38 * tune.depth]} />
    </>
  );
}

function LyricConstellation(props: SceneProps) {
  const { palette, intensity, lyric, params } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const group = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const seed = Number(params.seed ?? 1);
  const nodes = useMemo(() => {
    const wordLimit = props.visualStyle === 'emotional' ? 7 : props.visualStyle === 'minimal' ? 3 : 5;
    const words = (lyric || 'FEEL THE DROP').split(' ').slice(0, wordLimit);
    return words.map((word, i) => ({
      word,
      x: Math.sin(seed + i * 1.7 + journey.turn) * (2.1 + journey.reveal * 1.5 + journey.peak * 0.8) * tune.depth,
      y: Math.cos(seed + i * 2.1 + journey.turn * 0.6) * (1.0 + journey.wave * 1.1) * tune.depth,
      z: -1.5 - i * (0.3 + journey.progress * 0.34) * tune.depth,
    }));
  }, [journey.peak, journey.progress, journey.reveal, journey.turn, journey.wave, lyric, props.visualStyle, seed, tune.depth]);
  const lyricDust = useMemo(() => Array.from({ length: Math.max(18, Math.round((24 + journey.chapter * 6) * tune.density)) }, (_, i) => ({
    x: (seededUnit(i, 531) - 0.5) * (7.2 + journey.reveal * 2.4),
    y: (seededUnit(i, 532) - 0.5) * (4.2 + journey.wave * 1.2),
    z: -1.1 - seededUnit(i, 533) * 3.8,
    size: 0.018 + seededUnit(i, 534) * 0.045,
    phase: seededUnit(i, 535) * Math.PI * 2,
  })), [journey.chapter, journey.reveal, journey.wave, tune.density]);

  const initialConstellationScale = 0.82 + journey.reveal * 0.2 + journey.peak * 0.16;
  useFrame((_, delta) => {
    if (!group.current) return;
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    const scale = 0.82 + journey.reveal * 0.2 + journey.peak * 0.16 + (impact * 0.08 + phrase * 0.06) * tune.flash;
    group.current.scale.set(scale, scale, 1);
    group.current.rotation.z = Math.sin(journey.turn) * 0.12 * journey.wave;
  });

  return (
    <group ref={group} scale={[initialConstellationScale, initialConstellationScale, 1]} rotation={[0, 0, Math.sin(journey.turn) * 0.12 * journey.wave]}>
      {nodes.flatMap((from, i) =>
        nodes.slice(i + 1).map((to, j) => (
          <ConnectionLine
            key={`line-${i}-${j}`}
            from={[from.x, from.y, from.z]}
            to={[to.x, to.y, to.z]}
            color={rgb(j % 2 ? palette.primary : palette.secondary)}
            opacity={(0.08 + intensity * 0.12) * tune.contrast}
          />
        ))
      )}
      {nodes.map((n, i) => (
        <group key={i} position={[n.x, n.y, n.z]}>
          <mesh>
            <sphereGeometry args={[0.08 + intensity * 0.06 * tune.geometry, 12, 12]} />
            <meshBasicMaterial color={rgb(palette.accent)} />
          </mesh>
          <Text position={[0, -0.3, 0]} fontSize={(0.2 + intensity * 0.06) * tune.lyric} color={rgb(palette.glow, 0.95)} anchorX="center">
            {n.word}
          </Text>
        </group>
      ))}
      {lyricDust.map((dust, i) => {
        const emphasis = 0.5 + Math.sin(seed + i * 0.9 + journey.turn + dust.phase) * 0.5;
        return (
          <mesh key={`lyric-dust-${i}`} position={[dust.x, dust.y, dust.z]}>
            <sphereGeometry args={[dust.size * (1 + emphasis * intensity * 1.4), 8, 8]} />
            <meshBasicMaterial
              color={rgb(i % 3 === 0 ? palette.glow : i % 3 === 1 ? palette.primary : palette.accent, 0.3 + emphasis * 0.38)}
              transparent
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        );
      })}
      {nodes.map((n, i) => (
        <mesh key={`lyric-halo-${i}`} position={[n.x, n.y, n.z - 0.04]} rotation={[0, 0, journey.turn * 0.2 + i * 0.7]}>
          <ringGeometry args={[0.2 + i * 0.035, 0.205 + i * 0.035 + intensity * 0.012, 48]} />
          <meshBasicMaterial color={rgb(i % 2 ? palette.secondary : palette.glow, 0.12 + intensity * 0.12)} transparent blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function AlbumArtExplosion(props: SceneProps) {
  const { palette, intensity, albumArtUrl } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const group = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const burstRef = useRef(0);
  const [tex, setTex] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    if (!albumArtUrl) {
      setTex(null);
      return undefined;
    }

    let cancelled = false;
    let loadedTexture: THREE.Texture | null = null;
    const loader = new THREE.TextureLoader();
    if (!albumArtUrl.startsWith('data:')) loader.setCrossOrigin('anonymous');
    loader.load(
      albumArtUrl,
      (texture) => {
        if (cancelled) {
          texture.dispose();
          return;
        }
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        loadedTexture = texture;
        setTex(texture);
      },
      undefined,
      () => {
        if (!cancelled) setTex(null);
      }
    );

    return () => {
      cancelled = true;
      if (loadedTexture) loadedTexture.dispose();
    };
  }, [albumArtUrl]);
  const grid = props.visualStyle === 'minimal' ? 8 + journey.chapter : props.visualStyle === 'aggressive' ? 14 : 10 + journey.chapter;
  const tiles = useMemo(() => Array.from({ length: grid * grid }, (_, i) => i), [grid]);

  useFrame((state, delta) => {
    if (!group.current) return;
    const t = state.clock.elapsedTime;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    burstRef.current = smooth('burst', (journey.progress * 0.8 + journey.peak * 0.9 + music.groove * 0.18 + music.impact * 0.58 + music.phrase * 0.36) * tune.flash, delta, 0.0016);
    const s = 0.78 + journey.reveal * 0.25 + journey.peak * 0.22 + (groove * 0.04 + impact * 0.18 + phrase * 0.12) * tune.flash;
    group.current.scale.setScalar(s);
    group.current.rotation.z = Math.sin(t * 0.09 * motionSpeed + journey.turn) * (0.05 + journey.wave * 0.12) * tune.geometry;
    group.current.rotation.y = Math.sin(journey.turn * 0.5) * 0.18 * journey.peak;
  });

  return (
    <group ref={group}>
      {tiles.map((i) => {
        const col = i % grid;
        const row = Math.floor(i / grid);
        const center = (grid - 1) / 2;
        const tileGap = 3.25 / grid;
        const x = (col - center) * tileGap;
        const y = (center - row) * tileGap;
        const rawBurst = (journey.progress * 0.8 + journey.peak * 0.9 + music.groove * 0.18 + music.impact * 0.58 + music.phrase * 0.36) * tune.flash;
        const burst = burstRef.current || rawBurst;
        const dx = (col - center) * burst * (0.035 + journey.chapter * 0.018);
        const dy = (center - row) * burst * (0.035 + journey.chapter * 0.018);
        const depth = (seededUnit(i, col, row) - 0.5) * burst * (0.45 + journey.wave * 0.8);
        return (
          <group
            key={i}
            position={[x + dx, y + dy, depth]}
            rotation={[burst * (0.12 + seededUnit(i, 1) * 0.24), burst * (0.16 + seededUnit(i, 2) * 0.26), (i % 4) * burst * 0.08]}
          >
            <AlbumTile
              texture={tex}
              col={col}
              row={row}
              grid={grid}
              size={tileGap * 0.92}
              tint={tex ? colorVector([255, 255, 255], 0.95) : colorVector(i % 2 ? palette.primary : palette.accent)}
              opacity={0.72 + 0.1 * tune.contrast}
            />
          </group>
        );
      })}
      {Array.from({ length: Math.max(14, Math.round((20 + journey.chapter * 10) * tune.density)) }).map((_, i, sparks) => (
        <mesh
          key={`spark-${i}`}
          position={[
            Math.cos((i / sparks.length) * Math.PI * 2 + journey.turn) * (1.4 + journey.reveal * 0.8 + journey.peak * 1.2 + intensity * 0.9 * tune.depth + burstRef.current * 0.45),
            Math.sin((i / sparks.length) * Math.PI * 2 + journey.turn) * (1.4 + journey.reveal * 0.8 + journey.peak * 1.2 + intensity * 0.9 * tune.depth + burstRef.current * 0.45),
            -0.2,
          ]}
        >
          <boxGeometry args={[0.08, 0.08, 0.08]} />
          <meshBasicMaterial
            color={rgb(i % 2 ? palette.accent : palette.glow)}
            opacity={0.85}
            transparent
          />
        </mesh>
      ))}
    </group>
  );
}

function WaveformTerrain(props: SceneProps) {
  const { palette, intensity, progressMs } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const mesh = useRef<THREE.Mesh>(null);
  const glow = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const widthSegments = Math.max(36, Math.round((66 + journey.chapter * 16) * tune.density));
  const heightSegments = Math.max(26, Math.round((42 + journey.chapter * 10) * tune.density));
  const geom = useMemo(() => new THREE.PlaneGeometry((12 + journey.reveal * 5) * tune.depth, (8 + journey.reveal * 4) * tune.depth, widthSegments, heightSegments), [heightSegments, journey.reveal, tune.depth, widthSegments]);
  const railCount = Math.max(5, Math.round((7 + journey.chapter * 2) * tune.geometry));
  const rails = useMemo(() => Array.from({ length: railCount }, (_, i) => i), [railCount]);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const motionSpeed = motionSpeedFor(tune, intensity);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const phrase = smooth('phrase', music.phrase, delta);
    const pos = geom.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const distance = Math.sqrt(x * x + y * y);
      const wave =
        Math.sin(x * (1.05 + journey.progress) + t * (1.1 + intensity * 0.35 + journey.peak * 0.3) * motionSpeed) * (0.14 + journey.wave * 0.18) * tune.geometry +
        Math.sin(y * (1.6 + journey.chapter * 0.35) + progressMs * 0.0022) * (0.12 + journey.reveal * 0.12) +
        Math.sin(distance * (1.6 + journey.progress * 1.2) - t * 1.45 * motionSpeed) * (0.12 + journey.peak * 0.22) * tune.depth +
        (groove * 0.14 + impact * 0.38 + phrase * 0.18) * tune.flash;
      pos.setZ(i, wave);
    }
    pos.needsUpdate = true;
    geom.computeVertexNormals();
    if (glow.current) {
      glow.current.position.z = (t * (0.78 + intensity * 0.58 + journey.peak * 0.42) * motionSpeed) % 3;
      glow.current.rotation.z = Math.sin(t * 0.1 * motionSpeed + journey.turn) * (0.05 + journey.wave * 0.1) * tune.geometry;
    }
  });

  return (
    <>
      <mesh ref={mesh} geometry={geom} rotation={[-Math.PI / (2.7 - journey.reveal * 0.25), 0, Math.sin(journey.turn) * 0.08]} position={[0, -1.28 + journey.peak * 0.2, -2.3 * tune.depth]}>
        <meshStandardMaterial
          color={rgb(palette.secondary)}
          emissive={rgb(palette.glow, (0.18 + intensity * 0.44) * tune.contrast)}
          wireframe
          metalness={0.45}
          roughness={0.32}
        />
      </mesh>
      <group ref={glow} position={[0, -0.95, -3.2]}>
        {rails.map((i) => (
          <mesh key={i} position={[(i - (railCount - 1) / 2) * 0.72, 0, -2]} rotation={[-Math.PI / 2.9, 0, 0]}>
            <boxGeometry args={[0.02 + 0.012 * tune.geometry, 10.5 * tune.depth, 0.02 + 0.012 * tune.geometry]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.primary : palette.accent, 0.18 + intensity * 0.16)} transparent />
          </mesh>
        ))}
      </group>
    </>
  );
}

function CathedralOfLight(props: SceneProps) {
  const { palette, intensity, section, lyric } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const root = useRef<THREE.Group>(null);
  const naveRef = useRef<THREE.InstancedMesh>(null);
  const roofRibRef = useRef<THREE.InstancedMesh>(null);
  const buttressRef = useRef<THREE.InstancedMesh>(null);
  const spireRef = useRef<THREE.InstancedMesh>(null);
  const roseWindowRef = useRef<THREE.InstancedMesh>(null);
  const columnRef = useRef<THREE.InstancedMesh>(null);
  const archRef = useRef<THREE.InstancedMesh>(null);
  const railRef = useRef<THREE.InstancedMesh>(null);
  const floorRef = useRef<THREE.InstancedMesh>(null);
  const beamRef = useRef<THREE.InstancedMesh>(null);
  const bridgeRef = useRef<THREE.InstancedMesh>(null);
  const portalRef = useRef<THREE.InstancedMesh>(null);
  const windowRef = useRef<THREE.InstancedMesh>(null);
  const treeRef = useRef<THREE.InstancedMesh>(null);
  const monumentRef = useRef<THREE.InstancedMesh>(null);
  const particleRef = useRef<THREE.Points>(null);
  const waterfallRef = useRef<THREE.Points>(null);
  const columnCount = Math.max(24, Math.round(34 * tune.density));
  const archCount = 0;
  const railCount = 0;
  const railSegments = 0;
  const depth = 124 * tune.depth;
  const modelBayCount = Math.max(12, Math.round(16 * tune.depth));
  const modelBayDepth = depth / modelBayCount;
  const floorCount = 0;
  const beamCount = Math.max(5, Math.round(8 * tune.geometry));
  const bridgeCount = 0;
  const portalCount = 0;
  const windowCount = Math.max(3, Math.round(5 * tune.depth));
  const treeBranchCount = Math.max(12, Math.round(18 * tune.geometry));
  const monumentCount = Math.max(3, Math.round(5 * tune.density));
  const particleCount = Math.max(1800, Math.round(3000 * tune.density));
  const waterfallCount = Math.max(260, Math.round(520 * tune.density));
  const stage = section === 'drop'
    ? 1
    : section === 'build'
      ? 0.78
      : section === 'chorus'
        ? 0.68
        : section === 'intro' || section === 'outro'
          ? 0.3
          : 0.46;
  const rareMoment = THREE.MathUtils.smoothstep(
    Math.max(0, Math.sin(journey.progress * Math.PI * 4 - 0.35)),
    0.58,
    0.98
  );

  const navePieces = useMemo(() => {
    const pieces: any[] = [];
    for (let bay = 0; bay < modelBayCount; bay += 1) {
      const raw = (bay / modelBayCount) * depth;
      pieces.push({ raw, kind: 'aisle-floor', side: -1 });
      pieces.push({ raw, kind: 'aisle-floor', side: 1 });
      pieces.push({ raw, kind: 'wall', side: -1 });
      pieces.push({ raw, kind: 'wall', side: 1 });
      pieces.push({ raw, kind: 'clerestory', side: -1 });
      pieces.push({ raw, kind: 'clerestory', side: 1 });
      pieces.push({ raw, kind: 'pier', side: -1 });
      pieces.push({ raw, kind: 'pier', side: 1 });
    }
    return pieces;
  }, [depth, modelBayCount]);

  const roofRibs = useMemo(() => {
    const ribs: any[] = [];
    for (let bay = 0; bay < modelBayCount; bay += 1) {
      const raw = (bay / modelBayCount) * depth + modelBayDepth * 0.18;
      ribs.push({ raw, kind: 'diagonal', side: -1, level: 0 });
      ribs.push({ raw, kind: 'diagonal', side: 1, level: 0 });
      ribs.push({ raw, kind: 'arch', side: -1, level: 1 });
      ribs.push({ raw, kind: 'arch', side: 1, level: 1 });
    }
    return ribs;
  }, [depth, modelBayCount, modelBayDepth]);

  const buttresses = useMemo(() => {
    const parts: any[] = [];
    for (let bay = 0; bay < modelBayCount; bay += 1) {
      const raw = (bay / modelBayCount) * depth + modelBayDepth * 0.5;
      parts.push({ raw, kind: 'pier', side: -1, level: 0 });
      parts.push({ raw, kind: 'pier', side: 1, level: 0 });
      parts.push({ raw, kind: 'flying', side: -1, level: 0 });
      parts.push({ raw, kind: 'flying', side: 1, level: 0 });
      if (bay % 2 === 0) {
        parts.push({ raw, kind: 'flying', side: -1, level: 1 });
        parts.push({ raw, kind: 'flying', side: 1, level: 1 });
      }
    }
    return parts;
  }, [depth, modelBayCount, modelBayDepth]);

  const spires = useMemo(() => {
    const parts: any[] = [];
    for (let bay = 0; bay < modelBayCount; bay += 2) {
      const raw = (bay / modelBayCount) * depth + modelBayDepth * 0.3;
      parts.push({
        raw,
        side: -1,
        lane: 1.03 + seededUnit(bay, 61) * 0.18,
        radius: 0.16 + seededUnit(bay, 62) * 0.08,
        height: 1.7 + seededUnit(bay, 63) * 1.7,
      });
      parts.push({
        raw,
        side: 1,
        lane: 1.03 + seededUnit(bay, 64) * 0.18,
        radius: 0.16 + seededUnit(bay, 65) * 0.08,
        height: 1.7 + seededUnit(bay, 66) * 1.7,
      });
    }
    return parts;
  }, [depth, modelBayCount, modelBayDepth]);

  const roseWindows = useMemo(() => [], []);

  const columns = useMemo(() => Array.from({ length: columnCount }, (_, i) => ({
    raw: (i / columnCount) * depth,
    side: i % 2 ? 1 : -1,
    lane: Math.floor(i / 2),
    height: 3.6 + seededUnit(i, 2) * 2.8,
    thickness: 0.11 + seededUnit(i, 3) * 0.08,
    phase: seededUnit(i, 4) * Math.PI * 2,
  })), [columnCount, depth]);

  const arches = useMemo(() => Array.from({ length: archCount }, (_, i) => ({
    raw: (i / archCount) * depth + seededUnit(i, 5) * 1.4,
    scale: 0.84 + seededUnit(i, 6) * 0.28,
    rotation: (seededUnit(i, 7) - 0.5) * 0.18,
    spin: (seededUnit(i, 27) - 0.5) * 0.42,
    shape: i % 4,
  })), [archCount, depth]);

  const bridges = useMemo(() => Array.from({ length: bridgeCount }, (_, i) => ({
    raw: (i / bridgeCount) * depth + seededUnit(i, 8) * 2,
    y: -1.9 + seededUnit(i, 9) * 4.3,
    angle: seededUnit(i, 10) * Math.PI - Math.PI / 2,
    width: 0.8 + seededUnit(i, 11) * 0.6,
  })), [bridgeCount, depth]);

  const floorSlabs = useMemo(() => Array.from({ length: floorCount }, (_, i) => ({
    raw: (i / floorCount) * depth,
    glow: 0.55 + seededUnit(i, 28) * 0.65,
    split: i % 5 === 0 ? 1 : 0,
  })), [floorCount, depth]);

  const beams = useMemo(() => Array.from({ length: beamCount }, (_, i) => ({
    raw: (i / beamCount) * depth + seededUnit(i, 29) * 2.8,
    side: i % 2 ? 1 : -1,
    lane: 0.72 + seededUnit(i, 30) * 0.36,
    height: 5.8 + seededUnit(i, 31) * 5.4,
    thickness: 0.018 + seededUnit(i, 32) * 0.04,
  })), [beamCount, depth]);

  const portals = useMemo(() => Array.from({ length: portalCount }, (_, i) => ({
    raw: (i / portalCount) * depth + seededUnit(i, 12) * 2.6,
    scale: 1 + seededUnit(i, 13) * 0.32,
    spin: (seededUnit(i, 14) - 0.5) * 0.4,
  })), [portalCount, depth]);

  const windows = useMemo(() => Array.from({ length: windowCount }, (_, i) => ({
    raw: (i / windowCount) * depth + seededUnit(i, 33) * 4,
    side: i % 2 ? 1 : -1,
    y: 1.0 + seededUnit(i, 34) * 2.9,
    size: 0.7 + seededUnit(i, 35) * 1.2,
    spin: seededUnit(i, 36) * Math.PI,
  })), [windowCount, depth]);

  const treeBranches = useMemo(() => Array.from({ length: treeBranchCount }, (_, i) => ({
    raw: (Math.floor(i / 7) / Math.max(1, Math.floor(treeBranchCount / 7))) * depth + seededUnit(i, 37) * 2.2,
    side: i % 2 ? 1 : -1,
    branch: i % 7,
    height: 0.7 + seededUnit(i, 38) * 1.65,
    lean: (seededUnit(i, 39) - 0.5) * 0.6,
  })), [treeBranchCount, depth]);

  const monuments = useMemo(() => Array.from({ length: monumentCount }, (_, i) => ({
    raw: seededUnit(i, 15) * depth,
    side: i % 2 ? 1 : -1,
    height: -0.8 + seededUnit(i, 16) * 3.6,
    drift: seededUnit(i, 17) * Math.PI * 2,
    size: 0.2 + seededUnit(i, 18) * 0.38,
  })), [monumentCount, depth]);

  const particleSeeds = useMemo(() => Array.from({ length: particleCount }, (_, i) => ({
    raw: seededUnit(i, 22) * depth,
    x: (seededUnit(i, 23) - 0.5) * 12,
    y: (seededUnit(i, 24) - 0.5) * 7,
    drift: seededUnit(i, 25) * Math.PI * 2,
    speed: 0.35 + Math.pow(seededUnit(i, 26), 2) * 2.7,
  })), [particleCount, depth]);

  const waterfallSeeds = useMemo(() => Array.from({ length: waterfallCount }, (_, i) => ({
    raw: seededUnit(i, 41) * depth,
    x: (seededUnit(i, 42) - 0.5) * 2,
    y: seededUnit(i, 43),
    side: i % 2 ? 1 : -1,
    speed: 0.18 + seededUnit(i, 44) * 0.74,
  })), [waterfallCount, depth]);

  const particlePositions = useMemo(() => {
    return new Float32Array(particleCount * 3);
  }, [particleCount]);

  const waterfallPositions = useMemo(() => {
    return new Float32Array(waterfallCount * 3);
  }, [waterfallCount]);

  const cathedralProcessor = useMemo(
    () => createCathedral3DProcessor({ depth, railCount, railSegments, floorCount }),
    [depth, floorCount, railCount, railSegments]
  );

  useFrame((state, delta) => {
    cathedralProcessor.update({
      state,
      delta,
      props,
      tune,
      journey,
      stage,
      refs: {
        root,
        nave: naveRef,
        roofRib: roofRibRef,
        buttress: buttressRef,
        spire: spireRef,
        roseWindow: roseWindowRef,
        column: columnRef,
        arch: archRef,
        rail: railRef,
        floor: floorRef,
        beam: beamRef,
        bridge: bridgeRef,
        portal: portalRef,
        window: windowRef,
        tree: treeRef,
        monument: monumentRef,
        particle: particleRef,
        waterfall: waterfallRef,
      },
      data: {
        modelBayDepth,
        navePieces,
        roofRibs,
        buttresses,
        spires,
        roseWindows,
        columns,
        arches,
        rails: railCount,
        floorSlabs,
        beams,
        bridges,
        portals,
        windows,
        treeBranches,
        monuments,
        particleSeeds,
        particlePositions,
        waterfallSeeds,
        waterfallPositions,
      },
    });
  });

  const showLyric = lyric && (section === 'chorus' || section === 'drop');

  return (
    <>
      <fog attach="fog" args={[rgb(palette.background), 5, 42 * tune.depth]} />
      <group ref={root}>
        <Stars
          radius={85 * tune.depth}
          depth={40 * tune.depth}
          count={Math.round(1200 + rareMoment * 1800)}
          factor={1.8 + stage * 1.4}
          saturation={0}
          fade
          speed={0.25 + stage * 0.45}
        />

        <instancedMesh ref={naveRef} args={[undefined, undefined, navePieces.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial
            color={rgb(palette.background)}
            emissive={rgb(palette.primary, 0.18)}
            transparent
            opacity={0.12}
            metalness={0.15}
            roughness={0.48}
            depthWrite={false}
          />
        </instancedMesh>

        <instancedMesh ref={roofRibRef} args={[undefined, undefined, roofRibs.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.24} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        <instancedMesh ref={buttressRef} args={[undefined, undefined, buttresses.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshStandardMaterial
            color={rgb(palette.background)}
            emissive={rgb(palette.secondary, 0.18)}
            transparent
            opacity={0.1}
            metalness={0.2}
            roughness={0.4}
            depthWrite={false}
          />
        </instancedMesh>

        <instancedMesh ref={spireRef} args={[undefined, undefined, spires.length]}>
          <coneGeometry args={[1, 1, 5]} />
          <meshBasicMaterial color={rgb(palette.primary)} transparent opacity={0.22} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        {roseWindows.length > 0 && (
          <instancedMesh ref={roseWindowRef} args={[undefined, undefined, roseWindows.length]}>
            <torusGeometry args={[1, 0.025, 6, 18]} />
            <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.22} wireframe blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </instancedMesh>
        )}

        <instancedMesh ref={columnRef} args={[undefined, undefined, columns.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={rgb(palette.primary)} transparent opacity={0.34} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        {arches.length > 0 && (
          <instancedMesh ref={archRef} args={[undefined, undefined, arches.length]}>
            <torusGeometry args={[1, 0.018, 5, 84, Math.PI * 1.26]} />
            <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.3} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </instancedMesh>
        )}

        {railCount * railSegments > 0 && (
          <instancedMesh ref={railRef} args={[undefined, undefined, railCount * railSegments]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.24} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </instancedMesh>
        )}

        {floorSlabs.length > 0 && (
          <instancedMesh ref={floorRef} args={[undefined, undefined, floorSlabs.length]}>
            <planeGeometry args={[1, 1]} />
            <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.1} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
          </instancedMesh>
        )}

        <instancedMesh ref={beamRef} args={[undefined, undefined, beams.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.25} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        {bridges.length > 0 && (
          <instancedMesh ref={bridgeRef} args={[undefined, undefined, bridges.length]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshBasicMaterial color={rgb(palette.secondary)} transparent opacity={0.18} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </instancedMesh>
        )}

        {portals.length > 0 && (
          <instancedMesh ref={portalRef} args={[undefined, undefined, portals.length]}>
            <torusGeometry args={[1, 0.012, 4, 72]} />
            <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.2} blending={THREE.AdditiveBlending} depthWrite={false} wireframe toneMapped={false} />
          </instancedMesh>
        )}

        <instancedMesh ref={windowRef} args={[undefined, undefined, windows.length]}>
          <circleGeometry args={[1, 7]} />
          <meshBasicMaterial color={rgb(palette.secondary)} transparent opacity={0.2} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
        </instancedMesh>

        <instancedMesh ref={treeRef} args={[undefined, undefined, treeBranches.length]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.24} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        <instancedMesh ref={monumentRef} args={[undefined, undefined, monuments.length]}>
          <octahedronGeometry args={[1, 0]} />
          <meshBasicMaterial color={rgb(palette.secondary)} transparent opacity={0.38} wireframe blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </instancedMesh>

        <points ref={particleRef}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={particlePositions.length / 3} array={particlePositions} itemSize={3} />
          </bufferGeometry>
          <pointsMaterial
            size={0.026}
            color={rgb(palette.glow)}
            transparent
            opacity={0.4}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            sizeAttenuation
          />
        </points>

        <points ref={waterfallRef}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={waterfallPositions.length / 3} array={waterfallPositions} itemSize={3} />
          </bufferGeometry>
          <pointsMaterial
            size={0.024}
            color={rgb(palette.accent)}
            transparent
            opacity={0.08}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            sizeAttenuation
          />
        </points>

        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={`cathedral-haze-${i}`} position={[0, 0, -8 - i * 8]}>
            <circleGeometry args={[7 + i * 2.4, 96]} />
            <meshBasicMaterial
              color={rgb(i % 2 ? palette.primary : palette.secondary)}
              transparent
              opacity={(0.006 + intensity * 0.003 + stage * 0.004) * (1 - i * 0.12)}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        ))}

        {showLyric && (
          <Text
            position={[0, 2.7 + stage * 0.6, -6.2]}
            rotation={[-0.05, 0, 0]}
            fontSize={(0.42 + intensity * 0.18) * tune.lyric}
            color={rgb(palette.accent)}
            anchorX="center"
            anchorY="middle"
            outlineWidth={0.014}
            outlineColor={rgb(palette.glow)}
            maxWidth={7.2}
            textAlign="center"
            letterSpacing={0.08}
          >
            {lyric}
          </Text>
        )}
      </group>
    </>
  );
}

function BirdFlight(props: SceneProps) {
  const { palette, intensity, progressMs } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const bird = useRef<THREE.Group>(null);
  const world = useRef<THREE.Group>(null);
  const flock = useRef<THREE.Group>(null);
  const smooth = useSmoothedFrameValues();
  const landscapes = useMemo<BirdLandscapeKind[]>(() => [
    'sunset',
    'ocean',
    'mountains',
    'forest',
    'desert',
    'city',
    'aurora',
    'space',
    'festival',
  ], []);
  const flightTrail = useMemo(() => Array.from({ length: 18 }, (_, i) => i), []);
  const landscapeWidth = 10.2;
  const totalLandscapeTravel = landscapeWidth * (landscapes.length - 1);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const motionSpeed = motionSpeedFor(tune, intensity, 0.62);
    const groove = smooth('groove', music.groove, delta);
    const impact = smooth('impact', music.impact, delta);
    const crossingPhase = (journey.progress * 3.4 + t * 0.008 * (0.7 + motionSpeed)) % 1;
    const birdX = -6.25 + crossingPhase * 12.5;
    const birdY = 0.25
      + Math.sin(crossingPhase * Math.PI * 2 + journey.turn) * 0.42
      + Math.sin(t * 0.36 * motionSpeed) * 0.16
      + (groove * 0.035 + impact * 0.08) * tune.flash;

    if (bird.current) {
      const wingBeat = Math.sin(t * (6.8 + intensity * 2.5) * motionSpeed) * (0.58 + journey.wave * 0.2 + intensity * 0.12);
      bird.current.position.set(birdX, birdY, -1.05 + Math.sin(t * 0.14 * motionSpeed) * 0.06);
      bird.current.rotation.set(
        Math.sin(t * 0.22 * motionSpeed) * 0.025,
        0,
        Math.sin(t * 0.44 * motionSpeed + crossingPhase * Math.PI) * 0.06
      );
      bird.current.scale.setScalar(0.98 + intensity * 0.18 + journey.peak * 0.16 + impact * 0.045 * tune.flash);
      const leftWing = bird.current.getObjectByName('left-wing');
      const rightWing = bird.current.getObjectByName('right-wing');
      if (leftWing) leftWing.rotation.z = 0.1 + wingBeat;
      if (rightWing) rightWing.rotation.z = -0.12 - wingBeat * 0.82;
    }
    if (world.current) {
      const travel = journey.progress * totalLandscapeTravel;
      world.current.position.x = 5.05 - travel + Math.sin(t * 0.08) * 0.12;
      world.current.position.y = Math.sin(t * 0.05 + journey.turn) * 0.05;
    }
    if (flock.current) {
      flock.current.position.x = birdX - 1.5;
      flock.current.position.y = birdY - 0.15;
      flock.current.rotation.z = Math.sin(t * 0.15 + journey.turn) * 0.08;
    }
  });

  return (
    <>
      <group ref={world}>
        {landscapes.map((kind, i) => (
          <BirdLandscapePanel
            key={kind}
            kind={kind}
            index={i}
            x={i * landscapeWidth}
            width={landscapeWidth}
            {...props}
          />
        ))}
      </group>

      <group ref={flock}>
        {flightTrail.map((i) => (
          <group
            key={i}
            position={[
              -0.35 - i * 0.38,
              Math.sin(i * 0.7 + progressMs * 0.001) * 0.22,
              -1.4 - i * 0.015,
            ]}
            scale={[0.26 + seededUnit(i, 4) * 0.18, 0.26 + seededUnit(i, 4) * 0.18, 0.26 + seededUnit(i, 4) * 0.18]}
          >
            <BirdSilhouette color={rgb(i % 2 ? palette.primary : palette.secondary, Math.max(0.03, 0.24 - i * 0.011 + intensity * 0.08))} />
          </group>
        ))}
      </group>

      <group ref={bird}>
        <SideBirdBody palette={palette} intensity={intensity} />
      </group>

    </>
  );
}

type BirdLandscapeKind =
  | 'sunset'
  | 'ocean'
  | 'mountains'
  | 'forest'
  | 'desert'
  | 'city'
  | 'aurora'
  | 'space'
  | 'festival';

function SideBirdBody({ palette, intensity }: { palette: SceneProps['palette']; intensity: number }) {
  return (
    <group>
      <mesh position={[0.04, 0, 0]} scale={[0.52, 0.24, 0.18]}>
        <sphereGeometry args={[1, 24, 16]} />
        <meshStandardMaterial color={rgb(palette.accent)} emissive={rgb(palette.glow, 0.12 + intensity * 0.28)} roughness={0.34} metalness={0.2} />
      </mesh>
      <mesh position={[0.48, 0.1, 0]} scale={[0.19, 0.19, 0.16]}>
        <sphereGeometry args={[1, 18, 12]} />
        <meshStandardMaterial color={rgb(palette.secondary)} emissive={rgb(palette.glow, 0.1 + intensity * 0.18)} />
      </mesh>
      <mesh position={[0.68, 0.1, 0]} rotation={[0, 0, -Math.PI / 2]} scale={[0.1, 0.2, 0.1]}>
        <coneGeometry args={[1, 1, 3]} />
        <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.82} toneMapped={false} />
      </mesh>
      <mesh name="left-wing" position={[-0.08, 0.04, 0.05]} rotation={[0, 0.04, 0.35]}>
        <coneGeometry args={[0.2, 1.15, 3]} />
        <meshStandardMaterial color={rgb(palette.primary)} emissive={rgb(palette.glow, 0.22 + intensity * 0.28)} side={THREE.DoubleSide} roughness={0.38} />
      </mesh>
      <mesh name="right-wing" position={[-0.04, -0.02, -0.08]} rotation={[0, -0.12, -0.42]}>
        <coneGeometry args={[0.16, 0.95, 3]} />
        <meshStandardMaterial color={rgb(palette.secondary)} emissive={rgb(palette.glow, 0.16 + intensity * 0.22)} side={THREE.DoubleSide} roughness={0.42} />
      </mesh>
      <mesh position={[-0.5, -0.02, 0]} rotation={[0, 0, Math.PI / 2]} scale={[0.1, 0.36, 0.1]}>
        <coneGeometry args={[1, 1, 3]} />
        <meshBasicMaterial color={rgb(palette.primary)} transparent opacity={0.76} toneMapped={false} />
      </mesh>
      <pointLight color={rgb(palette.glow)} intensity={1.4 + intensity * 2.4} distance={4.8} />
    </group>
  );
}

function BirdLandscapePanel({
  kind,
  index,
  x,
  width,
  palette,
  intensity,
  beat,
  progressMs,
  section,
}: SceneProps & { kind: BirdLandscapeKind; index: number; x: number; width: number }) {
  const music = musicalPulse({ beat, intensity, section } as SceneProps);
  const pulse = music.groove * 0.4 + music.impact * 0.9 + music.phrase * 0.55;
  const t = progressMs / 1000;
  const beatClock = continuousBeat(beat);
  const seeds = useMemo(() => Array.from({ length: 18 }, (_, i) => i), []);
  const panelColor = index % 2 ? palette.background : palette.primary;

  return (
    <group position={[x, 0, -3.8]}>
      <mesh position={[0, 0.2, -0.4]}>
        <planeGeometry args={[width + 0.1, 6.4]} />
        <meshBasicMaterial color={rgb(panelColor)} transparent opacity={0.14 + intensity * 0.04} depthWrite={false} />
      </mesh>

      {(kind === 'sunset' || kind === 'ocean') && (
        <>
          <mesh position={[kind === 'sunset' ? -1.9 : 2.2, 1.1, -0.25]}>
            <circleGeometry args={[0.95 + pulse * 0.08, 64]} />
            <meshBasicMaterial color={rgb(kind === 'sunset' ? palette.accent : palette.glow)} transparent opacity={0.58} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
          {seeds.slice(0, 8).map((i) => (
            <ConnectionLine
              key={i}
              from={[-width / 2, -1.85 + i * 0.22 + Math.sin(t * 0.45 + i) * 0.04, -0.1]}
              to={[width / 2, -1.85 + i * 0.22 + Math.sin(t * 0.45 + i + 1) * 0.04, -0.1]}
              color={rgb(i % 2 ? palette.secondary : palette.glow)}
              opacity={0.12 + intensity * 0.07}
            />
          ))}
        </>
      )}

      {kind === 'mountains' && seeds.slice(0, 12).map((i) => {
        const h = 1.1 + seededUnit(i, 10) * 1.9;
        return (
          <mesh key={i} position={[-4.9 + i * 0.88, -2.15 + h / 2, -0.08]} rotation={[0, 0, seededUnit(i, 11) * 0.08 - 0.04]}>
            <coneGeometry args={[0.78, h, 3]} />
            <meshStandardMaterial color={rgb(i % 2 ? palette.primary : palette.secondary)} emissive={rgb(palette.glow, 0.05 + intensity * 0.08)} wireframe={i % 3 === 0} />
          </mesh>
        );
      })}

      {kind === 'forest' && seeds.map((i) => {
        const h = 0.9 + seededUnit(i, 20) * 1.8;
        const px = -4.8 + i * 0.56;
        return (
          <group key={i} position={[px, -2.25, -0.05]}>
            <mesh position={[0, h * 0.38, 0]} scale={[0.09, h * 0.76, 0.09]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshBasicMaterial color={rgb(palette.secondary)} transparent opacity={0.32 + intensity * 0.12} />
            </mesh>
            <mesh position={[0, h * 0.88, 0]} scale={[0.42, 0.72, 0.42]}>
              <coneGeometry args={[1, 1, 5]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.primary : palette.glow)} transparent opacity={0.2 + intensity * 0.14} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        );
      })}

      {kind === 'desert' && seeds.slice(0, 9).map((i) => (
        <mesh key={i} position={[-4.7 + i * 1.2, -2.12 + Math.sin(i * 0.8 + t * 0.12) * 0.12, -0.08]} rotation={[0, 0, Math.sin(i) * 0.14]}>
          <circleGeometry args={[1.1 + seededUnit(i, 30) * 0.8, 32, 0, Math.PI]} />
          <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.secondary)} transparent opacity={0.12 + intensity * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      ))}

      {kind === 'city' && seeds.map((i) => {
        const h = 0.8 + seededUnit(i, 40) * 2.7 + smoothLaneAccent(i, beatClock, 4) * pulse * 0.45;
        return (
          <mesh key={i} position={[-4.85 + i * 0.58, -2.25 + h / 2, -0.04]}>
            <boxGeometry args={[0.34, h, 0.26]} />
            <meshStandardMaterial color={rgb(palette.background)} emissive={rgb(i % 2 ? palette.primary : palette.secondary, 0.2 + intensity * 0.32)} />
          </mesh>
        );
      })}

      {(kind === 'aurora' || kind === 'space') && (
        <>
          {kind === 'space' && <Stars radius={18} depth={7} count={900} factor={2.8 + intensity * 1.5} fade speed={0.35 + intensity * 0.4} />}
          {seeds.slice(0, kind === 'space' ? 14 : 9).map((i) => (
            <mesh key={i} position={[-4.7 + i * 0.78, Math.sin(i * 0.9 + t * 0.26) * 1.2 + 0.4, -0.08]} rotation={[0, 0, Math.sin(t * 0.18 + i) * 0.55]}>
              <torusGeometry args={[0.75 + seededUnit(i, 50) * 1.0 + pulse * 0.04, 0.012 + intensity * 0.006, 5, 64]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.primary : palette.glow)} transparent opacity={kind === 'space' ? 0.18 : 0.3} wireframe blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </>
      )}

      {kind === 'festival' && seeds.map((i) => {
        const h = 0.4 + seededUnit(i, 60) * 1.4 + pulse * (i % 3 === 0 ? 0.7 : 0.25);
        return (
          <group key={i} position={[-4.9 + i * 0.58, -2.35, -0.04]}>
            <mesh position={[0, h / 2, 0]}>
              <boxGeometry args={[0.08, h, 0.08]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.accent)} transparent opacity={0.38 + intensity * 0.26} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh position={[0, h + 0.18, 0]}>
              <circleGeometry args={[0.08 + seededUnit(i, 61) * 0.06, 16]} />
              <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.28 + pulse * 0.26} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

function BirdSilhouette({ color }: { color: string }) {
  return (
    <group>
      <mesh position={[-0.18, 0, 0]} rotation={[0, 0, 0.45]}>
        <coneGeometry args={[0.045, 0.45, 3]} />
        <meshBasicMaterial color={color} transparent side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0.18, 0, 0]} rotation={[0, 0, -0.45]}>
        <coneGeometry args={[0.045, 0.45, 3]} />
        <meshBasicMaterial color={color} transparent side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function BirdJourney2D(props: SceneProps) {
  const { palette, intensity, progressMs, params } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const music = musicalPulse(props);
  const mic = micReactivityFromParams(params);
  const root = useRef<THREE.Group>(null);
  const world = useRef<THREE.Group>(null);
  const bird = useRef<THREE.Group>(null);
  const echo = useRef<THREE.Group>(null);
  const panels = useMemo<BirdLandscapeKind[]>(() => [
    'sunset',
    'ocean',
    'mountains',
    'forest',
    'desert',
    'city',
    'aurora',
    'space',
    'festival',
  ], []);
  const viewportWidth = 10.4;
  const panelWidth = 38;
  const environmentDurationMs = 60_000;
  const echoes = useMemo(() => Array.from({ length: 5 }, (_, i) => i), []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const rawEnvironment = progressMs / environmentDurationMs;
    const currentEnvironment = Math.min(panels.length - 1, Math.floor(rawEnvironment));
    const crossing = currentEnvironment >= panels.length - 1
      ? THREE.MathUtils.clamp(rawEnvironment - currentEnvironment, 0, 1)
      : rawEnvironment - currentEnvironment;
    const x = -6.1 + crossing * 12.2;
    const y = 0.28 + Math.sin(crossing * Math.PI * 2 + journey.turn) * 0.42 + Math.sin(t * 0.7) * 0.13 + (music.groove * 0.05 + music.impact * 0.12 + music.phrase * 0.08 + mic.hit * 0.18 + mic.level * 0.08);
    const pulse = music.groove * 0.35 + music.impact * 0.85 + music.phrase * 0.5 + mic.hit * 0.9 + mic.level * 0.24;
    const wingPhase = t * (8.5 + intensity * 7 + pulse * 2.4 + mic.level * 2.2) * tune.speed;
    const flap = Math.sin(wingPhase);
    const downstroke = Math.max(0, -flap);
    const upstroke = Math.max(0, flap);
    const applyWingPose = (container: THREE.Object3D) => {
      container.traverse((object) => {
        if (object.name === 'flat-top-wing') {
          object.position.set(
            -0.08 - downstroke * 0.05 + upstroke * 0.025,
            0.02 + flap * 0.12 + downstroke * 0.025,
            0.05
          );
          object.rotation.z = 0.32 + flap * 0.78 - downstroke * 0.22;
          object.scale.set(1 + downstroke * 0.16 - upstroke * 0.06, 1 + downstroke * 0.18 - upstroke * 0.16, 1);
          object.children.forEach((feather, idx) => {
            const lag = Math.sin(wingPhase - idx * 0.18);
            feather.rotation.z = -0.16 - idx * 0.09 + lag * 0.11 - downstroke * 0.08;
            feather.position.y = -0.02 - idx * 0.025 - downstroke * 0.025 + upstroke * 0.035;
            feather.scale.set(0.12 + idx * 0.016, (0.58 - idx * 0.045) * (1 + downstroke * 0.22 - upstroke * 0.1), 1);
          });
        }
        if (object.name === 'flat-bottom-wing') {
          object.position.set(
            -0.04 - downstroke * 0.02,
            -0.07 - flap * 0.065 - downstroke * 0.02,
            0.035
          );
          object.rotation.z = -0.34 - flap * 0.52 + downstroke * 0.16;
          object.scale.set(1 + downstroke * 0.08, 1 + downstroke * 0.12 - upstroke * 0.08, 1);
          object.children.forEach((feather, idx) => {
            const lag = Math.sin(wingPhase - idx * 0.16);
            feather.rotation.z = 0.16 + idx * 0.08 - lag * 0.08 + downstroke * 0.06;
            feather.position.y = 0.01 + idx * 0.015 + downstroke * 0.02 - upstroke * 0.02;
            feather.scale.set(0.1 + idx * 0.018, (0.42 - idx * 0.04) * (1 + downstroke * 0.16 - upstroke * 0.08), 1);
          });
        }
      });
    };

    if (root.current) {
      root.current.rotation.z = Math.sin(t * 0.09 + journey.turn) * 0.01;
      root.current.scale.setScalar(1 + pulse * 0.024 + mic.hit * 0.014);
    }
    if (world.current) {
      const travel = Math.min(rawEnvironment, panels.length - 1 + crossing);
      world.current.position.x = (panelWidth - viewportWidth) / 2 - travel * panelWidth;
      world.current.position.y = Math.sin(t * 0.05) * 0.04;
    }
    if (bird.current) {
      bird.current.position.set(x, y, -1.2);
      bird.current.rotation.z = Math.sin(t * 0.8 + crossing * Math.PI) * 0.08;
      bird.current.scale.setScalar(0.9 + intensity * 0.18 + journey.peak * 0.12 + pulse * 0.06 + mic.level * 0.035);
      applyWingPose(bird.current);
    }
    if (echo.current) {
      echo.current.position.set(x - 0.85, y - 0.04, -1.28);
      applyWingPose(echo.current);
    }
  });

  return (
    <group ref={root}>
      <mesh position={[0, 0, -3.3]}>
        <planeGeometry args={[18, 10]} />
        <meshBasicMaterial color={rgb(palette.background)} transparent opacity={0.62} depthWrite={false} />
      </mesh>
      <MoodEffects2D {...props} music={music} />

      <group ref={world}>
        {panels.map((kind, i) => (
          <FlatLandscapePanel2D
            key={kind}
            kind={kind}
            index={i}
            x={i * panelWidth}
            width={panelWidth}
            {...props}
          />
        ))}
      </group>

      <group ref={echo}>
        {echoes.map((i) => (
          <group
            key={i}
            position={[-0.48 - i * 0.42, Math.sin(i * 0.7 + progressMs * 0.001) * 0.12 - i * 0.035, -0.02 - i * 0.002]}
            scale={[0.24 - i * 0.012, 0.24 - i * 0.012, 1]}
          >
            <FlatBirdSilhouette color={rgb([42, 54, 62], Math.max(0.08, 0.28 - i * 0.035))} accent={rgb([210, 220, 224], Math.max(0.06, 0.2 - i * 0.03))} />
          </group>
        ))}
      </group>

      <group ref={bird}>
        <FlatBirdSilhouette color="#5f8faf" accent="#dfe9ed" />
      </group>
      <MoodEffects2D {...props} music={music} foreground />

      <mesh position={[0, -3.25, -2.7]}>
        <planeGeometry args={[18, 1.4]} />
        <meshBasicMaterial color="#071019" transparent opacity={0.28} depthWrite={false} />
      </mesh>
    </group>
  );
}

function FlatBirdSilhouette({ color, accent }: { color: string; accent?: string }) {
  const featherColor = accent || color;
  return (
    <group>
      <mesh position={[0.02, -0.01, 0]} scale={[0.62, 0.26, 1]}>
        <circleGeometry args={[1, 48]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[-0.16, -0.04, 0.01]} scale={[0.38, 0.18, 1]}>
        <circleGeometry args={[1, 40]} />
        <meshBasicMaterial color={featherColor} transparent opacity={0.48} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[0.52, 0.1, 0.03]} scale={[0.2, 0.18, 1]}>
        <circleGeometry args={[1, 36]} />
        <meshBasicMaterial color={color} transparent opacity={0.94} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[0.61, 0.14, 0.05]} scale={[0.025, 0.025, 1]}>
        <circleGeometry args={[1, 16]} />
        <meshBasicMaterial color="#09131a" transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[0.78, 0.09, 0.02]} rotation={[0, 0, -Math.PI / 2]} scale={[0.12, 0.28, 1]}>
        <circleGeometry args={[1, 3]} />
        <meshBasicMaterial color="#f4b860" transparent opacity={0.9} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>
      <group name="flat-top-wing" position={[-0.08, 0.02, 0.05]} rotation={[0, 0, 0.35]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} position={[-0.05 - i * 0.08, -0.02 - i * 0.025, i * 0.002]} rotation={[0, 0, -0.16 - i * 0.09]} scale={[0.12 + i * 0.016, 0.58 - i * 0.045, 1]}>
            <circleGeometry args={[1, 3]} />
            <meshBasicMaterial color={i % 2 ? featherColor : color} transparent opacity={0.64 - i * 0.04} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        ))}
      </group>
      <group name="flat-bottom-wing" position={[-0.04, -0.07, 0.035]} rotation={[0, 0, -0.35]}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} position={[-0.02 - i * 0.08, 0.01 + i * 0.015, i * 0.002]} rotation={[0, 0, 0.16 + i * 0.08]} scale={[0.1 + i * 0.018, 0.42 - i * 0.04, 1]}>
            <circleGeometry args={[1, 3]} />
            <meshBasicMaterial color={i % 2 ? color : featherColor} transparent opacity={0.42 - i * 0.04} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        ))}
      </group>
      {[-0.04, 0.02, 0.08].map((dy, i) => (
        <mesh key={i} position={[-0.58, dy - 0.02, 0.02 + i * 0.002]} rotation={[0, 0, Math.PI / 2 + (i - 1) * 0.24]} scale={[0.1, 0.44 - i * 0.04, 1]}>
          <circleGeometry args={[1, 3]} />
          <meshBasicMaterial color={i === 1 ? featherColor : color} transparent opacity={0.64 - i * 0.08} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function FlatLandscapePanel2D({
  kind,
  index,
  x,
  width,
  palette,
  intensity,
  beat,
  progressMs,
  section,
  params,
}: SceneProps & { kind: BirdLandscapeKind; index: number; x: number; width: number }) {
  const music = musicalPulse({ beat, intensity, section } as SceneProps);
  const mic = micReactivityFromParams(params);
  const pulse = music.groove * 0.35 + music.impact * 0.9 + music.phrase * 0.45;
  const t = progressMs / 1000;
  const items = useMemo(() => Array.from({ length: 64 }, (_, i) => i), []);
  const environmentDurationMs = 60_000;
  const environmentPhase = THREE.MathUtils.clamp(progressMs / environmentDurationMs - index, 0, 1);
  const dayArc = Math.sin(environmentPhase * Math.PI);
  const slowDrift = t * 0.035 + index * 0.9;
  const lightShift = 0.5 + Math.sin(environmentPhase * Math.PI * 2 - 0.65) * 0.5;
  const breeze = Math.sin(environmentPhase * Math.PI * 2 + index) * 0.5 + 0.5;
  const beatAttack = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.65) * intensity;
  const skyBeat = beat.beatIndex % 2 === 0 ? beatAttack * 0.5 : beatAttack * 0.18;
  const musicAccent = Math.max(music.impact * 0.92, music.phrase * 1.15, skyBeat, mic.hit * 1.08, mic.level * 0.34) * 0.82;
  const shimmer = Math.min(1.55, pulse * 0.45 + musicAccent + mic.level * 0.22);
  const spreadX = (i: number, count: number, pad = 0.9) => (
    -width / 2 + pad + (i / Math.max(1, count - 1)) * (width - pad * 2)
  );
  const sky = {
    sunset: ['#4b2d47', '#a35455', '#e99b6b', '#f4c687'],
    ocean: ['#0e4262', '#327f9b', '#80b6c2', '#d6ebdf'],
    mountains: ['#183148', '#4d7893', '#93b7c4', '#d8e3de'],
    forest: ['#133225', '#315d40', '#7a9f73', '#c7d4a4'],
    desert: ['#81573b', '#c58655', '#e0b673', '#f2d49b'],
    city: ['#10172a', '#22345b', '#5a6681', '#b19580'],
    aurora: ['#061529', '#0b3144', '#29546b', '#385f66'],
    space: ['#02030a', '#050816', '#0c1430', '#18254a'],
    festival: ['#111525', '#2b2240', '#60406a', '#9d6876'],
  }[kind];
  const groundColor = {
    sunset: '#223247',
    ocean: '#174e63',
    mountains: '#273947',
    forest: '#173421',
    desert: '#b47a44',
    city: '#111827',
    aurora: '#122532',
    space: '#03040b',
    festival: '#161826',
  }[kind];

  return (
    <group position={[x, 0, -2.45]}>
      <mesh position={[0, 0.35, -0.08]}>
        <planeGeometry args={[width + 0.08, 8.8]} />
        <meshBasicMaterial color={sky[1]} transparent opacity={0.92} depthWrite={false} />
      </mesh>
      {sky.map((color, i) => (
        <mesh key={`sky-band-${i}`} position={[Math.sin(slowDrift * (0.5 + i * 0.12)) * 0.12, 3.25 - i * 1.08 + Math.sin(environmentPhase * Math.PI + i) * (0.025 + shimmer * 0.012), -0.06 + i * 0.004]}>
          <planeGeometry args={[width + 0.7, 1.28]} />
          <meshBasicMaterial color={color} transparent opacity={0.42 + i * 0.07 + lightShift * 0.035 + shimmer * 0.025} depthWrite={false} />
        </mesh>
      ))}
      <mesh position={[0, -0.42, 0.02]}>
        <planeGeometry args={[width + 0.5, 0.42]} />
        <meshBasicMaterial color="#f4efe5" transparent opacity={kind === 'space' ? 0.02 + shimmer * 0.02 : 0.08 + dayArc * 0.06 + shimmer * 0.045} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </mesh>
      {kind !== 'space' && kind !== 'aurora' && items.slice(0, 16).map((i) => (
        <group key={`cloud-${i}`} position={[spreadX(i, 16, 1.2) + Math.sin(slowDrift + i) * 0.3 + environmentPhase * (0.35 + seededUnit(i, 702) * 0.45), 1.75 + seededUnit(i, 701) * 0.8 + Math.sin(slowDrift * 0.7 + i) * 0.08, 0.05]}>
          {[0, 1, 2].map((blob) => (
            <mesh key={blob} position={[blob * 0.28 - 0.2 + shimmer * (blob - 1) * 0.018, Math.sin(blob + i) * 0.045, blob * 0.002]} scale={[0.48 - blob * 0.045 + shimmer * 0.035, 0.18 + blob * 0.03 + shimmer * 0.012, 1]}>
              <circleGeometry args={[1, 28]} />
              <meshBasicMaterial color={kind === 'desert' || kind === 'sunset' ? '#ffe1c2' : '#eef6f7'} transparent opacity={0.12 + intensity * 0.025 + dayArc * 0.04 + shimmer * 0.08} depthWrite={false} />
            </mesh>
          ))}
        </group>
      ))}
      {items.slice(0, 24).map((i) => (
        <mesh key={`bird-music-glint-${i}`} position={[spreadX(i, 24, 0.8) + Math.sin(slowDrift * 1.7 + i) * 0.24, 0.9 + seededUnit(i, 703) * 1.9 + Math.sin(t * 0.24 + i) * shimmer * 0.16, 0.08]}>
          <circleGeometry args={[0.014 + seededUnit(i, 704) * 0.035 + shimmer * (i % 3 === 0 ? 0.045 : 0.018), 12]} />
          <meshBasicMaterial color={kind === 'space' || kind === 'aurora' ? rgb(palette.glow) : '#fff6ce'} transparent opacity={0.02 + shimmer * (i % 2 ? 0.26 : 0.38)} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
      <mesh position={[0, -2.62, 0.09]}>
        <planeGeometry args={[width + 0.1, 1.05]} />
        <meshBasicMaterial color={groundColor} transparent opacity={0.68} depthWrite={false} />
      </mesh>

      {(kind === 'sunset' || kind === 'ocean') && (
        <>
          <mesh position={[
            (kind === 'sunset' ? -width * 0.34 : width * 0.24) + environmentPhase * (kind === 'sunset' ? width * 0.18 : -width * 0.12),
            1.05 + dayArc * 0.42,
            0.02,
          ]}>
            <circleGeometry args={[0.95 + pulse * 0.08 + shimmer * 0.05, 72]} />
            <meshBasicMaterial color={kind === 'sunset' ? '#ffd091' : '#edf9ff'} transparent opacity={0.64 + dayArc * 0.16 + shimmer * 0.08} depthWrite={false} toneMapped={false} />
          </mesh>
          {items.slice(0, 18).map((i) => {
            const h = 0.28 + seededUnit(i, 705) * 0.42;
            return (
              <mesh key={`coast-${i}`} position={[spreadX(i, 18, 0.4), -0.72 + h * 0.25, 0.015]} scale={[1.35 + seededUnit(i, 706) * 0.6, h, 1]}>
                <circleGeometry args={[1, 3]} />
                <meshBasicMaterial color={kind === 'sunset' ? '#5d4b54' : '#315c64'} transparent opacity={0.25 + dayArc * 0.06} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
              </mesh>
            );
          })}
          {items.slice(0, 10).map((i) => (
            <mesh key={i} position={[Math.sin(slowDrift * 2 + i) * 0.26, -1.75 + i * 0.2 + Math.sin(t * 0.38 + i + environmentPhase * 3) * 0.035, 0.03]}>
              <planeGeometry args={[width * (0.68 + (i % 3) * 0.08 + dayArc * 0.06), 0.022 + dayArc * 0.01]} />
              <meshBasicMaterial color={i % 2 ? '#d9f3ff' : rgb(palette.glow)} transparent opacity={0.14 + intensity * 0.06 + dayArc * 0.08 + shimmer * 0.08} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {items.slice(0, 18).map((i) => (
            <mesh key={`foam-${i}`} position={[spreadX(i, 18, 0.8) + Math.sin(slowDrift * 2.4 + i) * 0.2, -1.9 + Math.sin(t * 0.48 + i) * 0.05, 0.09]} scale={[0.55 + seededUnit(i, 708) * 0.35, 0.045, 1]}>
              <circleGeometry args={[1, 28]} />
              <meshBasicMaterial color="#e7f3ed" transparent opacity={0.12 + dayArc * 0.06} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'ocean' && items.slice(0, 28).map((i) => (
            <mesh key={`swell-${i}`} position={[spreadX(i, 28, 0.7) + Math.sin(slowDrift * 2.2 + i) * 0.16, -1.38 + Math.sin(t * 0.55 + i + environmentPhase * 2) * 0.08, 0.07]} scale={[0.58 + dayArc * 0.16, 0.18 + Math.sin(environmentPhase * Math.PI + i) * 0.035, 1]}>
              <circleGeometry args={[1, 32, 0, Math.PI]} />
              <meshBasicMaterial color="#b9f0ff" transparent opacity={0.16 + dayArc * 0.1 + shimmer * 0.09} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'ocean' && items.slice(0, 9).map((i) => (
            <group key={`gull-${i}`} position={[spreadX(i, 9, 1.4) + Math.sin(slowDrift + i) * 0.28, 1.12 + seededUnit(i, 709) * 0.7, 0.12]} scale={[0.28, 0.2, 1]}>
              <BirdSilhouette color={rgb([232, 239, 238], 0.55)} />
            </group>
          ))}
        </>
      )}

      {kind === 'mountains' && (
        <>
          {[0, 1, 2].map((range) => (
            <group key={`range-${range}`} position={[Math.sin(slowDrift * (0.45 + range * 0.1)) * 0.25, -1.02 + range * 0.22, -0.015 + range * 0.004]}>
              {items.slice(0, 18).map((i) => {
                const h = 0.55 + seededUnit(i + range * 20, 713) * (0.62 + range * 0.25);
                return (
                  <mesh key={i} position={[spreadX(i, 18, 0.4), h * 0.35, 0]} scale={[1.2 + range * 0.25, h, 1]}>
                    <circleGeometry args={[1, 3]} />
                    <meshBasicMaterial color={range === 0 ? '#7790a0' : range === 1 ? '#526d80' : '#304859'} transparent opacity={0.18 + range * 0.08 + dayArc * 0.04} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
                  </mesh>
                );
              })}
            </group>
          ))}
          {items.slice(0, 38).map((i) => {
            const h = 1.0 + seededUnit(i, 710) * 1.9;
            return (
              <group key={i} position={[spreadX(i, 38, 0.6), -2.05, 0.02]} rotation={[0, 0, seededUnit(i, 711) * 0.08 - 0.04]}>
                <mesh position={[0, h / 2, 0]} scale={[0.72, h, 1]}>
                  <circleGeometry args={[1, 3]} />
                  <meshBasicMaterial color={i % 2 ? '#385f72' : '#2c4d61'} transparent opacity={0.48 + intensity * 0.09 + lightShift * 0.08} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
                </mesh>
                <mesh position={[0.07 + lightShift * 0.08, h - 0.18 + dayArc * 0.04, 0.02]} rotation={[0, 0, Math.PI]} scale={[0.22 + dayArc * 0.04, 0.3 + dayArc * 0.05, 1]}>
                  <circleGeometry args={[1, 3]} />
                  <meshBasicMaterial color="#f1f5ed" transparent opacity={0.54 + dayArc * 0.16} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
                </mesh>
              </group>
            );
          })}
          {items.slice(0, 9).map((i) => (
            <mesh key={`mist-${i}`} position={[spreadX(i, 9, 2.2) + Math.sin(slowDrift + i) * 0.36, -0.92 + (i % 3) * 0.18, 0.08 + i * 0.004]} scale={[2.8, 0.22, 1]}>
              <circleGeometry args={[1, 32]} />
              <meshBasicMaterial color="#d8edf1" transparent opacity={0.08 + dayArc * 0.055 + shimmer * 0.045} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </>
      )}

      {kind === 'forest' && items.map((i) => {
        const h = 0.8 + seededUnit(i, 720) * 1.55;
        const sway = Math.sin(slowDrift * 2.4 + i * 0.7) * (0.035 + breeze * 0.035);
        return (
          <group key={i} position={[spreadX(i, items.length, 0.55), -2.25, 0.02]} rotation={[0, 0, sway]}>
            <mesh position={[0, h * 0.35, 0]}>
              <planeGeometry args={[0.06, h * 0.7]} />
              <meshBasicMaterial color="#5d3f2f" transparent opacity={0.55 + intensity * 0.08} />
            </mesh>
            <mesh position={[0, h * 0.8, 0.01]} scale={[0.35, 0.6, 1]}>
              <circleGeometry args={[1, 18]} />
              <meshBasicMaterial color={i % 2 ? '#23623d' : '#2d7b4a'} transparent opacity={0.48 + intensity * 0.1} depthWrite={false} toneMapped={false} />
            </mesh>
            {[0, 1].map((blob) => (
              <mesh key={blob} position={[blob ? 0.16 : -0.14, h * 0.84 + blob * 0.08, 0.02]} scale={[0.28 + dayArc * 0.03, 0.32 + lightShift * 0.03, 1]}>
                <circleGeometry args={[1, 16]} />
                <meshBasicMaterial color={blob ? '#315f35' : '#3b884f'} transparent opacity={0.36 + dayArc * 0.08} depthWrite={false} />
              </mesh>
            ))}
          </group>
        );
      })}
      {kind === 'forest' && items.slice(0, 36).map((i) => (
        <mesh key={`underbrush-${i}`} position={[spreadX(i, 36, 0.5), -2.2 + seededUnit(i, 724) * 0.18, 0.085]} scale={[0.3 + seededUnit(i, 725) * 0.18, 0.16 + seededUnit(i, 726) * 0.15, 1]}>
          <circleGeometry args={[1, 14]} />
          <meshBasicMaterial color={i % 2 ? '#214d2f' : '#2f6840'} transparent opacity={0.5 + dayArc * 0.08} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
      {kind === 'forest' && items.slice(0, 12).map((i) => (
        <mesh key={`forest-ray-${i}`} position={[spreadX(i, 12, 1.3) + Math.sin(slowDrift + i) * 0.18, 0.25, 0.08]} rotation={[0, 0, -0.18 + (i % 5) * 0.045]}>
          <planeGeometry args={[0.22, 4.6]} />
          <meshBasicMaterial color="#d9f6d7" transparent opacity={0.045 + dayArc * 0.035 + shimmer * 0.055} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}

      {kind === 'desert' && (
        <>
          <mesh position={[width * 0.18 - environmentPhase * width * 0.08, 1.18 + dayArc * 0.28, 0.01]}>
            <circleGeometry args={[0.62, 64]} />
            <meshBasicMaterial color="#ffe1a8" transparent opacity={0.36 + dayArc * 0.22} depthWrite={false} toneMapped={false} />
          </mesh>
          {items.slice(0, 26).map((i) => (
            <group key={i} position={[spreadX(i, 26, 0.6), -2.1 + Math.sin(i + slowDrift * 1.4) * 0.07, 0.02]}>
              <mesh scale={[1.1 + seededUnit(i, 730) * 0.7 + dayArc * 0.08, 0.45, 1]}>
                <circleGeometry args={[1, 32, 0, Math.PI]} />
                <meshBasicMaterial color={i % 2 ? '#d89d5b' : '#b98248'} transparent opacity={0.34 + intensity * 0.05 + lightShift * 0.08} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
              </mesh>
              <mesh position={[0.15 + lightShift * 0.18, -0.08, 0.01]} scale={[0.62, 0.08, 1]}>
                <circleGeometry args={[1, 32]} />
                <meshBasicMaterial color="#7a5437" transparent opacity={0.08 + dayArc * 0.06} depthWrite={false} toneMapped={false} />
              </mesh>
              {i % 3 === 0 && (
                <group position={[0.26, 0.34, 0.04]} rotation={[0, 0, Math.sin(slowDrift * 2 + i) * 0.04]}>
                  <mesh position={[0, 0.28, 0]}>
                    <planeGeometry args={[0.06, 0.56]} />
                    <meshBasicMaterial color="#436747" transparent opacity={0.7} depthWrite={false} />
                  </mesh>
                  <mesh position={[-0.1, 0.35, 0]} rotation={[0, 0, -0.4]}>
                    <planeGeometry args={[0.05, 0.34]} />
                    <meshBasicMaterial color="#436747" transparent opacity={0.65} depthWrite={false} />
                  </mesh>
                </group>
              )}
            </group>
          ))}
          {items.slice(0, 18).map((i) => (
            <mesh key={`rock-${i}`} position={[spreadX(i, 18, 0.75), -2.23 + seededUnit(i, 734) * 0.18, 0.085]} rotation={[0, 0, seededUnit(i, 735) * 0.8 - 0.4]} scale={[0.16 + seededUnit(i, 736) * 0.26, 0.08 + seededUnit(i, 737) * 0.16, 1]}>
              <circleGeometry args={[1, 6]} />
              <meshBasicMaterial color={i % 2 ? '#6f4b35' : '#8a6548'} transparent opacity={0.44 + dayArc * 0.08} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {items.slice(0, 14).map((i) => (
            <mesh key={`heat-${i}`} position={[spreadX(i, 14, 1.1) + Math.sin(slowDrift * 1.7 + i) * 0.2, -0.65 + (i % 5) * 0.13, 0.08]} rotation={[0, 0, Math.sin(slowDrift + i) * 0.05]}>
              <planeGeometry args={[1.25, 0.025]} />
              <meshBasicMaterial color="#fff0c8" transparent opacity={0.06 + dayArc * 0.05 + shimmer * 0.06} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </>
      )}

      {kind === 'city' && items.map((i) => {
        const h = 0.65 + seededUnit(i, 740) * 2.55 + (i % 4 === beat.barIndex % 4 ? pulse * 0.28 : 0);
        const windowCycle = 0.5 + Math.sin(environmentPhase * Math.PI * 2 + i * 1.7) * 0.5;
        return (
          <group key={i} position={[spreadX(i, items.length, 0.35), -2.25, 0.02]}>
            <mesh position={[0, h / 2, 0]}>
              <planeGeometry args={[0.34, h]} />
              <meshBasicMaterial color={i % 2 ? '#27364c' : '#1d293c'} transparent opacity={0.7 + lightShift * 0.12} depthWrite={false} toneMapped={false} />
            </mesh>
            {Array.from({ length: Math.max(1, Math.floor(h * 2.2)) }, (_, w) => (
              <mesh key={w} position={[w % 2 ? 0.07 : -0.07, 0.24 + w * 0.34, 0.03]}>
                <planeGeometry args={[0.055, 0.075]} />
                <meshBasicMaterial color={w % 3 === beat.barIndex % 3 ? '#f9df8b' : '#6eb8d8'} transparent opacity={0.24 + windowCycle * 0.18 + pulse * 0.08 + shimmer * 0.1} depthWrite={false} toneMapped={false} />
              </mesh>
            ))}
          </group>
        );
      })}
      {kind === 'city' && (
        <>
          <mesh position={[0, -2.15, 0.075]}>
            <planeGeometry args={[width + 0.1, 0.18]} />
            <meshBasicMaterial color="#0a0d12" transparent opacity={0.72} depthWrite={false} />
          </mesh>
          {items.slice(0, 24).map((i) => (
            <mesh key={`lane-${i}`} position={[spreadX(i, 24, 0.3), -2.15, 0.09]}>
              <planeGeometry args={[0.34, 0.018]} />
              <meshBasicMaterial color="#d9c48f" transparent opacity={0.32 + pulse * 0.04} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {items.slice(0, 11).map((i) => (
            <mesh key={`antenna-${i}`} position={[spreadX(i, 11, 1.2), 0.25 + seededUnit(i, 744) * 1.2, 0.07]}>
              <planeGeometry args={[0.025, 0.6 + seededUnit(i, 745) * 0.6]} />
              <meshBasicMaterial color="#536274" transparent opacity={0.45} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </>
      )}
      {kind === 'city' && items.slice(0, 18).map((i) => (
        <mesh key={`car-${i}`} position={[-width / 2 + ((i * 2.8 + environmentPhase * width * 0.18) % width), -2.08 + (i % 2) * 0.08, 0.08]}>
          <planeGeometry args={[0.18, 0.035]} />
          <meshBasicMaterial color={i % 2 ? '#ffd267' : '#e94b5f'} transparent opacity={0.3 + pulse * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}

      {(kind === 'aurora' || kind === 'space') && (
        <>
          {kind === 'aurora' && items.slice(0, 24).map((i) => {
            const h = 0.62 + seededUnit(i, 748) * 0.95;
            return (
              <mesh key={`aurora-mountain-${i}`} position={[spreadX(i, 24, 0.3), -1.9 + h * 0.5, 0.015]} scale={[0.82, h, 1]}>
                <circleGeometry args={[1, 3]} />
                <meshBasicMaterial color={i % 2 ? '#172b37' : '#203848'} transparent opacity={0.74} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
              </mesh>
            );
          })}
          {kind === 'aurora' && items.slice(0, 14).map((i) => (
            <mesh key={`curtain-${i}`} position={[spreadX(i, 14, 1) + Math.sin(environmentPhase * Math.PI * 2 + i) * 0.22, 0.4 + Math.sin(t * 0.18 + i) * 0.25, 0.01]} rotation={[0, 0, Math.sin(t * 0.14 + i) * 0.2]}>
              <planeGeometry args={[0.55 + dayArc * 0.24, 3.2 + Math.sin(environmentPhase * Math.PI + i) * 0.3]} />
              <meshBasicMaterial color={i % 2 ? '#67f0b5' : '#8b8cff'} transparent opacity={0.08 + intensity * 0.035 + dayArc * 0.055 + shimmer * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'space' && items.slice(0, 42).map((i) => (
            <mesh key={`dust-${i}`} position={[spreadX(i, 42, 0.8) + Math.sin(slowDrift + i) * 0.12, Math.sin(i * 0.9 + t * 0.22) * 1.1 + 0.35, 0.03]}>
              <circleGeometry args={[0.018 + seededUnit(i, 750) * 0.045, 12]} />
              <meshBasicMaterial color="#91b8e0" transparent opacity={0.16 + dayArc * 0.05 + shimmer * 0.16} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'space' && items.map((i) => (
            <mesh key={`star-${i}`} position={[-width / 2 + seededUnit(i, 760) * width + Math.sin(slowDrift + i) * (0.035 + shimmer * 0.03), -2.4 + seededUnit(i, 761) * 5.1, 0.04]}>
              <circleGeometry args={[0.018 + seededUnit(i, 762) * 0.035 + shimmer * (i % 4 === beat.barIndex % 4 ? 0.035 : 0.01), 12]} />
              <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.24 + intensity * 0.16 + Math.sin(environmentPhase * Math.PI * 4 + i) * 0.1 + shimmer * 0.24} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'space' && items.slice(0, 7).map((i) => (
            <mesh key={`distant-planet-${i}`} position={[spreadX(i, 7, 2) + Math.sin(slowDrift * 0.4 + i) * 0.18, -0.8 + seededUnit(i, 764) * 2.7, 0.015]} scale={[0.18 + seededUnit(i, 765) * 0.28, 0.18 + seededUnit(i, 765) * 0.28, 1]}>
              <circleGeometry args={[1, 40]} />
              <meshBasicMaterial color={i % 2 ? '#b8ccd5' : '#8f7cbd'} transparent opacity={0.12 + dayArc * 0.04} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {kind === 'space' && (
            <mesh position={[width * 0.26 - environmentPhase * width * 0.08, 1.35 + Math.sin(environmentPhase * Math.PI) * 0.18, 0.02]} scale={[0.38, 0.38, 1]}>
              <circleGeometry args={[1, 48]} />
              <meshBasicMaterial color="#d8ecff" transparent opacity={0.16 + dayArc * 0.08} depthWrite={false} toneMapped={false} />
            </mesh>
          )}
        </>
      )}

      {kind === 'festival' && items.map((i) => {
        const h = 0.35 + seededUnit(i, 770) * 1.35 + pulse * (i % 3 === 0 ? 0.7 : 0.28) + shimmer * (i % 4 === beat.barIndex % 4 ? 0.34 : 0.12) + Math.sin(environmentPhase * Math.PI * 2 + i) * 0.08;
        return (
          <group key={i} position={[spreadX(i, items.length, 0.4), -2.3, 0.02]}>
            <mesh position={[0, h / 2, 0]}>
              <planeGeometry args={[0.07, h]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.accent)} transparent opacity={0.32 + intensity * 0.2 + dayArc * 0.08 + shimmer * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh position={[0, h + 0.16, 0.01]}>
              <circleGeometry args={[0.055 + seededUnit(i, 771) * 0.05, 16]} />
              <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.24 + pulse * 0.18 + dayArc * 0.08 + shimmer * 0.16} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          </group>
        );
      })}
      {kind === 'festival' && (
        <>
          <mesh position={[0, -0.38, 0.02]}>
            <planeGeometry args={[width * 0.52, 1.18]} />
            <meshBasicMaterial color="#11131e" transparent opacity={0.72} depthWrite={false} />
          </mesh>
          {items.slice(0, 14).map((i) => (
            <mesh key={`screen-${i}`} position={[spreadX(i, 14, 2.2), -0.38 + Math.sin(i) * 0.08, 0.055]}>
              <planeGeometry args={[0.52, 0.36]} />
              <meshBasicMaterial color={i % 2 ? '#2f7ea1' : '#b05a73'} transparent opacity={0.16 + pulse * 0.08 + dayArc * 0.04 + shimmer * 0.1} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </>
      )}
      {kind === 'festival' && [-1, 0, 1].map((side) => (
        <group key={`stage-${side}`} position={[side * width * 0.27, -1.1, 0.06]}>
          <mesh position={[0, 0.55, 0]}>
            <planeGeometry args={[0.16, 1.6]} />
            <meshBasicMaterial color="#151923" transparent opacity={0.75} depthWrite={false} />
          </mesh>
          <mesh position={[(side || 1) * -0.5, 1.25, 0.02]} rotation={[0, 0, (side || 1) * (0.48 + Math.sin(environmentPhase * Math.PI * 2) * 0.28)]}>
            <planeGeometry args={[0.05, 2.5]} />
            <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.18 + pulse * 0.12 + dayArc * 0.09 + shimmer * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        </group>
      ))}
      {kind === 'festival' && items.slice(0, 10).map((i) => (
        <mesh key={`smoke-${i}`} position={[spreadX(i, 10, 1.2) + Math.sin(slowDrift + i) * 0.2, -0.72 + Math.sin(environmentPhase * Math.PI + i) * 0.12, 0.05]} scale={[1.4, 0.38, 1]}>
          <circleGeometry args={[1, 32]} />
          <meshBasicMaterial color="#d7e5ee" transparent opacity={0.055 + dayArc * 0.04} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

type DoodleQuestKind = 'farm' | 'chicago' | 'road' | 'san-francisco' | 'snowboard' | 'kayak' | 'festival' | 'san-francisco-return';

function PixelRect({
  x,
  y,
  w,
  h,
  color,
  opacity = 1,
  z = 0,
  rotation = 0,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  opacity?: number;
  z?: number;
  rotation?: number;
}) {
  return (
    <mesh position={[x, y, z]} rotation={[0, 0, rotation]}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

function micReactivityFromParams(params: SceneProps['params'] = {}) {
  const rawEnergy = typeof params.micEnergy === 'number' ? params.micEnergy : 0;
  const rawOnset = typeof params.micOnset === 'number' ? params.micOnset : 0;
  const level = THREE.MathUtils.clamp(rawEnergy * 3.2, 0, 1.45);
  const hit = THREE.MathUtils.clamp(rawOnset * 8.5, 0, 1.65);
  const pulse = Math.max(hit, level * 0.48);
  const lift = THREE.MathUtils.clamp(level * 0.32 + hit * 0.72, 0, 1.6);
  return {
    active: rawEnergy > 0.004 || rawOnset > 0.004,
    level,
    hit,
    pulse,
    lift,
  };
}

function MoodEffects2D({
  visualStyle,
  palette,
  intensity,
  beat,
  progressMs,
  params,
  music,
  foreground = false,
}: Pick<SceneProps, 'visualStyle' | 'palette' | 'intensity' | 'beat' | 'progressMs' | 'params'> & {
  music: { groove: number; impact: number; phrase: number };
  foreground?: boolean;
}) {
  const t = progressMs / 1000;
  const items = useMemo(() => Array.from({ length: 72 }, (_, i) => i), []);
  if (visualStyle === 'none') return null;

  const mic = micReactivityFromParams(params);
  const beatPop = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.45) * intensity;
  const impact = Math.max(music.impact, music.phrase * 0.85, beatPop * 0.28, mic.hit * 0.88, mic.level * 0.22);
  const moodGain = visualStyle === 'minimal' || visualStyle === 'cinematic' ? 0.55 : visualStyle === 'aggressive' || visualStyle === 'psychedelic' ? 1.18 : 0.9;
  const z = foreground ? -0.72 : -3.02;

  if (visualStyle === 'plasma') {
    const bands = [0, 1, 2, 3, 4];
    const segments = 34;
    const point = (band: number, i: number): [number, number, number] => {
      const x = -6 + (i / segments) * 12;
      const waveA = Math.sin(i * 0.46 + t * (0.62 + band * 0.08) + band * 1.4);
      const waveB = Math.sin(i * 0.18 - t * 0.38 + band);
      const y = -2.05 + band * 1.02 + (waveA * 0.28 + waveB * 0.16) * (0.7 + intensity * 0.5 + impact * 0.18);
      return [x, y, z];
    };
    return (
      <group>
        {!foreground && bands.map((band) => (
          <group key={`plasma-band-${band}`}>
            {Array.from({ length: segments }, (_, i) => (
              <ConnectionLine
                key={`plasma-seg-${band}-${i}`}
                from={point(band, i)}
                to={point(band, i + 1)}
                color={rgb(band % 3 === 0 ? palette.primary : band % 3 === 1 ? palette.secondary : palette.accent)}
                opacity={0.16 + impact * 0.16}
              />
            ))}
          </group>
        ))}
        {items.slice(0, foreground ? 9 : 14).map((i) => {
          const a = t * (0.22 + i * 0.012) + i * 1.4;
          const r = 0.9 + (i % 5) * 0.46 + impact * 0.24;
          return (
            <mesh key={`plasma-orbit-${i}`} position={[Math.cos(a) * r, Math.sin(a * 0.82) * r * 0.62, foreground ? -0.66 : -1.4]}>
              <circleGeometry args={[0.035 + impact * 0.04 + seededUnit(i, 930) * 0.035, 18]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.glow : palette.accent)} transparent opacity={0.42 + impact * 0.32} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          );
        })}
      </group>
    );
  }

  if (visualStyle === 'vortex') {
    return (
      <group>
        {items.slice(0, foreground ? 8 : 18).map((i) => {
          const p = (i / (foreground ? 8 : 18) + t * (0.018 + impact * 0.014)) % 1;
          const radius = 0.25 + p * 5.1;
          const sides = 6 + (i % 3) * 2;
          return (
            <mesh key={`vortex-ring-${i}`} position={[0, 0, z]} rotation={[0, 0, t * (0.08 + i * 0.01) * (i % 2 ? -1 : 1)]}>
              <circleGeometry args={[radius * (1 + Math.sin(t * 0.8 + i) * 0.018), sides]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.glow : palette.accent)} transparent opacity={(1 - p) * (foreground ? 0.07 : 0.13) + impact * 0.05} blending={THREE.AdditiveBlending} depthWrite={false} wireframe toneMapped={false} />
            </mesh>
          );
        })}
        {items.slice(0, foreground ? 36 : 58).map((i) => {
          const p = (seededUnit(i, 940) + t * (0.018 + seededUnit(i, 941) * 0.035 + impact * 0.03)) % 1;
          const a = seededUnit(i, 942) * Math.PI * 2 + t * 0.18;
          const r = 0.18 + p * 5.6;
          return (
            <mesh key={`vortex-star-${i}`} position={[Math.cos(a) * r, Math.sin(a) * r * 0.58, foreground ? -0.64 : -1.35]}>
              <circleGeometry args={[0.014 + p * 0.035 + impact * 0.012, 10]} />
              <meshBasicMaterial color={rgb(i % 4 ? palette.glow : palette.secondary)} transparent opacity={0.18 + p * 0.55 + impact * 0.18} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          );
        })}
      </group>
    );
  }

  if (visualStyle === 'strobe-burst') {
    const beamOpacity = 0.1 + impact * 0.34;
    return (
      <group>
        {items.slice(0, foreground ? 8 : 12).map((i) => {
          const angle = t * 0.26 + i * (Math.PI / 6) + beat.barIndex * 0.18;
          return (
            <mesh key={`strobe-beam-${i}`} position={[0, 0, z]} rotation={[0, 0, angle]}>
              <planeGeometry args={[0.07 + impact * 0.08, 13]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.accent : i % 3 === 1 ? palette.glow : palette.primary)} transparent opacity={beamOpacity * (foreground ? 0.55 : 0.82)} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          );
        })}
        {items.slice(0, 5).map((i) => (
          <mesh key={`strobe-ripple-${i}`} position={[0, 0, foreground ? -0.63 : -1.28]}>
            <torusGeometry args={[0.55 + i * 0.75 + impact * (0.25 + i * 0.08), 0.01 + impact * 0.012, 5, 140]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.secondary : palette.glow)} transparent opacity={(0.11 - i * 0.014) + impact * 0.16} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
        {foreground && impact > 0.16 && (
          <mesh position={[0, 0, -0.61]}>
            <planeGeometry args={[18, 10]} />
            <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={Math.min(0.18, impact * 0.12)} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        )}
      </group>
    );
  }

  if (visualStyle === 'flash') {
    const flash = Math.max(0, impact * 0.75 + music.phrase * 0.45);
    return (
      <group>
        {!foreground && (
          <mesh position={[0, 0, -3.08]}>
            <planeGeometry args={[18, 10]} />
            <meshBasicMaterial color="#000000" transparent opacity={0.18 + flash * 0.28} depthWrite={false} toneMapped={false} />
          </mesh>
        )}
        {foreground && flash > 0.03 && (
          <>
            <mesh position={[0, 0, -0.62]}>
              <planeGeometry args={[18, 10]} />
              <meshBasicMaterial color={flash > 0.42 ? '#ffffff' : rgb(palette.glow)} transparent opacity={Math.min(0.62, flash * 0.56)} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            {items.slice(0, 10).map((i) => (
              <ConnectionLine key={`flash-scan-${i}`} from={[-6.2, -2.8 + i * 0.62, -0.61]} to={[6.2, -2.8 + i * 0.62, -0.61]} color={rgb(i % 2 ? palette.accent : palette.glow)} opacity={0.05 + flash * 0.18} />
            ))}
          </>
        )}
      </group>
    );
  }

  if (visualStyle === 'neon-journey') {
    const roadY = -1.35;
    return (
      <group>
        {!foreground && (
          <>
            <mesh position={[0, 0.7, -3.08]}>
              <planeGeometry args={[18, 6.6]} />
              <meshBasicMaterial color="#080015" transparent opacity={0.62} depthWrite={false} toneMapped={false} />
            </mesh>
            {items.slice(0, 26).map((i) => {
              const side = i % 2 ? 1 : -1;
              const depth = seededUnit(i, 950);
              const x = side * (2.7 + depth * 3.4);
              const h = 0.7 + seededUnit(i, 951) * 2.6 + impact * 0.15;
              return (
                <group key={`journey-building-${i}`} position={[x, roadY + h / 2 - 0.15, -1.65 + depth * 0.2]}>
                  <mesh>
                    <planeGeometry args={[0.34 + seededUnit(i, 952) * 0.58, h]} />
                    <meshBasicMaterial color="#05000d" transparent opacity={0.72} depthWrite={false} toneMapped={false} />
                  </mesh>
                  {Array.from({ length: 4 }, (_, w) => (
                    <mesh key={w} position={[0, -h * 0.32 + w * h * 0.18, 0.01]}>
                      <planeGeometry args={[0.12, 0.035 + impact * 0.018]} />
                      <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.16 + impact * 0.42} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
                    </mesh>
                  ))}
                </group>
              );
            })}
            {[-1, 1].map((side) => (
              <ConnectionLine key={`journey-road-${side}`} from={[side * 0.18, roadY + 0.34, -1.3]} to={[side * 5.8, -3.0, -1.3]} color={rgb(palette.glow)} opacity={0.16 + impact * 0.16} />
            ))}
          </>
        )}
        {foreground && (
          <group position={[-3.1 + Math.sin(t * 0.22) * 0.3, 0.3 + Math.sin(t * 0.86) * 0.24 + impact * 0.32, -0.62]} scale={[0.8 + impact * 0.08, 0.8 + impact * 0.08, 1]}>
            <mesh position={[0, 0, 0]} scale={[0.4, 0.14, 1]}>
              <circleGeometry args={[1, 28]} />
              <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.9} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh position={[-0.35, Math.sin(t * 7.5) * 0.24, 0.01]} rotation={[0, 0, -0.42 + Math.sin(t * 7.5) * 0.45]}>
              <circleGeometry args={[0.42, 3]} />
              <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.78} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
            <mesh position={[0.35, -Math.sin(t * 7.5) * 0.24, 0.01]} rotation={[0, 0, 0.42 - Math.sin(t * 7.5) * 0.45]}>
              <circleGeometry args={[0.42, 3]} />
              <meshBasicMaterial color={rgb(palette.secondary)} transparent opacity={0.78} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
            </mesh>
          </group>
        )}
      </group>
    );
  }

  if (visualStyle === 'horizon-journey') {
    const scroll = (t * (0.08 + intensity * 0.04)) % 1;
    return (
      <group>
        {!foreground && (
          <>
            <mesh position={[0, 1.1, -3.1]}>
              <planeGeometry args={[18, 5.0]} />
              <meshBasicMaterial color="#103454" transparent opacity={0.34} depthWrite={false} toneMapped={false} />
            </mesh>
            {[0, 1, 2].map((layer) => (
              <group key={`horizon-layer-${layer}`}>
                {items.slice(0, 10).map((i) => {
                  const x = -6.6 + (((i / 10 + scroll * (0.28 + layer * 0.18)) % 1) * 13.2);
                  const y = -0.95 + layer * 0.42;
                  const size = 0.72 + seededUnit(i + layer * 13, 960) * 0.58;
                  return (
                    <mesh key={`horizon-mountain-${layer}-${i}`} position={[x, y, -1.7 + layer * 0.04]} rotation={[0, 0, Math.PI / 4]}>
                      <planeGeometry args={[size, size]} />
                      <meshBasicMaterial color={layer === 0 ? '#183348' : layer === 1 ? '#24506a' : '#3a7590'} transparent opacity={0.38 + impact * 0.08} depthWrite={false} toneMapped={false} />
                    </mesh>
                  );
                })}
              </group>
            ))}
            {items.slice(0, 5).map((i) => {
              const y = -1.55 - i * 0.22;
              return (
                <ConnectionLine
                  key={`horizon-wave-${i}`}
                  from={[-6.1, y + Math.sin(t * 0.4 + i) * 0.04, -1.2]}
                  to={[6.1, y + Math.sin(t * 0.4 + i + 1) * 0.04, -1.2]}
                  color={rgb(i % 2 ? palette.secondary : palette.glow)}
                  opacity={0.08 + impact * 0.12}
                />
              );
            })}
          </>
        )}
        {foreground && items.slice(0, 5).map((i) => (
          <mesh key={`horizon-cloud-${i}`} position={[-5.2 + (((i * 0.23 + scroll * 0.25) % 1) * 10.4), 1.35 + seededUnit(i, 970) * 0.9, -0.66]} scale={[1 + seededUnit(i, 971) * 0.7, 0.42, 1]}>
            <circleGeometry args={[0.32 + impact * 0.03, 24]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={0.14 + impact * 0.1} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'minimal') {
    return (
      <group>
        {items.slice(0, 14).map((i) => (
          <ConnectionLine key={`minimal-v-${i}`} from={[-6 + i * 0.92, -3.05, -1.05]} to={[-6 + i * 0.92, 3.05, -1.05]} color={rgb(palette.glow)} opacity={foreground ? 0.015 : 0.025 + impact * 0.025} />
        ))}
        {items.slice(0, 8).map((i) => (
          <ConnectionLine key={`minimal-h-${i}`} from={[-6.2, -2.7 + i * 0.78, -1.04]} to={[6.2, -2.7 + i * 0.78, -1.04]} color={rgb(palette.secondary)} opacity={foreground ? 0.012 : 0.022 + impact * 0.018} />
        ))}
      </group>
    );
  }

  if (visualStyle === 'cinematic') {
    return (
      <group>
        {!foreground && (
          <>
            <mesh position={[0, 3.08, -0.95]}>
              <planeGeometry args={[14, 0.9]} />
              <meshBasicMaterial color="#05070a" transparent opacity={0.68} depthWrite={false} toneMapped={false} />
            </mesh>
            <mesh position={[0, -3.08, -0.95]}>
              <planeGeometry args={[14, 0.9]} />
              <meshBasicMaterial color="#05070a" transparent opacity={0.68} depthWrite={false} toneMapped={false} />
            </mesh>
          </>
        )}
        {foreground && items.slice(0, 18).map((i) => (
          <mesh key={`film-${i}`} position={[-5.8 + i * 0.68 + Math.sin(t * 0.22 + i) * 0.03, 0, z]} rotation={[0, 0, 0.015]}>
            <planeGeometry args={[0.025, 5.8]} />
            <meshBasicMaterial color="#ffffff" transparent opacity={0.015 + seededUnit(i, 880) * 0.018 + impact * 0.01} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'festival') {
    return (
      <group>
        {items.slice(0, foreground ? 18 : 28).map((i) => {
          const side = i % 2 ? 1 : -1;
          const y = -1.8 + (i % 9) * 0.48;
          return (
            <mesh key={`festival-beam-${i}`} position={[side * (3.6 + seededUnit(i, 890) * 1.6), y, z]} rotation={[0, 0, side * (-0.54 + Math.sin(t * 0.35 + i) * 0.22)]}>
              <planeGeometry args={[0.06 + impact * 0.045, 3.0 + impact * 0.7]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.glow : i % 3 === 1 ? palette.accent : palette.primary)} transparent opacity={(foreground ? 0.06 : 0.09) + impact * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          );
        })}
        {foreground && items.slice(0, 36).map((i) => (
          <mesh key={`festival-confetti-${i}`} position={[-5.9 + seededUnit(i, 891) * 11.8, -2.5 + ((t * (0.24 + seededUnit(i, 892) * 0.22) + seededUnit(i, 893) * 6) % 5.4), -0.68]} rotation={[0, 0, t * (0.4 + seededUnit(i, 894)) + i]}>
            <planeGeometry args={[0.045 + impact * 0.035, 0.12 + seededUnit(i, 895) * 0.1]} />
            <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.accent : i % 3 === 1 ? palette.secondary : palette.glow)} transparent opacity={0.28 + impact * 0.28} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'psychedelic') {
    return (
      <group>
        {items.slice(0, foreground ? 9 : 14).map((i) => (
          <mesh key={`psych-ring-${i}`} position={[Math.sin(t * 0.13 + i) * 2.1, Math.cos(t * 0.11 + i * 0.7) * 1.25, z]} rotation={[0, 0, t * (0.08 + i * 0.008)]}>
            <torusGeometry args={[0.7 + i * 0.22 + impact * 0.16, 0.012 + impact * 0.01, 5, 90]} />
            <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.accent : palette.glow)} transparent opacity={(foreground ? 0.09 : 0.13) * moodGain + impact * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
        {foreground && items.slice(0, 12).map((i) => (
          <mesh key={`psych-prism-${i}`} position={[-5.4 + i * 0.98, Math.sin(t * 0.45 + i) * 2.0, -0.66]} rotation={[0, 0, t * 0.18 + i]}>
            <circleGeometry args={[0.18 + impact * 0.09, 3]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.secondary : palette.accent)} transparent opacity={0.16 + impact * 0.18} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'emotional') {
    return (
      <group>
        {items.slice(0, foreground ? 16 : 10).map((i) => (
          <mesh key={`emotional-glow-${i}`} position={[-5.4 + seededUnit(i, 900) * 10.8, -2.2 + seededUnit(i, 901) * 4.4 + Math.sin(t * 0.18 + i) * 0.16, z]}>
            <circleGeometry args={[0.18 + seededUnit(i, 902) * 0.34 + impact * 0.08, 32]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.secondary)} transparent opacity={(foreground ? 0.08 : 0.11) + impact * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'aggressive') {
    return (
      <group>
        {items.slice(0, foreground ? 18 : 24).map((i) => (
          <mesh key={`aggressive-slash-${i}`} position={[-6.4 + ((i * 1.2 + t * (0.8 + impact * 1.6)) % 13), -2.6 + (i % 8) * 0.72, z]} rotation={[0, 0, -0.72]}>
            <planeGeometry args={[0.08 + impact * 0.05, 1.4 + impact * 0.8]} />
            <meshBasicMaterial color={rgb(i % 2 ? palette.accent : palette.glow)} transparent opacity={(foreground ? 0.12 : 0.16) + impact * 0.2} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  if (visualStyle === 'cyberpunk') {
    return (
      <group>
        {items.slice(0, foreground ? 14 : 22).map((i) => (
          <mesh key={`cyber-rain-${i}`} position={[-5.9 + seededUnit(i, 910) * 11.8, 2.9 - ((t * (0.45 + seededUnit(i, 911) * 0.75) + seededUnit(i, 912) * 6) % 6.1), z]}>
            <planeGeometry args={[0.035 + impact * 0.014, 0.35 + seededUnit(i, 913) * 0.7]} />
            <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.glow : palette.primary)} transparent opacity={(foreground ? 0.12 : 0.16) + impact * 0.13} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
        {foreground && items.slice(0, 9).map((i) => (
          <ConnectionLine key={`cyber-scan-${i}`} from={[-6.2, -2.6 + i * 0.64, -0.65]} to={[6.2, -2.6 + i * 0.64, -0.65]} color={rgb(palette.secondary)} opacity={0.055 + impact * 0.06} />
        ))}
      </group>
    );
  }

  if (visualStyle === 'space') {
    return (
      <group>
        {items.slice(0, foreground ? 42 : 64).map((i) => (
          <mesh key={`space-star-${i}`} position={[-6 + seededUnit(i, 920) * 12, -3 + seededUnit(i, 921) * 6, z]}>
            <circleGeometry args={[0.012 + seededUnit(i, 922) * 0.035 + impact * (i % 5 === beat.barIndex ? 0.025 : 0.004), 10]} />
            <meshBasicMaterial color={rgb(i % 4 ? palette.glow : palette.accent)} transparent opacity={(foreground ? 0.18 : 0.22) + seededUnit(i, 923) * 0.16 + impact * 0.18} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
        {foreground && items.slice(0, 5).map((i) => (
          <mesh key={`space-comet-${i}`} position={[-5.6 + ((t * (0.24 + i * 0.04) + i * 2.5) % 11.2), 2.2 - i * 0.9 + Math.sin(t * 0.2 + i) * 0.18, -0.66]} rotation={[0, 0, -0.18]}>
            <planeGeometry args={[0.95 + impact * 0.3, 0.025 + impact * 0.02]} />
            <meshBasicMaterial color={rgb(palette.glow)} transparent opacity={0.18 + impact * 0.24} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    );
  }

  return null;
}

function PixelDoodle({
  coat = 'blonde',
  scale = 1,
  riding,
  barkSignal = 0,
  dilatedPupils = false,
}: {
  coat?: 'blonde' | 'brunette';
  scale?: number;
  riding?: 'snowboard' | 'kayak';
  barkSignal?: number;
  dilatedPupils?: boolean;
}) {
  const fur = coat === 'brunette' ? '#6a4a32' : '#d9b66b';
  const furMid = coat === 'brunette' ? '#7a563a' : '#c99d50';
  const furDark = coat === 'brunette' ? '#3f2b1f' : '#9d7439';
  const furLight = coat === 'brunette' ? '#9b7350' : '#f2d897';
  const furCream = coat === 'brunette' ? '#b99368' : '#ffe7ad';
  const collar = coat === 'brunette' ? '#d36b7d' : '#2e7ecb';
  const barkOpen = Math.min(1, barkSignal);
  const curlPatches = [
    [-0.66, 0.06, 0.18, furLight, 0.72],
    [-0.5, 0.18, 0.16, furMid, 0.66],
    [-0.31, 0.1, 0.18, furCream, 0.58],
    [-0.12, 0.18, 0.17, furLight, 0.62],
    [0.07, 0.08, 0.19, furMid, 0.64],
    [0.25, 0.2, 0.16, furCream, 0.58],
    [0.43, 0.05, 0.17, furLight, 0.62],
  ] as const;
  const headCurls = [
    [0.38, 0.44, 0.15, furLight, 0.72],
    [0.53, 0.5, 0.14, furCream, 0.68],
    [0.68, 0.42, 0.13, furMid, 0.58],
    [0.37, 0.27, 0.14, furMid, 0.62],
    [0.62, 0.25, 0.13, furLight, 0.64],
  ] as const;
  return (
    <group scale={[scale, scale, 1]}>
      {riding === 'snowboard' && (
        <group position={[0, -0.62, -0.02]} rotation={[0, 0, -0.08]}>
          <PixelRect x={0} y={0} w={1.86} h={0.11} color="#273141" z={0} />
          <PixelRect x={-0.78} y={0.08} w={0.28} h={0.08} color="#edf4f7" z={0.01} />
          <PixelRect x={0.78} y={0.08} w={0.28} h={0.08} color="#edf4f7" z={0.01} />
          <PixelRect x={0.02} y={0.08} w={0.34} h={0.04} color="#61a5d8" z={0.02} />
        </group>
      )}
      {riding === 'kayak' && (
        <group position={[0, -0.5, -0.02]}>
          <PixelRect x={0} y={0} w={2.02} h={0.24} color="#c44735" z={0} />
          <PixelRect x={-0.86} y={0.04} w={0.18} h={0.12} color="#8f2e28" z={0.01} />
          <PixelRect x={0.86} y={0.04} w={0.18} h={0.12} color="#8f2e28" z={0.01} />
          <PixelRect x={0} y={0.08} w={0.88} h={0.08} color="#df6e50" opacity={0.8} z={0.02} />
          <PixelRect x={0.92} y={0.42} w={1.15} h={0.055} color="#d6e8ef" z={0.02} />
          <PixelRect x={1.42} y={0.45} w={0.22} h={0.1} color="#f0cf5f" z={0.03} />
        </group>
      )}
      <group position={[0, riding ? -0.06 : 0, 0]}>
        <PixelRect x={-0.2} y={-0.11} w={1.05} h={0.42} color={fur} z={0.04} />
        <PixelRect x={-0.42} y={0.03} w={0.72} h={0.34} color={furLight} opacity={0.75} z={0.05} />
        <PixelRect x={0.05} y={-0.02} w={0.72} h={0.34} color={furMid} opacity={0.42} z={0.055} />

        <PixelRect x={-0.82} y={0.06} w={0.28} h={0.18} color={fur} z={0.04} rotation={0.22} />
        <PixelRect x={-0.98} y={0.13} w={0.24} h={0.12} color={furLight} opacity={0.84} z={0.05} rotation={0.22} />
        <PixelRect x={-1.08} y={0.2} w={0.16} h={0.08} color={furCream} opacity={0.72} z={0.06} rotation={0.28} />
        <PixelRect x={-0.74} y={-0.02} w={0.2} h={0.16} color={furDark} opacity={0.42} z={0.06} />

        <PixelRect x={0.42} y={0.16} w={0.44} h={0.42} color={fur} z={0.08} />
        <PixelRect x={0.56} y={0.36} w={0.34} h={0.2} color={furLight} z={0.09} />
        <PixelRect x={0.77} y={0.1} w={0.25} h={0.18} color={furCream} z={0.1} />
        <PixelRect x={0.9} y={0.04} w={0.18} h={0.12} color="#231b15" z={0.12} />
        <PixelRect x={0.49} y={0.18} w={dilatedPupils ? 0.19 : 0.06} h={dilatedPupils ? 0.19 : 0.06} color="#161310" z={dilatedPupils ? 0.2 : 0.13} />
        {dilatedPupils && <PixelRect x={0.49} y={0.18} w={0.12} h={0.12} color="#080706" opacity={0.82} z={0.21} />}
        <PixelRect x={dilatedPupils ? 0.545 : 0.515} y={dilatedPupils ? 0.245 : 0.205} w={dilatedPupils ? 0.034 : 0.025} h={dilatedPupils ? 0.034 : 0.025} color="#f4ead8" opacity={dilatedPupils ? 0.82 : 1} z={dilatedPupils ? 0.22 : 0.14} />
        <PixelRect x={0.76} y={-0.04 - barkOpen * 0.015} w={0.11 + barkOpen * 0.04} h={0.035 + barkOpen * 0.055} color="#4b2a1e" opacity={0.82 + barkOpen * 0.14} z={0.13} rotation={-0.08} />
        {barkOpen > 0.08 && (
          <PixelRect x={0.78} y={-0.025} w={0.045} h={0.02} color="#f1cf62" opacity={0.7 * barkOpen} z={0.14} rotation={-0.08} />
        )}

        <PixelRect x={0.28} y={0.15} w={0.2} h={0.46} color={furDark} opacity={0.82} z={0.07} rotation={-0.08} />
        <PixelRect x={0.22} y={-0.02} w={0.22} h={0.34} color={furMid} opacity={0.78} z={0.08} rotation={-0.04} />
        <PixelRect x={0.64} y={0.14} w={0.18} h={0.42} color={furDark} opacity={0.72} z={0.09} rotation={0.08} />

        <PixelRect x={-0.5} y={-0.45} w={0.15} h={0.42} color={furDark} z={0.03} />
        <PixelRect x={-0.44} y={-0.67} w={0.24} h={0.12} color={furMid} z={0.04} />
        <PixelRect x={-0.1} y={-0.46} w={0.15} h={0.43} color={furDark} z={0.03} />
        <PixelRect x={-0.05} y={-0.68} w={0.24} h={0.12} color={furMid} z={0.04} />
        <PixelRect x={0.32} y={-0.43} w={0.14} h={0.36} color={furDark} z={0.03} />
        <PixelRect x={0.38} y={-0.61} w={0.22} h={0.11} color={furMid} z={0.04} />

        <PixelRect x={0.13} y={0.1} w={0.24} h={0.08} color={collar} z={0.12} />
        <PixelRect x={0.24} y={0.03} w={0.07} h={0.07} color="#f1cf62" z={0.13} />

        {curlPatches.map(([cx, cy, size, color, opacity], i) => (
          <PixelRect key={`body-curl-${i}`} x={cx} y={cy} w={size} h={size} color={color} opacity={opacity} z={0.11 + i * 0.001} rotation={(i % 3 - 1) * 0.16} />
        ))}
        {headCurls.map(([cx, cy, size, color, opacity], i) => (
          <PixelRect key={`head-curl-${i}`} x={cx} y={cy} w={size} h={size} color={color} opacity={opacity} z={0.13 + i * 0.001} rotation={(i % 2 ? 0.14 : -0.12)} />
        ))}
      </group>
    </group>
  );
}

function PixelRoadTripCar({ barkSignal = 0 }: { barkSignal?: number }) {
  return (
    <group>
      <group position={[-0.04, 0.28, 0.02]}>
        <PixelDoodle coat="blonde" scale={0.58} barkSignal={barkSignal} />
      </group>
      <group position={[0, -0.18, 0.15]}>
        <PixelRect x={0} y={0} w={1.86} h={0.42} color="#2f6e9c" z={0.04} />
        <PixelRect x={0.18} y={0.26} w={1.0} h={0.38} color="#285978" z={0.05} />
        <PixelRect x={0.15} y={0.32} w={0.78} h={0.24} color="#b9d9e4" opacity={0.72} z={0.06} />
        <PixelRect x={-0.48} y={0.32} w={0.28} h={0.2} color="#b9d9e4" opacity={0.62} z={0.06} />
        <PixelRect x={0.82} y={0.06} w={0.12} h={0.08} color="#f1d15d" z={0.07} />
        <PixelRect x={-0.82} y={0.05} w={0.12} h={0.08} color="#d24a3d" z={0.07} />
        <PixelRect x={-0.52} y={-0.24} w={0.34} h={0.34} color="#20252b" z={0.08} />
        <PixelRect x={0.58} y={-0.24} w={0.34} h={0.34} color="#20252b" z={0.08} />
        <PixelRect x={-0.52} y={-0.24} w={0.16} h={0.16} color="#7f8a91" z={0.09} />
        <PixelRect x={0.58} y={-0.24} w={0.16} h={0.16} color="#7f8a91" z={0.09} />
        <PixelRect x={-0.02} y={0.06} w={0.5} h={0.08} color="#64a9d8" opacity={0.9} z={0.09} />
      </group>
    </group>
  );
}

function PixelCat({ scale = 1, singSignal = 0 }: { scale?: number; singSignal?: number }) {
  const singOpen = Math.min(1, singSignal);
  return (
    <group scale={[scale, scale, 1]}>
      <PixelRect x={-0.1} y={-0.1} w={0.78} h={0.34} color="#17191b" z={0.04} />
      <PixelRect x={-0.18} y={-0.12} w={0.32} h={0.24} color="#f3f0e4" z={0.05} />
      <PixelRect x={-0.42} y={-0.42} w={0.12} h={0.3} color="#111315" z={0.03} />
      <PixelRect x={-0.18} y={-0.42} w={0.12} h={0.3} color="#111315" z={0.03} />
      <PixelRect x={0.1} y={-0.42} w={0.12} h={0.3} color="#111315" z={0.03} />
      <PixelRect x={0.34} y={-0.42} w={0.12} h={0.3} color="#111315" z={0.03} />
      <PixelRect x={-0.42} y={-0.58} w={0.2} h={0.08} color="#f3f0e4" z={0.04} />
      <PixelRect x={-0.18} y={-0.58} w={0.2} h={0.08} color="#17191b" z={0.04} />
      <PixelRect x={0.1} y={-0.58} w={0.2} h={0.08} color="#f3f0e4" z={0.04} />
      <PixelRect x={0.34} y={-0.58} w={0.2} h={0.08} color="#f3f0e4" z={0.04} />
      <PixelRect x={-0.62} y={0.04} w={0.3} h={0.1} color="#111315" z={0.04} rotation={0.32} />
      <PixelRect x={-0.78} y={0.16} w={0.24} h={0.1} color="#111315" z={0.04} rotation={0.72} />
      <PixelRect x={-0.86} y={0.32} w={0.18} h={0.09} color="#f3f0e4" z={0.05} rotation={0.88} />

      <PixelRect x={0.45} y={0.16} w={0.36} h={0.34} color="#181a1c" z={0.08} />
      <PixelRect x={0.52} y={0.1} w={0.22} h={0.18} color="#f3f0e4" z={0.09} />
      <PixelRect x={0.31} y={0.38} w={0.16} h={0.16} color="#151719" z={0.07} rotation={0.72} />
      <PixelRect x={0.59} y={0.38} w={0.16} h={0.16} color="#151719" z={0.07} rotation={-0.72} />
      <PixelRect x={0.41} y={0.18} w={0.045} h={0.07} color="#92d782" z={0.12} />
      <PixelRect x={0.6} y={0.18} w={0.045} h={0.07} color="#92d782" z={0.12} />
      <PixelRect x={0.5} y={0.05} w={0.07} h={0.04} color="#f2a9b4" z={0.13} />
      <PixelRect x={0.5} y={-0.02 - singOpen * 0.01} w={0.13 + singOpen * 0.045} h={0.035 + singOpen * 0.055} color="#2d2020" opacity={0.75 + singOpen * 0.18} z={0.12} />
      {singOpen > 0.08 && <PixelRect x={0.52} y={0.005} w={0.05} h={0.02} color="#f0b5bd" opacity={0.65 * singOpen} z={0.13} />}
      {[-1, 1].map((side) => (
        <group key={side} position={[0.52, 0.04, 0.13]} rotation={[0, 0, side * 0.12]}>
          <PixelRect x={side * 0.17} y={0.04} w={0.26} h={0.012} color="#f3f0e4" opacity={0.8} z={0.01} rotation={side * 0.08} />
          <PixelRect x={side * 0.18} y={-0.03} w={0.24} h={0.012} color="#f3f0e4" opacity={0.72} z={0.01} rotation={side * -0.08} />
        </group>
      ))}
    </group>
  );
}

function CatSongBurst({
  signal,
  beatIndex,
}: {
  signal: number;
  beatIndex: number;
}) {
  const strength = Math.min(1, signal);
  if (strength <= 0.04) return null;
  const word = ['MEOW', 'MROW', 'PURR'][Math.abs(beatIndex) % 3];
  const scale = 0.54 + strength * 0.2;
  const opacity = Math.min(0.94, 0.18 + strength * 0.88);
  return (
    <group position={[0.42 + strength * 0.08, 0.88 + strength * 0.1, 0.25]} scale={[scale, scale, 1]}>
      <PixelRect x={0} y={0} w={0.82} h={0.3} color="#e9fbff" opacity={opacity} z={0.01} />
      <PixelRect x={-0.28} y={-0.18} w={0.14} h={0.09} color="#e9fbff" opacity={opacity} z={0.01} rotation={0.45} />
      <PixelRect x={0} y={0.17} w={0.88} h={0.05} color="#8ee7f2" opacity={opacity * 0.78} z={0.02} />
      <Text
        position={[-0.03, -0.012, 0.08]}
        fontSize={0.115}
        color="#143640"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.004}
        outlineColor="#f2fdff"
      >
        {word}
      </Text>
      {[0, 1, 2].map((i) => (
        <group key={`cat-note-${i}`} position={[0.52 + i * 0.2, 0.08 + i * 0.13, 0.06]} scale={[0.82 - i * 0.12, 0.82 - i * 0.12, 1]}>
          <PixelRect x={0} y={0} w={0.08} h={0.06} color={i % 2 ? '#ffffff' : '#8ee7f2'} opacity={opacity * (0.62 + strength * 0.25)} z={0.01} rotation={-0.2} />
          <PixelRect x={0.045} y={0.13} w={0.02} h={0.26} color={i % 2 ? '#ffffff' : '#8ee7f2'} opacity={opacity * (0.62 + strength * 0.25)} z={0.01} />
          <PixelRect x={0.12} y={0.24} w={0.16} h={0.035} color={i % 2 ? '#ffffff' : '#8ee7f2'} opacity={opacity * (0.58 + strength * 0.22)} z={0.02} rotation={-0.22} />
        </group>
      ))}
    </group>
  );
}

function DoodleBarkBurst({
  signal,
  beatIndex,
  riding,
  pitch = 'normal',
  position,
}: {
  signal: number;
  beatIndex: number;
  riding?: 'snowboard' | 'kayak';
  pitch?: 'normal' | 'high';
  position?: [number, number, number];
}) {
  const strength = Math.min(1, signal);
  if (strength <= 0.04) return null;
  const high = pitch === 'high';
  const word = (high ? ['YIP!', 'YAP!', 'IP!'] : ['BARK!', 'WOOF!', 'ARF!'])[Math.abs(beatIndex) % 3];
  const lift = riding ? 0.08 : 0;
  const scale = high ? 0.56 + strength * 0.22 : 0.82 + strength * 0.38;
  const opacity = Math.min(0.95, 0.2 + strength * 0.9);
  const bubbleColor = high ? '#f9e7ff' : '#fff4c9';
  const stripeColor = high ? '#e8adff' : '#f7d46b';
  const textColor = high ? '#3b2253' : '#2a2117';
  const textOutline = high ? '#fff2ff' : '#fff7d8';
  const burstPosition = position ?? [1.25 + strength * 0.12, 0.62 + lift + strength * 0.1, 0.24];
  return (
    <group position={burstPosition} scale={[scale, scale, 1]}>
      <PixelRect x={0} y={0} w={high ? 0.74 : 0.92} h={high ? 0.3 : 0.36} color={bubbleColor} opacity={opacity} z={0.01} />
      <PixelRect x={high ? -0.26 : -0.34} y={high ? -0.18 : -0.22} w={high ? 0.14 : 0.18} h={high ? 0.09 : 0.12} color={bubbleColor} opacity={opacity} z={0.01} rotation={0.45} />
      <PixelRect x={0} y={high ? 0.165 : 0.2} w={high ? 0.78 : 0.98} h={0.06} color={stripeColor} opacity={opacity * 0.8} z={0.02} />
      <Text
        position={[0, -0.015, 0.08]}
        fontSize={high ? 0.12 : 0.16}
        color={textColor}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.004}
        outlineColor={textOutline}
      >
        {word}
      </Text>
      {[-1, 0, 1].map((i) => (
        <PixelRect
          key={i}
          x={(high ? -0.56 : -0.74) + i * (high ? 0.09 : 0.12)}
          y={(high ? -0.09 : -0.12) + i * (high ? 0.08 : 0.1)}
          w={(high ? 0.13 : 0.2) + strength * (high ? 0.08 : 0.12)}
          h={0.035}
          color={bubbleColor}
          opacity={opacity * (0.55 + strength * 0.28)}
          z={0.03}
          rotation={-0.18 + i * 0.22}
        />
      ))}
      {high && [-1, 1].map((i) => (
        <PixelRect
          key={`chirp-${i}`}
          x={i * 0.3}
          y={0.34 + strength * 0.05}
          w={0.12}
          h={0.028}
          color="#ffffff"
          opacity={opacity * 0.75}
          z={0.04}
          rotation={i * 0.72}
        />
      ))}
    </group>
  );
}

function DoodleQuestScene(props: SceneProps) {
  const { progressMs, beat, section, intensity, params } = props;
  const music = musicalPulse(props);
  const mic = micReactivityFromParams(params);
  const reactiveMusic = {
    groove: Math.max(music.groove, mic.level * 0.18),
    impact: Math.max(music.impact, mic.hit * 1.12, mic.level * 0.26),
    phrase: Math.max(music.phrase, mic.hit * 0.54),
  };
  const root = useRef<THREE.Group>(null);
  const world = useRef<THREE.Group>(null);
  const dog = useRef<THREE.Group>(null);
  const smoothChapterRef = useRef<number | null>(null);
  const smoothDogRef = useRef(new THREE.Vector3(-4.85, -0.55, -1.02));
  const chapters = useMemo<DoodleQuestKind[]>(() => ['farm', 'chicago', 'road', 'san-francisco', 'snowboard', 'kayak', 'festival', 'san-francisco-return'], []);
  const chapterDurationMs = 60_000;
  const panelWidth = 42;
  const viewportWidth = 10.4;
  const totalDurationMs = chapterDurationMs * chapters.length;
  const visibleCenterOffset = viewportWidth / (panelWidth * 2);
  const maxScrollChapter = chapters.length - viewportWidth / panelWidth;
  const beatMs = 60_000 / Math.max(60, beat.bpm || 128);
  const phraseAgeMs = progressMs % (beatMs * 32);
  const phraseBark = phraseAgeMs < 1500
    ? Math.pow(1 - phraseAgeMs / 1500, 1.25) * intensity
    : 0;
  const barBark = beat.beatIndex % 8 === 0 && beat.beatPhase < 0.32
    ? Math.pow(1 - beat.beatPhase / 0.32, 1.35) * Math.max(0.55, intensity)
    : 0;
  const barkSignal = Math.max(
    reactiveMusic.phrase,
    phraseBark,
    barBark,
    mic.hit * 1.08,
    mic.level * 0.22,
    (section === 'drop' || section === 'chorus') ? reactiveMusic.impact * 0.72 : 0
  );

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    const rawChapter = (progressMs % totalDurationMs) / chapterDurationMs;
    if (smoothChapterRef.current === null || Math.abs(rawChapter - smoothChapterRef.current) > 1.4) {
      smoothChapterRef.current = rawChapter;
    } else {
      const follow = 1 - Math.pow(0.0004, delta);
      smoothChapterRef.current = THREE.MathUtils.lerp(smoothChapterRef.current, rawChapter, follow);
    }
    const smoothChapter = smoothChapterRef.current;
    const visibleChapter = Math.min(chapters.length - 1, Math.floor(smoothChapter + visibleCenterOffset));
    const questProgress = smoothChapter / chapters.length;
    const scrollChapter = Math.min(smoothChapter, maxScrollChapter);
    const pulse = reactiveMusic.groove * 0.22 + reactiveMusic.impact * 0.65 + mic.level * 0.08;
    if (world.current) {
      world.current.position.x = (panelWidth - viewportWidth) / 2 - scrollChapter * panelWidth;
      world.current.position.y = 0;
    }
    if (dog.current) {
      const riding = chapters[visibleChapter] === 'snowboard' || chapters[visibleChapter] === 'kayak';
      const dogTarget = smoothDogRef.current;
      dogTarget.set(-4.85 + questProgress * 9.7, -0.55 + Math.sin(questProgress * Math.PI * 2) * 0.08 + Math.sin(t * 4.4) * (riding ? 0.025 : 0.06), -1.02);
      if (dog.current.position.distanceTo(dogTarget) > 1.4) {
        dog.current.position.copy(dogTarget);
      } else {
        dog.current.position.lerp(dogTarget, 1 - Math.pow(0.001, delta));
      }
      dog.current.rotation.z = Math.sin(t * 2.8) * (riding ? 0.035 : 0.06);
      dog.current.scale.setScalar(0.92 + pulse * 0.025 + mic.hit * 0.018);
    }
    if (root.current) {
      root.current.rotation.z = 0;
    }
  });

  const rawChapter = (progressMs % totalDurationMs) / chapterDurationMs;
  const chapterIndex = Math.min(chapters.length - 1, Math.floor(rawChapter + visibleCenterOffset));
  const currentKind = chapters[chapterIndex];
  const brunetteBarkSignal = currentKind === 'chicago'
    ? Math.max(barkSignal * 0.82, reactiveMusic.impact * 0.52, mic.hit * 0.72)
    : 0;
  const catSongSignal = currentKind === 'san-francisco-return'
    ? Math.max(barkSignal * 0.72, reactiveMusic.phrase * 0.68, reactiveMusic.impact * 0.44, mic.hit * 0.64)
    : 0;

  return (
    <group ref={root}>
      <mesh position={[0, 0, -3.2]}>
        <planeGeometry args={[18, 10]} />
        <meshBasicMaterial color="#101820" transparent opacity={1} depthWrite={false} />
      </mesh>
      <MoodEffects2D {...props} music={music} />
      <group ref={world}>
        {chapters.map((kind, i) => (
          <DoodleQuestPanel key={kind} kind={kind} index={i} x={i * panelWidth} width={panelWidth} questChapter={rawChapter} visibleCenterOffset={visibleCenterOffset} progressMs={progressMs} beat={beat} music={reactiveMusic} albumArtUrl={props.albumArtUrl} lyric={props.lyric} />
        ))}
      </group>
      {currentKind === 'chicago' && (
        <group position={[-2.25, -0.7, -1.01]} scale={[0.74, 0.74, 1]}>
          <PixelDoodle coat="brunette" barkSignal={brunetteBarkSignal} />
          <DoodleBarkBurst signal={brunetteBarkSignal} beatIndex={beat.beatIndex + 1} pitch="high" position={[1.05, 0.72, 0.24]} />
        </group>
      )}
      <group ref={dog}>
        {currentKind === 'road'
          ? <PixelRoadTripCar barkSignal={barkSignal} />
          : <PixelDoodle coat="blonde" riding={currentKind === 'snowboard' ? 'snowboard' : currentKind === 'kayak' ? 'kayak' : undefined} barkSignal={barkSignal} dilatedPupils={currentKind === 'festival'} />}
        {currentKind === 'san-francisco-return' && (
          <group position={[-0.96, -0.1, 0.03]} scale={[0.62, 0.62, 1]}>
            <PixelCat singSignal={catSongSignal} />
            <CatSongBurst signal={catSongSignal} beatIndex={beat.beatIndex + 2} />
          </group>
        )}
        <DoodleBarkBurst signal={barkSignal} beatIndex={beat.beatIndex} riding={currentKind === 'snowboard' ? 'snowboard' : currentKind === 'kayak' ? 'kayak' : undefined} />
      </group>
      <MoodEffects2D {...props} music={music} foreground />
    </group>
  );
}

function DoodleQuestPanel({
  kind,
  index,
  x,
  width,
  questChapter,
  visibleCenterOffset,
  progressMs,
  beat,
  music,
  albumArtUrl,
  lyric,
}: {
  kind: DoodleQuestKind;
  index: number;
  x: number;
  width: number;
  questChapter: number;
  visibleCenterOffset: number;
  progressMs: number;
  beat: any;
  music: { groove: number; impact: number; phrase: number };
  albumArtUrl: string;
  lyric: string;
}) {
  const t = progressMs / 1000;
  const items = useMemo(() => Array.from({ length: 96 }, (_, i) => i), []);
  const [albumTexture, setAlbumTexture] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    if (!albumArtUrl || kind !== 'road') {
      setAlbumTexture(null);
      return undefined;
    }

    let cancelled = false;
    let loadedTexture: THREE.Texture | null = null;
    const loader = new THREE.TextureLoader();
    if (!albumArtUrl.startsWith('data:')) loader.setCrossOrigin('anonymous');
    loader.load(
      albumArtUrl,
      (texture) => {
        if (cancelled) {
          texture.dispose();
          return;
        }
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        loadedTexture = texture;
        setAlbumTexture(texture);
      },
      undefined,
      () => {
        if (!cancelled) setAlbumTexture(null);
      }
    );

    return () => {
      cancelled = true;
      if (loadedTexture) loadedTexture.dispose();
    };
  }, [albumArtUrl, kind]);
  const local = THREE.MathUtils.clamp(questChapter - index + visibleCenterOffset, 0, 1);
  const spreadX = (i: number, count: number, pad = 0.8) => -width / 2 + pad + (i / Math.max(1, count - 1)) * (width - pad * 2);
  const day = Math.sin(local * Math.PI);
  const drift = Math.sin(t * 0.035 + index) * 0.18;
  const everyOtherBeat = beat.beatIndex % 2 === 0 ? Math.pow(Math.max(0, 1 - beat.beatPhase), 1.55) : 0;
  const musicShimmer = Math.max(everyOtherBeat * 0.72, music.impact * 0.42, music.phrase * 0.58);
  const sparkleDrift = Math.sin(t * 0.8 + index) * 0.08;
  const isSanFrancisco = kind === 'san-francisco' || kind === 'san-francisco-return';
  const billboardLyric = (lyric || 'FEEL THE DROP').toUpperCase().slice(0, 34);
  const sky = {
    farm: ['#6eadd5', '#b8d8e8', '#f0dca4'],
    chicago: ['#7d96ad', '#b9c4ca', '#e0d0bb'],
    festival: ['#8fb5d3', '#efd99e', '#d9a05d'],
    road: ['#85b4d8', '#d6d0b5', '#d7ab68'],
    'san-francisco': ['#68879c', '#aebcc4', '#ded6c8'],
    snowboard: ['#7ea4c7', '#d4e6f0', '#f7f8f5'],
    kayak: ['#5f98b5', '#a9d0ce', '#d7d5af'],
    'san-francisco-return': ['#4f6f86', '#a6b8c4', '#dfcaa8'],
  }[kind];
  const mountainLayer = (prefix: string, y: number, color: string, count: number, height = 1.1, z = -0.01) => (
    items.slice(0, count).map((i) => {
      const h = height * (0.78 + seededUnit(i + index * 17, 910) * 0.95);
      const w = 2.0 + seededUnit(i, 911) * 1.65;
      const shoulder = h * (0.42 + seededUnit(i, 913) * 0.22);
      const capOpacity = kind === 'snowboard' ? 0.82 : kind === 'kayak' ? 0.22 : 0.3;
      const shadowColor = kind === 'snowboard' ? '#5f8296' : kind === 'festival' || kind === 'road' ? '#7f5c43' : '#3e5d48';
      return (
        <group key={`${prefix}-${i}`} position={[spreadX(i, count, -1.8), y + h / 2, z]} rotation={[0, 0, seededUnit(i, 912) * 0.035 - 0.017]}>
          <PixelRect x={0} y={-h * 0.08} w={w} h={h} color={color} opacity={0.74} z={0} rotation={Math.PI / 4} />
          <PixelRect x={-w * 0.28} y={-h * 0.22} w={w * 0.52} h={shoulder} color={color} opacity={0.58} z={-0.002} rotation={Math.PI / 4} />
          <PixelRect x={w * 0.28} y={-h * 0.26} w={w * 0.48} h={shoulder * 0.9} color={shadowColor} opacity={0.38} z={0.002} rotation={Math.PI / 4} />
          <PixelRect x={-w * 0.08} y={h * 0.22} w={w * 0.34} h={h * 0.26} color="#f6f4e8" opacity={capOpacity} z={0.01} rotation={Math.PI / 4} />
          <PixelRect x={w * 0.1} y={h * 0.08} w={w * 0.22} h={h * 0.18} color="#eef3ef" opacity={capOpacity * 0.52} z={0.012} rotation={Math.PI / 4} />
          <PixelRect x={w * 0.08} y={-h * 0.02} w={w * 0.09} h={h * 0.82} color={shadowColor} opacity={0.18} z={0.014} rotation={-0.04} />
        </group>
      );
    })
  );
  const leafyTree = (key: string, px: number, py: number, scale: number, trunk = '#6a4528', leaf = '#2f6f3f') => (
    <group key={key} position={[px, py, 0.09]} scale={[scale, scale, 1]}>
      <PixelRect x={0} y={0.28} w={0.12} h={0.58} color={trunk} z={0} />
      <PixelRect x={0} y={0.78} w={0.7} h={0.42} color={leaf} z={0.01} />
      <PixelRect x={-0.2} y={0.98} w={0.46} h={0.32} color="#3f8450" opacity={0.88} z={0.02} />
      <PixelRect x={0.22} y={0.94} w={0.42} h={0.28} color="#245a35" opacity={0.9} z={0.03} />
    </group>
  );
  const pineTree = (key: string, px: number, py: number, scale: number, snowy = false) => (
    <group key={key} position={[px, py, 0.1]} scale={[scale, scale, 1]}>
      <PixelRect x={0} y={0.3} w={0.1} h={0.62} color="#5a412c" z={0} />
      {[0, 1, 2].map((tier) => (
        <group key={tier} position={[0, 0.58 + tier * 0.32, 0.01 + tier * 0.01]}>
          <PixelRect x={0} y={0} w={0.82 - tier * 0.18} h={0.36} color={tier % 2 ? '#2f6048' : '#244f3c'} z={0} rotation={Math.PI / 4} />
          {snowy && <PixelRect x={-0.1} y={0.08} w={0.36 - tier * 0.06} h={0.08} color="#f7fbfb" opacity={0.72} z={0.02} />}
        </group>
      ))}
    </group>
  );
  const palmTree = (key: string, px: number, py: number, scale = 1) => (
    <group key={key} position={[px, py, 0.1]} scale={[scale, scale, 1]}>
      <PixelRect x={0} y={0.34} w={0.12} h={0.78} color="#8d5a35" z={0} rotation={-0.08} />
      {[-2, -1, 0, 1, 2].map((leaf) => (
        <PixelRect
          key={leaf}
          x={leaf * 0.12}
          y={0.86 - Math.abs(leaf) * 0.04}
          w={0.58 - Math.abs(leaf) * 0.05}
          h={0.12}
          color={leaf % 2 ? '#4a8b45' : '#2f6f3f'}
          opacity={0.9}
          z={0.02}
          rotation={leaf * 0.36}
        />
      ))}
      <PixelRect x={0.02} y={0.78} w={0.2} h={0.18} color="#8c6a36" z={0.03} />
    </group>
  );
  const car = (key: string, px: number, py: number, color: string, scale = 1, facing = 1) => (
    <group key={key} position={[px, py, 0.16]} scale={[scale * facing, scale, 1]}>
      <PixelRect x={0} y={0} w={0.82} h={0.2} color={color} z={0} />
      <PixelRect x={0.1} y={0.16} w={0.38} h={0.18} color="#d9e8ee" opacity={0.78} z={0.01} />
      <PixelRect x={-0.28} y={-0.13} w={0.14} h={0.14} color="#1d2024" z={0.02} />
      <PixelRect x={0.3} y={-0.13} w={0.14} h={0.14} color="#1d2024" z={0.02} />
      <PixelRect x={0.46} y={0.02} w={0.07} h={0.05} color="#f6d56a" z={0.03} />
    </group>
  );
  const albumBillboard = (key: string, px: number, py: number, scale = 1) => (
    <group key={key} position={[px, py, 0.16]} scale={[scale, scale, 1]}>
      <PixelRect x={-0.38} y={-0.58} w={0.08} h={1.12} color="#5a4a38" z={0} />
      <PixelRect x={0.38} y={-0.58} w={0.08} h={1.12} color="#5a4a38" z={0} />
      <PixelRect x={0} y={0.08} w={1.72} h={1.18} color="#252c35" z={0.01} />
      <PixelRect x={0} y={0.08} w={1.52} h={0.98} color="#d6c48b" opacity={albumTexture ? 0.22 : 0.85} z={0.02} />
      {albumTexture ? (
        <mesh position={[0, 0.08, 0.04]}>
          <planeGeometry args={[0.96, 0.96]} />
          <meshBasicMaterial map={albumTexture} transparent opacity={0.96} depthWrite={false} toneMapped={false} />
        </mesh>
      ) : (
        <group>
          <PixelRect x={-0.24} y={0.22} w={0.34} h={0.34} color="#7ca3c4" z={0.04} />
          <PixelRect x={0.2} y={0} w={0.42} h={0.42} color="#d86f51" z={0.04} />
          <PixelRect x={-0.02} y={-0.26} w={0.5} h={0.18} color="#f0c765" z={0.04} />
        </group>
      )}
      <PixelRect x={0} y={-0.52} w={1.42} h={0.14} color="#f0cf5f" z={0.05} />
      <Text position={[0, -0.52, 0.08]} fontSize={0.09} color="#2b271c" anchorX="center" anchorY="middle" maxWidth={1.25}>
        NOW PLAYING
      </Text>
    </group>
  );
  const lyricBillboard = (key: string, px: number, py: number, text: string, scale = 1) => (
    <group key={key} position={[px, py, 0.17]} scale={[scale, scale, 1]}>
      <PixelRect x={-0.54} y={-0.5} w={0.08} h={1.0} color="#5a4a38" z={0} />
      <PixelRect x={0.54} y={-0.5} w={0.08} h={1.0} color="#5a4a38" z={0} />
      <PixelRect x={0} y={0.1} w={2.3} h={0.9} color="#263746" z={0.01} />
      <PixelRect x={0} y={0.1} w={2.06} h={0.68} color="#f6e6b5" opacity={0.92} z={0.02} />
      <PixelRect x={0} y={0.46} w={2.1} h={0.1} color="#c8583b" z={0.03} />
      <Text position={[0, 0.06, 0.08]} fontSize={0.13} color="#2b271c" anchorX="center" anchorY="middle" maxWidth={1.82} textAlign="center">
        {text}
      </Text>
    </group>
  );
  const skyBird = (key: string, px: number, py: number, scale = 1, phase = 0) => {
    const wingLift = Math.sin(t * (1.15 + seededUnit(phase, 721) * 0.5) + phase) * 0.16;
    const color = kind === 'snowboard' ? '#455d69' : kind === 'festival' ? '#3a3040' : '#263846';
    return (
      <group key={key} position={[px, py + Math.sin(t * 0.12 + phase) * 0.08, 0.07]} scale={[scale, scale, 1]}>
        <PixelRect x={0} y={0} w={0.16} h={0.055} color={color} opacity={0.62} z={0.01} rotation={0.02} />
        <PixelRect x={-0.13} y={0.03} w={0.24} h={0.035} color={color} opacity={0.56} z={0.02} rotation={-0.26 - wingLift} />
        <PixelRect x={0.13} y={0.03} w={0.24} h={0.035} color={color} opacity={0.56} z={0.02} rotation={0.26 + wingLift} />
        <PixelRect x={0.13} y={-0.005} w={0.055} h={0.04} color={color} opacity={0.58} z={0.03} />
      </group>
    );
  };
  const musicNote = (key: string, px: number, py: number, scale = 1, color = '#fff6c7', phase = 0, double = false) => {
    const notePulse = 1 + musicShimmer * 0.22 + Math.sin(t * 0.75 + phase) * 0.035;
    const opacity = 0.24 + musicShimmer * 0.58 + seededUnit(phase, 731) * 0.12;
    return (
      <group
        key={key}
        position={[
          px + Math.sin(t * 0.18 + phase) * 0.16,
          py + Math.sin(t * 0.28 + phase) * 0.12 + musicShimmer * 0.08,
          0.2,
        ]}
        scale={[scale * notePulse, scale * notePulse, 1]}
        rotation={[0, 0, -0.12 + Math.sin(t * 0.22 + phase) * 0.12]}
      >
        <PixelRect x={0} y={0} w={0.13} h={0.09} color={color} opacity={opacity} z={0.02} rotation={-0.2} />
        <PixelRect x={0.07} y={0.22} w={0.035} h={0.44} color={color} opacity={opacity} z={0.02} />
        <PixelRect x={0.2} y={0.42} w={0.26} h={0.06} color={color} opacity={opacity * 0.92} z={0.03} rotation={-0.26} />
        {double && (
          <>
            <PixelRect x={0.34} y={-0.02} w={0.12} h={0.085} color={color} opacity={opacity * 0.88} z={0.02} rotation={-0.2} />
            <PixelRect x={0.4} y={0.2} w={0.035} h={0.4} color={color} opacity={opacity * 0.88} z={0.02} />
            <PixelRect x={0.24} y={0.36} w={0.34} h={0.055} color={color} opacity={opacity * 0.82} z={0.03} rotation={-0.08} />
          </>
        )}
      </group>
    );
  };

  return (
    <group position={[x, 0, -2.3]}>
      <PixelRect x={0} y={1.8} w={width + 0.2} h={2.4} color={sky[0]} z={-0.06} />
      <PixelRect x={0} y={0.62} w={width + 0.2} h={1.2} color={sky[1]} z={-0.05} />
      <PixelRect x={0} y={-0.08} w={width + 0.2} h={0.65} color={sky[2]} opacity={0.9} z={-0.04} />
      <PixelRect x={-width * 0.28 + drift} y={2.2 - day * 0.28} w={0.7 + day * 0.18 + musicShimmer * 0.08} h={0.7 + day * 0.18 + musicShimmer * 0.08} color={kind === 'snowboard' ? '#fff7cf' : '#f0c36c'} opacity={isSanFrancisco || kind === 'chicago' ? 0.32 + musicShimmer * 0.08 : 0.62 + musicShimmer * 0.08} z={-0.02} />
      {items.slice(0, 16).map((i) => (
        <group key={`cloud-${i}`} position={[spreadX(i, 16, 1.1) + Math.sin(t * 0.018 + i) * 0.2, 1.74 + seededUnit(i + index * 19, 400) * 0.74, 0.02]}>
          <PixelRect x={0} y={0} w={0.68 + seededUnit(i, 404) * 0.36 + musicShimmer * 0.05} h={0.18 + musicShimmer * 0.018} color="#f4f1e8" opacity={0.42 + musicShimmer * 0.16} z={0.01} />
          <PixelRect x={-0.24 - musicShimmer * 0.02} y={0.08} w={0.3 + musicShimmer * 0.05} h={0.16} color="#ffffff" opacity={0.34 + musicShimmer * 0.22} z={0.02} />
          <PixelRect x={0.22 + musicShimmer * 0.02} y={0.09} w={0.34 + musicShimmer * 0.05} h={0.18} color="#ffffff" opacity={0.32 + musicShimmer * 0.22} z={0.02} />
          {i % 4 === 0 && <PixelRect x={0.42 + sparkleDrift} y={0.16} w={0.08 + musicShimmer * 0.08} h={0.025} color="#fffbe9" opacity={musicShimmer * 0.62} z={0.04} rotation={0.22} />}
        </group>
      ))}
      {items.slice(0, 28).map((i) => (
        <PixelRect
          key={`music-sky-spark-${i}`}
          x={spreadX(i, 28, 0.7) + Math.sin(t * 0.12 + i) * 0.15}
          y={1.22 + seededUnit(i, 405) * 1.34 + Math.sin(t * 0.2 + i) * musicShimmer * 0.12}
          w={0.035 + musicShimmer * (i % 3 === 0 ? 0.075 : 0.035)}
          h={0.035 + musicShimmer * (i % 3 === 0 ? 0.075 : 0.035)}
          color={i % 4 === 0 ? '#fff5b8' : '#ffffff'}
          opacity={(0.04 + seededUnit(i, 406) * 0.08) + musicShimmer * (i % 2 ? 0.28 : 0.42)}
          z={0.035}
          rotation={t * 0.18 + i}
        />
      ))}
      {items.slice(0, kind === 'festival' ? 4 : 7).map((i) => {
        const seed = i + index * 37;
        const glide = (seededUnit(seed, 720) + t * (0.012 + seededUnit(seed, 722) * 0.014)) % 1;
        const px = -width / 2 - 1.2 + glide * (width + 2.4);
        const py = 1.35 + seededUnit(seed, 723) * 1.02 + Math.sin(t * 0.09 + seed) * 0.06;
        const scale = 0.42 + seededUnit(seed, 724) * 0.48;
        return skyBird(`sky-bird-${i}`, px, py, scale, seed);
      })}
      {kind === 'farm' && (
        <>
          <PixelRect x={0} y={-1.78} w={width + 0.2} h={2.1} color="#557f36" z={0} />
          <PixelRect x={0} y={-0.55} w={width + 0.2} h={0.72} color="#a9bf64" z={0.01} />
          <PixelRect x={-width * 0.2} y={-0.25} w={width * 0.58} h={0.46} color="#d3b65e" opacity={0.82} z={0.02} />
          <PixelRect x={width * 0.24} y={-0.35} w={width * 0.42} h={0.42} color="#6f9a43" opacity={0.8} z={0.02} />
          {items.slice(0, 58).map((i) => (
            <PixelRect key={`crop-${i}`} x={spreadX(i, 58, 0.4)} y={-1.62 + (i % 8) * 0.17} w={0.42} h={0.045} color={i % 3 === 0 ? '#e1c76a' : i % 3 === 1 ? '#6f9d44' : '#466f2d'} opacity={0.75} z={0.05} rotation={-0.04 + (i % 4) * 0.02} />
          ))}
          {items.slice(0, 13).map((i) => leafyTree(`orchard-${i}`, spreadX(i, 13, 1.5), -0.58 + (i % 2) * 0.08, 0.58 + seededUnit(i, 812) * 0.22))}
          <group position={[-width * 0.31, -0.48, 0.08]}>
            <PixelRect x={0} y={0.06} w={2.25} h={1.12} color="#a83b31" z={0.03} />
            <PixelRect x={0} y={0.78} w={2.58} h={0.36} color="#5f2a27" z={0.04} rotation={0.02} />
            <PixelRect x={-0.6} y={0.1} w={0.36} h={0.48} color="#f3dfbd" z={0.05} />
            <PixelRect x={0.42} y={-0.13} w={0.58} h={0.74} color="#4d2b22" z={0.05} />
            <PixelRect x={0.92} y={0.2} w={0.22} h={0.32} color="#f1d7a5" opacity={0.86} z={0.06} />
            <PixelRect x={1.34} y={0.22} w={0.2} h={1.28} color="#d9d3c8" z={0.02} />
            <PixelRect x={1.34} y={0.92} w={0.34} h={0.18} color="#a9b2b6" z={0.04} />
            <PixelRect x={-1.42} y={0.82} w={0.15} h={1.25} color="#c8c7b5" z={0.02} />
            {[0, 1, 2, 3].map((blade) => <PixelRect key={blade} x={-1.42} y={1.52} w={0.08} h={0.88} color="#eee6c9" z={0.03} rotation={(Math.PI / 4) * blade + t * 0.05} />)}
          </group>
          {items.slice(0, 22).map((i) => (
            <group key={`fence-${i}`} position={[spreadX(i, 22, 0.8), -0.98 + Math.sin(i) * 0.025, 0.12]}>
              <PixelRect x={0} y={0} w={0.08} h={0.54} color="#d8c097" z={0} />
              <PixelRect x={0.42} y={0.12} w={0.82} h={0.08} color="#d8c097" z={0} />
            </group>
          ))}
          {items.slice(0, 9).map((i) => (
            <group key={`hay-${i}`} position={[spreadX(i, 9, 2.4), -1.44 + (i % 3) * 0.12, 0.14]}>
              <PixelRect x={0} y={0} w={0.42} h={0.28} color="#c99a3f" z={0} />
              <PixelRect x={0} y={0.08} w={0.32} h={0.08} color="#e1bd61" z={0.01} />
            </group>
          ))}
        </>
      )}
      {kind === 'chicago' && (
        <>
          <PixelRect x={0} y={-1.88} w={width + 0.2} h={2} color="#1d2630" z={0} />
          <PixelRect x={0} y={-1.08} w={width + 0.2} h={0.54} color="#28465d" z={0.03} />
          <PixelRect x={0} y={-1.33} w={width + 0.2} h={0.12} color="#1c2d38" opacity={0.72} z={0.04} />
          <PixelRect x={0} y={-0.74} w={width + 0.2} h={0.16} color="#6d7c82" z={0.06} />
          {items.slice(0, 58).map((i) => {
            const isWillis = i === 27;
            const isHancock = i === 36;
            const h = (isWillis ? 3.5 : isHancock ? 2.95 : 0.8 + seededUnit(i, 410) * 2.35);
            return (
              <group key={`tower-${i}`} position={[spreadX(i, 58, 0.25), -0.92 + h / 2, 0.04]}>
                <PixelRect x={0} y={0} w={isWillis ? 0.72 : isHancock ? 0.58 : 0.34 + seededUnit(i, 411) * 0.42} h={h} color={i % 3 === 0 ? '#2f3f52' : i % 3 === 1 ? '#394a5f' : '#263443'} z={0} />
                {isWillis && <PixelRect x={-0.2} y={h / 2 + 0.3} w={0.06} h={0.62} color="#1b242f" z={0.02} />}
                {isWillis && <PixelRect x={0.18} y={h / 2 + 0.24} w={0.06} h={0.5} color="#1b242f" z={0.02} />}
                {isHancock && <PixelRect x={0} y={0} w={0.72} h={0.08} color="#b6c4cd" opacity={0.45} z={0.02} rotation={0.42} />}
                {Array.from({ length: Math.max(2, Math.floor(h * 3.1)) }, (_, w) => (
                  <PixelRect key={w} x={(w % 3 - 1) * 0.11} y={-h / 2 + 0.2 + w * 0.28} w={0.055} h={0.065} color={w % 4 ? '#f0cf79' : '#77a9c8'} opacity={0.62} z={0.02} />
                ))}
              </group>
            );
          })}
          <group position={[-width * 0.34, -0.46, 0.13]} scale={[0.88, 0.88, 1]}>
            <PixelRect x={0} y={0.02} w={0.62} h={1.3} color="#562936" z={0} />
            <PixelRect x={0} y={0.7} w={0.74} h={0.18} color="#f1c84f" opacity={0.88 + musicShimmer * 0.08} z={0.02} />
            <PixelRect x={0} y={0.22} w={0.44} h={0.56} color="#bd3d4b" z={0.01} />
            <PixelRect x={0} y={-0.52} w={1.18} h={0.22} color="#e9d27a" opacity={0.9} z={0.03} />
            <Text position={[0, 0.21, 0.08]} fontSize={0.11} color="#ffe78a" anchorX="center" anchorY="middle" maxWidth={0.34}>
              CHI
            </Text>
          </group>
          <group position={[-width * 0.2, -0.88, 0.12]} scale={[0.72, 0.72, 1]}>
            <mesh position={[0, 0.64, 0.02]}>
              <torusGeometry args={[0.72, 0.022, 5, 40]} />
              <meshBasicMaterial color="#c9d7df" transparent opacity={0.58} depthWrite={false} toneMapped={false} />
            </mesh>
            {items.slice(0, 12).map((i) => (
              <PixelRect key={`navy-wheel-spoke-${i}`} x={0} y={0.64} w={0.022} h={1.28} color="#d8e3e9" opacity={0.38} z={0.03} rotation={(i / 12) * Math.PI} />
            ))}
            <PixelRect x={0} y={-0.03} w={1.7} h={0.09} color="#6f7b82" z={0.02} />
            <PixelRect x={-0.44} y={0.18} w={0.07} h={0.38} color="#8b969c" z={0.02} />
            <PixelRect x={0.44} y={0.18} w={0.07} h={0.38} color="#8b969c" z={0.02} />
          </group>
          <group position={[width * 0.31, -0.54, 0.13]} scale={[0.86, 0.86, 1]}>
            <PixelRect x={0} y={0.16} w={1.18} h={0.36} color="#23313e" z={0} />
            <PixelRect x={0} y={0.46} w={1.32} h={0.18} color="#415365" z={0.01} />
            <PixelRect x={-0.36} y={0.1} w={0.14} h={0.22} color="#82b3c7" opacity={0.58} z={0.02} />
            <PixelRect x={0.0} y={0.1} w={0.14} h={0.22} color="#f0cf79" opacity={0.56 + musicShimmer * 0.18} z={0.02} />
            <PixelRect x={0.36} y={0.1} w={0.14} h={0.22} color="#82b3c7" opacity={0.58} z={0.02} />
            <PixelRect x={0} y={0.72} w={1.48} h={0.06} color="#bbc8cf" opacity={0.5} z={0.02} />
          </group>
          {items.slice(0, 18).map((i) => <PixelRect key={`river-glint-${i}`} x={spreadX(i, 18, 1.8)} y={-1.08 + Math.sin(i * 0.8 + t * 0.16) * 0.08} w={0.58} h={0.035} color="#9fc5cf" opacity={0.32} z={0.07} />)}
          <PixelRect x={width * 0.12} y={-0.78} w={5.35} h={0.13} color="#7b878b" z={0.09} />
          <PixelRect x={width * 0.12} y={-0.55} w={5.35} h={0.08} color="#8d989d" z={0.09} />
          {items.slice(0, 12).map((i) => <PixelRect key={`bridge-${i}`} x={width * 0.12 - 2.45 + i * 0.46} y={-0.67} w={0.05} h={0.42} color="#5d6970" z={0.1} />)}
          {items.slice(0, 8).map((i) => car(`traffic-${i}`, spreadX(i, 8, 3.2) + local * 1.5, -0.7 + (i % 2) * 0.08, i % 2 ? '#b04a3f' : '#d7a840', 0.58, i % 2 ? -1 : 1))}
          <PixelRect x={-2.25} y={-1.22} w={1.5} h={0.08} color="#6a5544" z={0.13} />
          <PixelRect x={-2.25} y={-1.05} w={1.1} h={0.16} color="#2b4354" z={0.12} />
        </>
      )}
      {kind === 'festival' && (
        <>
          <PixelRect x={0} y={-1.86} w={width + 0.2} h={2.2} color="#d59758" z={0} />
          <PixelRect x={0} y={-0.68} w={width + 0.2} h={0.82} color="#e0b064" opacity={0.88} z={0.01} />
          <PixelRect x={0} y={-1.38} w={width + 0.2} h={0.18} color="#8f6946" opacity={0.28} z={0.02} />
          {mountainLayer('festival-desert-ridge-back', -0.18, '#b48866', 13, 0.78, 0.012)}
          {mountainLayer('festival-desert-ridge-front', -0.46, '#a97858', 11, 0.7, 0.022)}
          {items.slice(0, 8).map((i) => palmTree(`festival-palm-${i}`, spreadX(i, 8, 1.1), -0.56 + (i % 3) * 0.08, 0.68 + seededUnit(i, 600) * 0.28))}
          <group position={[-width * 0.3, -0.36, 0.11]} scale={[1.04, 1.04, 1]}>
            <PixelRect x={0} y={0.02} w={4.9} h={1.24} color="#2b2930" z={0} />
            <PixelRect x={0} y={0.78} w={5.35} h={0.28} color="#151820" z={0.01} />
            <PixelRect x={-1.58} y={0.08} w={1.06} h={0.72} color="#68c4da" opacity={0.82} z={0.02} />
            <PixelRect x={0} y={0.1} w={1.3} h={0.8} color="#f0c95a" opacity={0.9} z={0.02} />
            <PixelRect x={1.58} y={0.08} w={1.06} h={0.72} color="#d45b78" opacity={0.82} z={0.02} />
            {items.slice(0, 9).map((i) => (
              <PixelRect key={`stage-eq-${i}`} x={-0.48 + i * 0.12} y={-0.18 + seededUnit(i, 610) * 0.32} w={0.08} h={0.16 + seededUnit(i, 611) * 0.42 + musicShimmer * (0.12 + (i % 3) * 0.04)} color={i % 2 ? '#ffffff' : '#52d1e4'} opacity={0.66 + musicShimmer * 0.26} z={0.03} />
            ))}
            {[-1, 1].map((side) => (
              <group key={`stage-tower-${side}`} position={[side * 2.95, 0.08, 0.01]}>
                <PixelRect x={0} y={0} w={0.16} h={1.84} color="#222832" z={0} />
                <PixelRect x={side * 0.18} y={0.74} w={0.1} h={0.78} color="#38424a" z={0.01} rotation={side * 0.16} />
                <PixelRect x={side * 0.22} y={0.16} w={0.9} h={0.055} color="#e8f6ff" opacity={0.34 + day * 0.18} z={0.02} rotation={side * -0.22} />
              </group>
            ))}
          </group>
          <group position={[-width * 0.08, -0.34, 0.12]} scale={[0.82, 0.82, 1]}>
            <PixelRect x={0} y={0.02} w={3.8} h={0.92} color="#312735" opacity={0.96} z={0} />
            <PixelRect x={0} y={0.62} w={4.28} h={0.18} color="#151820" z={0.01} />
            <PixelRect x={-1.0} y={0.02} w={0.72} h={0.54} color="#67d5df" opacity={0.76} z={0.02} />
            <PixelRect x={0} y={0.04} w={0.82} h={0.62} color="#f4d25c" opacity={0.78} z={0.02} />
            <PixelRect x={1.0} y={0.02} w={0.72} h={0.54} color="#df5f82" opacity={0.76} z={0.02} />
            <PixelRect x={-1.86} y={0.0} w={0.12} h={1.34} color="#252d35" z={0.01} />
            <PixelRect x={1.86} y={0.0} w={0.12} h={1.34} color="#252d35" z={0.01} />
            {[-1, 1].map((side) => (
              <PixelRect key={`center-laser-${side}`} x={side * 0.92} y={0.58} w={2.7 + musicShimmer * 0.35} h={0.045 + musicShimmer * 0.025} color="#f8fbff" opacity={0.28 + day * 0.18 + musicShimmer * 0.24} z={0.03} rotation={side * -0.34} />
            ))}
          </group>
          <group position={[width * 0.08, -0.58, 0.13]} scale={[0.86, 0.86, 1]}>
            <PixelRect x={0} y={0.18} w={2.8} h={0.38} color="#f3e2ba" z={0} />
            <PixelRect x={0} y={0.52} w={3.12} h={0.18} color="#c15d4b" z={0.01} />
            <PixelRect x={-1.0} y={0.2} w={0.48} h={0.44} color="#58bcc9" opacity={0.7} z={0.02} />
            <PixelRect x={0.0} y={0.2} w={0.52} h={0.48} color="#f2cc5a" opacity={0.78} z={0.02} />
            <PixelRect x={1.0} y={0.2} w={0.48} h={0.44} color="#d95772" opacity={0.72} z={0.02} />
          </group>
          <group position={[width * 0.2, -0.18, 0.09]} scale={[0.9, 0.9, 1]}>
            {[0, 1, 2].map((ring) => (
              <mesh key={`wheel-ring-${ring}`} position={[0, 0.42, ring * 0.002]}>
                <torusGeometry args={[0.72 - ring * 0.14 + musicShimmer * 0.035, 0.018 + musicShimmer * 0.004, 5, 48]} />
                <meshBasicMaterial color={ring % 2 ? '#f4d36f' : '#f1f4e4'} transparent opacity={0.7 - ring * 0.12 + musicShimmer * 0.16} depthWrite={false} toneMapped={false} />
              </mesh>
            ))}
            {items.slice(0, 12).map((i) => (
              <PixelRect key={`wheel-spoke-${i}`} x={0} y={0.42} w={0.025} h={1.34} color="#ece7d1" opacity={0.48} z={0.03} rotation={(i / 12) * Math.PI} />
            ))}
            <PixelRect x={-0.42} y={-0.44} w={0.08} h={0.9} color="#80664a" z={0.02} rotation={-0.18} />
            <PixelRect x={0.42} y={-0.44} w={0.08} h={0.9} color="#80664a" z={0.02} rotation={0.18} />
          </group>
          {items.slice(0, 22).map((i) => {
            const px = spreadX(i, 22, 1.2) + Math.sin(t * 0.16 + i) * 0.18;
            const py = 0.18 + seededUnit(i, 640) * 1.56 + Math.sin(t * 0.26 + i) * 0.1;
            const noteColor = i % 4 === 0 ? '#ffffff' : i % 4 === 1 ? '#f8d964' : i % 4 === 2 ? '#6fe1ee' : '#f07ba2';
            return musicNote(`festival-note-${i}`, px, py, 0.56 + seededUnit(i, 641) * 0.32, noteColor, i + index * 11, i % 3 === 0);
          })}
          {items.slice(0, 44).map((i) => (
            <group key={`festival-crowd-${i}`} position={[spreadX(i, 44, 0.45), -1.04 + (i % 4) * 0.08, 0.16]} scale={[0.72 + seededUnit(i, 620) * 0.28, 0.72 + seededUnit(i, 620) * 0.28, 1]}>
              <PixelRect x={0} y={0.12} w={0.1} h={0.2} color={i % 5 === 0 ? '#52b7c9' : i % 5 === 1 ? '#d95f67' : i % 5 === 2 ? '#f2cf68' : '#3c3341'} z={0} />
              <PixelRect x={0} y={0.28} w={0.12} h={0.12} color={i % 3 ? '#8a5b3f' : '#d0a36c'} z={0.01} />
              {i % 7 === 0 && <PixelRect x={0.08} y={0.46} w={0.035} h={0.36} color="#f6ead1" z={0.02} rotation={0.24} />}
            </group>
          ))}
          {items.slice(0, 9).map((i) => (
            <group key={`festival-tent-${i}`} position={[spreadX(i, 9, 1.4), -0.72 + (i % 2) * 0.08, 0.13]} scale={[0.72 + seededUnit(i, 630) * 0.25, 0.72 + seededUnit(i, 630) * 0.25, 1]}>
              <PixelRect x={0} y={0.22} w={0.82} h={0.32} color={i % 2 ? '#f1ead6' : '#d5efe8'} z={0} rotation={Math.PI / 4} />
              <PixelRect x={0} y={0.04} w={0.96} h={0.12} color="#8f6e4e" opacity={0.55} z={0.01} />
            </group>
          ))}
        </>
      )}
      {kind === 'road' && (
        <>
          <PixelRect x={0} y={-1.86} w={width + 0.2} h={2.15} color="#b9824d" z={0} />
          {mountainLayer('distant-buttes-back', -0.34, '#b18a63', 16, 0.92, 0.006)}
          {mountainLayer('distant-buttes-front', -0.7, '#9f7651', 14, 0.78, 0.018)}
          {albumBillboard('album-billboard-early', -width * 0.39, -0.16, 0.92)}
          {lyricBillboard('lyric-billboard-early', -width * 0.25, -0.22, billboardLyric, 0.82)}
          {albumBillboard('album-billboard-mid', -width * 0.02, -0.2, 0.78)}
          {lyricBillboard('lyric-billboard-mid', width * 0.14, -0.26, 'NEXT STOP: ' + billboardLyric.slice(0, 18), 0.7)}
          {lyricBillboard('lyric-billboard-late', width * 0.34, -0.22, billboardLyric, 0.78)}
          <PixelRect x={0} y={-1.42} w={width + 0.2} h={0.86} color="#3b3f3f" z={0.05} />
          <PixelRect x={0} y={-1.72} w={width + 0.2} h={0.12} color="#262b2d" z={0.07} />
          {items.slice(0, 38).map((i) => <PixelRect key={`stripe-${i}`} x={spreadX(i, 38, 0.4)} y={-1.38 + Math.sin(i * 0.4) * 0.02} w={0.56} h={0.055} color="#e8dcb1" z={0.08} />)}
          {items.slice(0, 24).map((i) => (
            <group key={`sage-${i}`} position={[spreadX(i, 24, 0.3), -0.62 + seededUnit(i, 424) * 0.18, 0.07]} scale={[0.7 + seededUnit(i, 425) * 0.45, 0.7 + seededUnit(i, 425) * 0.45, 1]}>
              <PixelRect x={0} y={0} w={0.52} h={0.16} color={i % 2 ? '#78865b' : '#b38855'} opacity={0.62} z={0} />
              <PixelRect x={0.16} y={0.16} w={0.22} h={0.22} color={i % 2 ? '#67784e' : '#8f684d'} opacity={0.5} z={0.01} />
            </group>
          ))}
          {items.slice(0, 9).map((i) => car(`road-car-${i}`, spreadX(i, 9, 1.2) + local * 1.6, -1.08 + (i % 2) * 0.17, i % 3 === 0 ? '#91413a' : i % 3 === 1 ? '#425f84' : '#d3b34f', 0.72, i % 2 ? -1 : 1))}
          <group position={[width * 0.19, -0.62, 0.12]}>
            <PixelRect x={0} y={0.2} w={1.2} h={0.65} color="#c96d3b" z={0} />
            <PixelRect x={0} y={0.62} w={1.34} h={0.15} color="#e8c56d" z={0.01} />
            <PixelRect x={0.54} y={0.22} w={0.22} h={0.32} color="#2d3b45" z={0.02} />
            <PixelRect x={-0.42} y={0.24} w={0.3} h={0.18} color="#9ed0dc" opacity={0.7} z={0.02} />
            <PixelRect x={0.9} y={0.1} w={0.09} h={1.2} color="#5a4a38" z={0} />
            <PixelRect x={1.2} y={0.7} w={0.64} h={0.32} color="#32485c" z={0.02} />
            <PixelRect x={1.2} y={0.7} w={0.5} h={0.08} color="#f2d15f" z={0.03} />
          </group>
          {items.slice(0, 12).map((i) => (
            <group key={`power-${i}`} position={[spreadX(i, 12, 0.6), -0.12, 0.05]}>
              <PixelRect x={0} y={0} w={0.06} h={1.15} color="#594535" z={0} />
              <PixelRect x={0} y={0.55} w={0.42} h={0.05} color="#594535" z={0} />
            </group>
          ))}
        </>
      )}
      {isSanFrancisco && (
        <>
          <PixelRect x={0} y={-1.92} w={width + 0.2} h={2.05} color="#4b8392" z={0} />
          <PixelRect x={0} y={-1.32} w={width + 0.2} h={0.32} color="#3c7586" opacity={0.74} z={0.01} />
          <PixelRect x={0} y={-0.58} w={width + 0.2} h={0.5} color="#c5b18d" z={0.02} />
          <PixelRect x={-width * 0.19} y={-0.3} w={10.2} h={0.2} color="#8ba0a4" opacity={0.24} z={0.05} />
          {items.slice(0, 24).map((i) => <PixelRect key={`bay-glint-${i}`} x={spreadX(i, 24, 1.2)} y={-1.48 + (i % 5) * 0.13 + Math.sin(t * 0.12 + i) * 0.035} w={0.58 + seededUnit(i, 470) * 0.42 + musicShimmer * 0.22} h={0.035 + musicShimmer * 0.012} color="#d5ecee" opacity={0.26 + seededUnit(i, 471) * 0.18 + musicShimmer * 0.28} z={0.06} />)}
          <group position={[-width * 0.36, -1.03, 0.12]} scale={[0.82, 0.82, 1]}>
            <PixelRect x={0} y={0} w={8.6} h={0.1} color="#b5483e" z={0} />
            {[-1, 1].map((side) => (
              <group key={`early-gg-${side}`} position={[side * 2.5, 0.1, 0.01]}>
                <PixelRect x={0} y={0} w={0.2} h={1.5} color="#a33d36" z={0} />
                <PixelRect x={side * 0.36} y={0} w={0.12} h={1.34} color="#a33d36" z={0} />
                <PixelRect x={side * 0.18} y={0.52} w={0.78} h={0.08} color="#d96454" z={0.01} />
                <PixelRect x={side * 0.18} y={-0.12} w={0.72} h={0.06} color="#d96454" opacity={0.82} z={0.01} />
              </group>
            ))}
            {items.slice(0, 22).map((i) => <PixelRect key={`early-cable-${i}`} x={-4.05 + i * 0.39} y={0.34 + Math.sin(i * 0.3) * 0.33} w={0.03} h={0.62} color="#cf675b" opacity={0.56} z={0.02} />)}
          </group>
          <PixelRect x={-width * 0.34} y={0.44} w={10.4} h={0.32} color="#e9edf0" opacity={0.18 + Math.sin(t * 0.08) * 0.03} z={0.13} />
          <PixelRect x={-width * 0.12} y={-1.02} w={7.2} h={0.1} color="#b5483e" z={0.09} />
          {[-1, 1].map((side) => (
            <group key={`tower-${side}`} position={[-width * 0.12 + side * 2.34, -0.9, 0.1]}>
              <PixelRect x={0} y={0} w={0.2} h={1.68} color="#a33d36" z={0} />
              <PixelRect x={0.4 * side} y={0} w={0.12} h={1.5} color="#a33d36" z={0} />
              <PixelRect x={0.2 * side} y={0.56} w={0.86} h={0.08} color="#a33d36" z={0.01} />
              <PixelRect x={0.2 * side} y={-0.15} w={0.84} h={0.06} color="#d96454" z={0.02} />
              <PixelRect x={0.1 * side} y={-0.96} w={0.12} h={0.62} color="#7f3432" opacity={0.74} z={0.01} />
            </group>
          ))}
          {items.slice(0, 22).map((i) => <PixelRect key={`cable-${i}`} x={-width * 0.12 - 3.4 + i * 0.33} y={-0.66 + Math.sin(i * 0.32) * 0.32} w={0.035} h={0.72} color="#cf675b" opacity={0.62} z={0.1} />)}
          <group position={[width * 0.18, -0.98, 0.11]} scale={[0.72, 0.72, 1]}>
            <PixelRect x={0} y={0} w={8.8} h={0.08} color="#b5483e" opacity={0.92} z={0} />
            {[-1, 1].map((side) => (
              <group key={`distant-gg-${side}`} position={[side * 2.7, 0.08, 0.02]}>
                <PixelRect x={0} y={0} w={0.16} h={1.26} color="#a33d36" z={0} />
                <PixelRect x={side * 0.3} y={0.02} w={0.09} h={1.08} color="#a33d36" z={0} />
                <PixelRect x={side * 0.15} y={0.44} w={0.62} h={0.06} color="#d96454" z={0.01} />
              </group>
            ))}
            {items.slice(0, 20).map((i) => <PixelRect key={`distant-cable-${i}`} x={-4.1 + i * 0.42} y={0.26 + Math.sin(i * 0.28 + 0.4) * 0.28} w={0.026} h={0.5} color="#cf675b" opacity={0.48} z={0.02} />)}
          </group>
          <PixelRect x={-width * 0.02} y={0.72} w={width + 0.2} h={0.36} color="#e9edf0" opacity={0.16 + Math.sin(t * 0.08) * 0.03} z={0.08} />
          <group position={[-width * 0.22, -0.42, 0.12]} scale={[0.9, 0.9, 1]}>
            {[0, 1, 2, 3, 4, 5].map((tier) => (
              <PixelRect key={`transamerica-tier-${tier}`} x={0} y={0.18 + tier * 0.22} w={0.72 - tier * 0.09} h={0.2} color="#d9ddd7" opacity={0.82} z={0.01} />
            ))}
            <PixelRect x={0} y={1.5} w={0.08} h={0.66} color="#cbd1cc" opacity={0.78} z={0.02} />
            <PixelRect x={0} y={1.88} w={0.04} h={0.42} color="#eef2ef" opacity={0.68} z={0.03} />
          </group>
          <group position={[-width * 0.16, -0.5, 0.11]} scale={[0.82, 0.82, 1]}>
            <PixelRect x={0} y={0.52} w={0.54} h={2.04} color="#879aa1" opacity={0.78} z={0} />
            <PixelRect x={0} y={1.6} w={0.46} h={0.18} color="#d7e3e5" opacity={0.58 + musicShimmer * 0.18} z={0.02} />
            <PixelRect x={0} y={-0.55} w={0.62} h={0.26} color="#6f848d" opacity={0.78} z={0.01} />
            {items.slice(0, 6).map((i) => <PixelRect key={`salesforce-window-${i}`} x={(i % 2 ? 0.11 : -0.11)} y={-0.16 + i * 0.28} w={0.07} h={0.08} color="#d7edf0" opacity={0.34 + musicShimmer * 0.18} z={0.03} />)}
          </group>
          <group position={[width * 0.14, -0.38, 0.14]} scale={[0.72, 0.72, 1]}>
            <PixelRect x={0} y={0.08} w={0.42} h={1.1} color="#d8d0bb" z={0} />
            <PixelRect x={0} y={0.74} w={0.55} h={0.18} color="#b5a88f" z={0.01} />
            <PixelRect x={0} y={0.98} w={0.32} h={0.28} color="#efe8d0" z={0.02} />
            <mesh position={[0, 0.98, 0.05]}>
              <circleGeometry args={[0.1, 18]} />
              <meshBasicMaterial color="#42505a" transparent opacity={0.78} depthWrite={false} toneMapped={false} />
            </mesh>
            <PixelRect x={0} y={1.22} w={0.08} h={0.34} color="#a29582" z={0.03} />
          </group>
          <group position={[width * 0.27, -0.36, 0.13]} scale={[0.76, 0.76, 1]}>
            <PixelRect x={0} y={0.08} w={0.46} h={1.06} color="#d8d0bb" z={0} />
            <PixelRect x={0} y={0.72} w={0.58} h={0.18} color="#b4aa96" z={0.01} />
            <PixelRect x={0} y={0.98} w={0.36} h={0.26} color="#eee8d3" z={0.02} />
            <PixelRect x={0} y={1.2} w={0.14} h={0.22} color="#b4aa96" z={0.03} />
          </group>
          <group position={[width * 0.04, -1.2, 0.16]}>
            <PixelRect x={0} y={0.1} w={1.25} h={0.34} color="#5d6b62" opacity={0.78} z={0} />
            <PixelRect x={-0.28} y={0.34} w={0.28} h={0.42} color="#c9c2ad" opacity={0.72} z={0.01} />
            <PixelRect x={0.16} y={0.36} w={0.32} h={0.36} color="#b7aa96" opacity={0.72} z={0.01} />
            <PixelRect x={0.52} y={0.28} w={0.2} h={0.26} color="#d0c8b8" opacity={0.68} z={0.02} />
          </group>
          <group position={[-width * 0.34, -0.3, 0.15]} scale={[0.66, 0.66, 1]}>
            {[-2, -1, 0, 1, 2].map((house) => (
              <group key={`painted-lady-${house}`} position={[house * 0.42, Math.abs(house) * -0.02, house * 0.001]}>
                <PixelRect x={0} y={0.12} w={0.34} h={0.52} color={house % 2 ? '#d6a78c' : '#c8d1b8'} opacity={0.92} z={0} />
                <PixelRect x={0} y={0.45} w={0.38} h={0.12} color={house % 2 ? '#7d5b57' : '#6a7a73'} z={0.01} />
                <PixelRect x={-0.08} y={0.12} w={0.07} h={0.1} color="#f2d46d" opacity={0.62 + musicShimmer * 0.18} z={0.02} />
                <PixelRect x={0.08} y={0.12} w={0.07} h={0.1} color="#b7d9df" opacity={0.58} z={0.02} />
                <PixelRect x={0} y={-0.18} w={0.1} h={0.2} color="#463832" z={0.02} />
              </group>
            ))}
          </group>
          {items.slice(0, 36).map((i) => {
            const h = 0.28 + seededUnit(i, 430) * 0.9;
            const hill = Math.sin((i / 36) * Math.PI * 2 + 0.4) * 0.35;
            return (
              <group key={`sf-house-${i}`} position={[spreadX(i, 36, 0.55), -0.46 + hill + h / 2, 0.07]}>
                <PixelRect x={0} y={0} w={0.46} h={h} color={i % 4 === 0 ? '#d2a07b' : i % 4 === 1 ? '#8fa5a8' : i % 4 === 2 ? '#c7c0a6' : '#d8b7a0'} opacity={0.82} z={0} />
                <PixelRect x={0} y={h / 2 + 0.08} w={0.5} h={0.12} color={i % 2 ? '#80584a' : '#667a80'} opacity={0.8} z={0.01} />
                <PixelRect x={-0.1} y={0.04} w={0.06} h={0.08} color="#f0d073" opacity={0.6} z={0.02} />
                <PixelRect x={0.1} y={0.04} w={0.06} h={0.08} color="#cfe3ea" opacity={0.55} z={0.02} />
              </group>
            );
          })}
          <group position={[width * 0.22, -0.78, 0.14]}>
            <PixelRect x={0} y={0} w={1.1} h={0.28} color="#b84539" z={0} />
            <PixelRect x={0} y={0.2} w={0.92} h={0.18} color="#e6c95a" z={0.01} />
            {[0, 1, 2].map((i) => <PixelRect key={i} x={-0.32 + i * 0.32} y={0.02} w={0.12} h={0.1} color="#273642" z={0.02} />)}
          </group>
          {kind === 'san-francisco-return' && (
            <>
              <group position={[-width * 0.31, -0.72, 0.18]} scale={[0.92, 0.92, 1]}>
                <PixelRect x={0} y={0.18} w={1.42} h={0.64} color="#d8b48d" z={0} />
                <PixelRect x={0} y={0.58} w={1.62} h={0.16} color="#6c4d45" z={0.01} />
                <PixelRect x={-0.34} y={0.18} w={0.2} h={0.26} color="#f1d26b" opacity={0.75 + musicShimmer * 0.2} z={0.02} />
                <PixelRect x={0.02} y={0.18} w={0.22} h={0.28} color="#9fc6cf" opacity={0.66 + musicShimmer * 0.18} z={0.02} />
                <PixelRect x={0.4} y={0.04} w={0.28} h={0.42} color="#463832" z={0.02} />
              </group>
              <PixelRect x={-width * 0.31} y={-0.16} w={2.22} h={0.08} color="#f6e7ad" opacity={0.28 + musicShimmer * 0.26} z={0.19} />
              {items.slice(0, 12).map((i) => <PixelRect key={`sf-return-window-${i}`} x={spreadX(i, 12, 2.4)} y={0.15 + (i % 3) * 0.28} w={0.09} h={0.1} color={i % 2 ? '#f2d46d' : '#b7d9df'} opacity={0.24 + musicShimmer * 0.34} z={0.16} />)}
            </>
          )}
        </>
      )}
      {kind === 'snowboard' && (
        <>
          <PixelRect x={0} y={-1.7} w={width + 0.2} h={2.55} color="#edf4f7" z={0} />
          {mountainLayer('snow-peaks-far', 0.02, '#b7cbd9', 18, 1.48, 0.004)}
          {mountainLayer('snow-peaks-back', -0.28, '#91adbf', 16, 1.68, 0.014)}
          {mountainLayer('snow-peaks-front', -0.72, '#6f91a5', 13, 1.54, 0.034)}
          <PixelRect x={0} y={-1.38} w={width + 0.2} h={0.28} color="#d4e4ec" opacity={0.72} z={0.07} rotation={-0.02} />
          {[0, 1, 2, 3].map((i) => <PixelRect key={`slope-${i}`} x={0} y={-1.34 + i * 0.32} w={width + 0.2} h={0.08} color={i % 2 ? '#c8dbe6' : '#ffffff'} opacity={0.42} z={0.09} rotation={-0.035} />)}
          {items.slice(0, 44).map((i) => pineTree(`pine-${i}`, spreadX(i, 44, 0.35), -1.08 + seededUnit(i, 440) * 0.55, 0.56 + seededUnit(i, 441) * 0.55, true))}
          <group position={[width * 0.22, -0.48, 0.13]}>
            <PixelRect x={0} y={0.15} w={1.45} h={0.58} color="#7b4a35" z={0} />
            <PixelRect x={0} y={0.55} w={1.65} h={0.22} color="#4e352b" z={0.01} rotation={0.03} />
            <PixelRect x={-0.36} y={0.17} w={0.28} h={0.24} color="#f6d36b" z={0.02} />
            <PixelRect x={0.36} y={0.17} w={0.28} h={0.24} color="#9fc7d2" opacity={0.75} z={0.02} />
          </group>
          {items.slice(0, 10).map((i) => (
            <group key={`lift-${i}`} position={[spreadX(i, 10, 1.8), 0.74 + Math.sin(i) * 0.12, 0.12]}>
              <PixelRect x={0} y={0} w={0.56} h={0.04} color="#3a4650" z={0} />
              <PixelRect x={0} y={-0.12} w={0.28} h={0.18} color="#2d3640" z={0.01} />
            </group>
          ))}
          <PixelRect x={0} y={1.0} w={width + 0.2} h={0.035} color="#3a4650" opacity={0.55} z={0.1} rotation={-0.035} />
          {items.slice(0, 54).map((i) => <PixelRect key={`snow-${i}`} x={spreadX(i, 54, 0.3)} y={-0.05 + seededUnit(i, 443) * 2.1 + Math.sin(t * 0.9 + i) * musicShimmer * 0.08} w={0.05 + musicShimmer * 0.025} h={0.05 + musicShimmer * 0.025} color="#ffffff" opacity={0.72 + musicShimmer * 0.22} z={0.14} />)}
        </>
      )}
      {kind === 'kayak' && (
        <>
          <PixelRect x={0} y={-1.86} w={width + 0.2} h={2.45} color="#397f5b" z={0} />
          {mountainLayer('river-bluffs-back', -0.18, '#6f8a67', 16, 0.98, 0.006)}
          {mountainLayer('river-bluffs-front', -0.5, '#4f714f', 14, 0.88, 0.018)}
          <PixelRect x={0} y={-1.16} w={width + 0.2} h={1.0} color="#65aeba" z={0.05} />
          <PixelRect x={0} y={-1.48} w={width + 0.2} h={0.38} color="#3c879a" opacity={0.55} z={0.06} />
          {items.slice(0, 32).map((i) => <PixelRect key={`rapid-${i}`} x={spreadX(i, 32, 0.45) + Math.sin(t * 0.24 + i) * (0.18 + musicShimmer * 0.08)} y={-1.12 + (i % 5) * 0.13 + Math.sin(i * 0.7 + t * 0.3) * (0.03 + musicShimmer * 0.04)} w={0.72 + seededUnit(i, 460) * 0.44 + musicShimmer * 0.24} h={0.055 + musicShimmer * 0.018} color="#e9f8f8" opacity={0.5 + seededUnit(i, 461) * 0.24 + musicShimmer * 0.18} z={0.12} rotation={Math.sin(i) * 0.08} />)}
          {items.slice(0, 34).map((i) => {
            const leftBank = i % 2 === 0;
            return leftBank
              ? pineTree(`river-pine-${i}`, spreadX(i, 34, 0.35), -0.34 + seededUnit(i, 450) * 0.5, 0.5 + seededUnit(i, 451) * 0.5)
              : leafyTree(`river-tree-${i}`, spreadX(i, 34, 0.35), -0.22 + seededUnit(i, 452) * 0.36, 0.45 + seededUnit(i, 453) * 0.4, '#5f3c25', i % 3 ? '#2c6940' : '#3a7c48');
          })}
          {items.slice(0, 18).map((i) => <PixelRect key={`rock-${i}`} x={spreadX(i, 18, 0.8)} y={-1.65 + seededUnit(i, 451) * 0.48} w={0.46 + seededUnit(i, 452) * 0.24} h={0.16 + seededUnit(i, 453) * 0.1} color={i % 2 ? '#59656b' : '#767d7d'} opacity={0.78} z={0.15} rotation={seededUnit(i, 454) * 0.2 - 0.1} />)}
          <group position={[width * 0.18, -0.3, 0.12]}>
            <PixelRect x={0} y={0.06} w={1.25} h={0.7} color="#6b6f5a" opacity={0.62} z={0} />
            <PixelRect x={0.12} y={0.44} w={0.9} h={0.18} color="#c7d6ce" opacity={0.36} z={0.01} />
          </group>
        </>
      )}
    </group>
  );
}

type TwoDVariant =
  | 'spectrum-bars'
  | 'radial-eq'
  | 'oscilloscope'
  | 'waveform-ribbon'
  | 'beat-kaleidoscope'
  | 'lyric-poster'
  | 'album-slices'
  | 'bass-orbit'
  | 'grid-pulse'
  | 'color-fields'
  | 'dot-matrix'
  | 'lissajous-lasers'
  | 'vinyl-rings'
  | 'skyline-eq';

function makeTwoDScene(variant: TwoDVariant) {
  return function TwoDSceneVariant(props: SceneProps) {
    return <TwoDVisualizer variant={variant} {...props} />;
  };
}

function TwoDVisualizer({ variant, ...props }: SceneProps & { variant: TwoDVariant }) {
  const { palette, intensity, beat, progressMs, lyric, section, params } = props;
  const tune = sceneTuning(props);
  const journey = sceneJourney(props);
  const root = useRef<THREE.Group>(null);
  const fitScale = useViewportContainTransform(18.8, 10.6, 0.92, -1.8);
  const t = progressMs / 1000;
  const music = musicalPulse(props);
  const mic = micReactivityFromParams(params);
  const beatAttack = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.7) * intensity;
  const alternatingBeat = beat.beatIndex % 2 === 0 ? beatAttack * 0.52 : beatAttack * 0.18;
  const impactAccent = Math.max(music.impact * 1.15, music.phrase * 1.35, alternatingBeat * 0.58, mic.hit * 1.22, mic.level * 0.36) * tune.flash;
  const pulse = (music.groove * 0.6 + music.impact * 1.12 + music.phrase * 0.78 + alternatingBeat * 0.28 + mic.pulse * 0.9 + mic.level * 0.22) * tune.flash;
  const energy = Math.min(1.9, intensity * tune.energy + mic.level * 0.58);
  const samples = useMemo(() => Array.from({ length: 96 }, (_, i) => ({
    i,
    x: seededUnit(i, 82) * 2 - 1,
    y: seededUnit(i, 83) * 2 - 1,
    a: seededUnit(i, 84) * Math.PI * 2,
    s: 0.5 + seededUnit(i, 85) * 0.9,
  })), []);
  const stageGain = (section === 'drop' ? 1.4 : section === 'build' ? 1.18 : section === 'chorus' ? 1.1 : 0.9) + impactAccent * 0.18 + mic.level * 0.12;
  const signal = Math.min(1.9, 0.38 + energy * 0.42 + pulse * 0.62 + impactAccent * 0.48 + mic.lift * 0.36);
  const reactiveOpacity = Math.min(0.54, 0.055 + pulse * 0.065 + impactAccent * 0.12 + mic.hit * 0.08);

  useFrame((state) => {
    if (!root.current) return;
    const clock = state.clock.elapsedTime;
    root.current.position.x = Math.sin(clock * 0.16 + journey.turn) * 0.05;
    root.current.position.y = Math.cos(clock * 0.13 + journey.turn) * 0.04;
    root.current.rotation.z = Math.sin(clock * 0.1 + journey.turn) * 0.012;
    const musicScale = THREE.MathUtils.clamp(1 + pulse * 0.018 + impactAccent * 0.006 + mic.level * 0.012, 1, 1.035);
    root.current.scale.set(fitScale.x * musicScale, fitScale.y * musicScale, 1);
  });

  const beatValue = (i: number, speed = 1) => {
    const tone = Math.sin(i * 0.63 + t * (0.9 + tune.speed * speed) + beat.beatIndex * 0.09);
    const sub = Math.sin(i * 0.17 - t * 0.42 + journey.turn * 2);
    const accentLane = i % 8 === beat.beatIndex % 8 ? impactAccent * 0.48 : i % 4 === beat.barIndex % 4 ? pulse * 0.16 : 0;
    const micLane = mic.active ? mic.hit * (0.2 + seededUnit(i, 986) * 0.32) + mic.level * (0.08 + seededUnit(i, 987) * 0.16) : 0;
    return Math.max(0.08, (tone * 0.5 + 0.5) * 0.7 + (sub * 0.5 + 0.5) * 0.3) * (0.78 + energy * 0.68 + pulse * 0.34 + accentLane + micLane);
  };

  const linePoint = (i: number, count: number, amp = 1): [number, number, number] => {
    const x = -5.6 + (i / Math.max(1, count - 1)) * 11.2;
    const y = Math.sin(i * 0.34 + t * (1.2 + tune.speed) + beat.beatIndex * 0.11) * amp
      + Math.sin(i * 0.09 - t * 0.7) * amp * 0.35
      + Math.sin(i * 0.72 + beat.beatIndex * 0.35) * impactAccent * 0.16
      + Math.sin(i * 1.18 + t * 3.2) * mic.hit * 0.24
      + Math.sin(i * 0.23 - t * 1.1) * mic.level * 0.12;
    return [x, y, -2];
  };

  const baseBackdrop = (
    <>
      <mesh position={[0, 0, -3.2]}>
        <planeGeometry args={[18, 10]} />
        <meshBasicMaterial color={rgb(palette.background)} transparent opacity={0.48} depthWrite={false} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={`field-${i}`} position={[Math.sin(t * 0.1 + i) * 2.8, Math.cos(t * 0.09 + i) * 1.7, -3 + i * 0.02]} rotation={[0, 0, t * 0.03 + i]}>
          <circleGeometry args={[3.2 + i * 1.6 + pulse * 0.48 + impactAccent * 0.22 + mic.lift * 0.24, 96]} />
          <meshBasicMaterial
            color={rgb(i === 0 ? palette.primary : i === 1 ? palette.secondary : palette.accent)}
            transparent
            opacity={0.04 + energy * 0.018 + impactAccent * 0.026 + mic.hit * 0.018}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
    </>
  );
  const musicOverlay = (
    <group>
      {[0, 1, 2].map((ring) => (
        <mesh key={`reactive-ring-${ring}`} position={[0, 0, -1.92]} rotation={[0, 0, t * (0.035 + ring * 0.025) + ring * 0.7]}>
          <torusGeometry args={[1.35 + ring * 1.05 + impactAccent * (0.16 + ring * 0.08) + mic.lift * (0.08 + ring * 0.035), 0.006 + impactAccent * 0.009 + mic.hit * 0.004, 5, 180]} />
          <meshBasicMaterial
            color={rgb(ring % 2 ? palette.secondary : palette.glow)}
            transparent
            opacity={reactiveOpacity * (0.78 - ring * 0.16) + mic.hit * 0.035}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
      {samples.slice(0, 28).map(({ i }) => {
        const side = i % 2 ? 1 : -1;
        const lane = Math.floor(i / 2);
        const meter = beatValue(i + 11, 0.9) * signal + mic.level * (0.08 + (lane % 4) * 0.025);
        return (
          <mesh key={`edge-meter-${i}`} position={[side * (5.75 - (lane % 3) * 0.08), -2.62 + (lane % 14) * 0.39, -1.54]}>
            <planeGeometry args={[0.035 + impactAccent * 0.02 + mic.hit * 0.01, 0.08 + meter * 0.28]} />
            <meshBasicMaterial
              color={rgb(i % 3 === 0 ? palette.accent : i % 3 === 1 ? palette.primary : palette.secondary)}
              transparent
              opacity={0.12 + pulse * 0.08 + impactAccent * 0.16 + mic.hit * 0.08}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        );
      })}
    </group>
  );

  let content: JSX.Element;
  switch (variant) {
    case 'spectrum-bars':
      content = (
        <group>
          {samples.slice(0, 64).map(({ i }) => {
            const accent = i % 8 === beat.beatIndex % 8 ? impactAccent * 0.85 : 0;
            const h = beatValue(i, 1.4) * (1.3 + (i % 7) * 0.1 + accent) * stageGain;
            const x = -5.7 + i * 0.18;
            return (
              <mesh key={i} position={[x, -2.2 + h / 2, -1.6]}>
                <planeGeometry args={[0.11 + accent * 0.025, h]} />
                <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.glow)} transparent opacity={0.55 + energy * 0.22 + accent * 0.14} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
          {samples.slice(0, 64).map(({ i }) => {
            const accent = i % 8 === (beat.beatIndex + 4) % 8 ? impactAccent * 0.55 : 0;
            const h = beatValue(i + 9, 1.1) * (0.8 + accent) * stageGain;
            const x = -5.7 + i * 0.18;
            return (
              <mesh key={`top-${i}`} position={[x, 2.25 - h / 2, -1.7]}>
                <planeGeometry args={[0.08, h]} />
                <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.28 + energy * 0.12 + accent * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'radial-eq':
      content = (
        <group rotation={[0, 0, t * 0.08]}>
          {samples.slice(0, 72).map(({ i }) => {
            const a = (i / 72) * Math.PI * 2;
            const spokeAccent = i % 8 === beat.beatIndex % 8 ? impactAccent * 0.62 : 0;
            const h = 0.35 + beatValue(i, 1.2) * (1.6 + spokeAccent) * stageGain;
            const r = 1.25 + h * 0.45;
            return (
              <mesh key={i} position={[Math.cos(a) * r, Math.sin(a) * r, -1.7]} rotation={[0, 0, a + Math.PI / 2]}>
                <planeGeometry args={[0.055 + spokeAccent * 0.018, h]} />
                <meshBasicMaterial color={rgb(i % 2 ? palette.primary : palette.secondary)} transparent opacity={0.48 + pulse * 0.08 + spokeAccent * 0.16} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
          {[0, 1, 2, 3].map((i) => (
            <mesh key={i} position={[0, 0, -1.75]}>
              <torusGeometry args={[0.85 + i * 0.55 + pulse * 0.11 + impactAccent * 0.05, 0.01 + impactAccent * 0.004, 5, 144]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.glow : palette.accent)} transparent opacity={0.24 - i * 0.035 + energy * 0.04 + impactAccent * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      );
      break;
    case 'oscilloscope':
      content = (
        <group>
          {samples.slice(0, 78).map(({ i }) => (
            <ConnectionLine
              key={i}
              from={linePoint(i, 79, 1.15 + pulse * 0.34 + impactAccent * 0.18)}
              to={linePoint(i + 1, 79, 1.15 + pulse * 0.34 + impactAccent * 0.18)}
              color={rgb(i % 2 ? palette.glow : palette.primary)}
              opacity={0.3 + energy * 0.25 + impactAccent * 0.12}
            />
          ))}
          {[-1.8, -0.9, 0, 0.9, 1.8].map((y, i) => (
            <ConnectionLine key={`scan-${i}`} from={[-5.8, y, -2.1]} to={[5.8, y, -2.1]} color={rgb(palette.secondary)} opacity={0.05 + pulse * 0.04 + impactAccent * 0.035} />
          ))}
        </group>
      );
      break;
    case 'waveform-ribbon':
      content = (
        <group>
          {samples.slice(0, 56).map(({ i }) => {
            const x = -5.5 + i * 0.2;
            const y = Math.sin(i * 0.28 + t * 0.9) * (1.05 + impactAccent * 0.08) + Math.sin(i * 0.07 - t * 0.4) * 0.55;
            const h = 0.12 + beatValue(i, 1.1) * (0.52 + impactAccent * 0.12);
            return (
              <mesh key={i} position={[x, y, -1.65]} rotation={[0, 0, Math.sin(i * 0.2 + t) * 0.55]}>
                <planeGeometry args={[0.42 + impactAccent * 0.06, h]} />
                <meshBasicMaterial color={rgb(i % 4 < 2 ? palette.primary : palette.accent)} transparent opacity={0.3 + energy * 0.18 + impactAccent * 0.09} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'beat-kaleidoscope':
      content = (
        <group rotation={[0, 0, t * 0.12 + journey.turn * 0.2]}>
          {samples.slice(0, 48).map(({ i }) => {
            const a = (i / 12) * Math.PI * 2;
            const ring = Math.floor(i / 12);
            const r = 0.75 + ring * 0.78 + beatValue(i, 0.8) * (0.22 + impactAccent * 0.09);
            return (
              <mesh key={i} position={[Math.cos(a) * r, Math.sin(a) * r, -1.7]} rotation={[0, 0, a + t * (0.08 + ring * 0.02)]}>
                <circleGeometry args={[0.45 + pulse * 0.08 + impactAccent * 0.035, 3]} />
                <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.accent)} transparent opacity={0.18 + energy * 0.18 + impactAccent * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} side={THREE.DoubleSide} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'lyric-poster':
      content = (
        <group>
          {samples.slice(0, 18).map(({ i }) => (
            <mesh key={i} position={[0, -2.5 + i * 0.3, -1.95]} rotation={[0, 0, (i % 2 ? -1 : 1) * 0.02]}>
              <planeGeometry args={[9.5 - (i % 5) * 0.75 + Math.sin(t + i) * 0.4 + impactAccent * 0.44, 0.05 + beatValue(i) * (0.08 + impactAccent * 0.018)]} />
              <meshBasicMaterial color={rgb(i % 2 ? palette.primary : palette.secondary)} transparent opacity={0.08 + energy * 0.07 + impactAccent * 0.05} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          <Text position={[0, 0.15 + impactAccent * 0.05, -1.55]} fontSize={0.52 + pulse * 0.1 + impactAccent * 0.03} maxWidth={7.6} textAlign="center" anchorX="center" anchorY="middle" color={rgb(palette.accent)} outlineColor={rgb(palette.glow)} outlineWidth={0.012 + impactAccent * 0.004}>
            {lyric || (section === 'drop' ? 'DROP' : 'HOW YOU MET ME')}
          </Text>
        </group>
      );
      break;
    case 'album-slices':
      content = (
        <group rotation={[0, 0, Math.sin(t * 0.12) * 0.08]}>
          {samples.slice(0, 34).map(({ i }) => {
            const x = -5.6 + i * 0.34;
            const drift = Math.sin(t * (0.2 + (i % 5) * 0.03) + i) * 0.35;
            return (
              <mesh key={i} position={[x, drift, -1.75]} rotation={[0, 0, -0.55 + Math.sin(i) * 0.08]}>
                <planeGeometry args={[0.22 + beatValue(i) * (0.08 + impactAccent * 0.018), 6.8 + beatValue(i + 3) * (0.9 + impactAccent * 0.22)]} />
                <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.accent)} transparent opacity={0.16 + energy * 0.12 + impactAccent * 0.07} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'bass-orbit':
      content = (
        <group>
          {[0, 1, 2, 3, 4].map((ring) => (
            <mesh key={`ring-${ring}`} position={[0, 0, -1.85]} rotation={[0, 0, t * (0.08 + ring * 0.025)]}>
              <torusGeometry args={[0.75 + ring * 0.55 + pulse * 0.08 + impactAccent * 0.05, 0.012 + impactAccent * 0.006, 6, 144]} />
              <meshBasicMaterial color={rgb(ring % 2 ? palette.secondary : palette.primary)} transparent opacity={0.16 + energy * 0.04 + impactAccent * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          {samples.slice(0, 28).map(({ i }) => {
            const a = i * 1.618 + t * (0.35 + (i % 4) * 0.07);
            const r = 0.9 + (i % 5) * 0.48 + beatValue(i) * (0.16 + impactAccent * 0.05);
            return (
              <mesh key={i} position={[Math.cos(a) * r, Math.sin(a) * r, -1.55]}>
                <circleGeometry args={[0.055 + beatValue(i) * (0.035 + impactAccent * 0.012), 24]} />
                <meshBasicMaterial color={rgb(i % 2 ? palette.glow : palette.accent)} transparent opacity={0.5 + energy * 0.22 + impactAccent * 0.11} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'grid-pulse':
      content = (
        <group>
          {samples.slice(0, 81).map(({ i }) => {
            const col = i % 9;
            const row = Math.floor(i / 9);
            const v = beatValue(i + row * 3, 0.7);
            return (
              <mesh key={i} position={[-4 + col, -2.7 + row * 0.68, -1.7]} rotation={[0, 0, Math.sin(t * 0.3 + i) * 0.05]}>
                <planeGeometry args={[0.5 + v * (0.16 + impactAccent * 0.04), 0.32 + v * (0.22 + impactAccent * 0.06)]} />
                <meshBasicMaterial color={rgb((col + row) % 3 === 0 ? palette.primary : (col + row) % 3 === 1 ? palette.secondary : palette.accent)} transparent opacity={0.12 + v * 0.22 + impactAccent * 0.06} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'color-fields':
      content = (
        <group>
          {samples.slice(0, 16).map(({ i }) => (
            <mesh key={i} position={[Math.sin(i * 1.7 + t * 0.25) * 3.2, Math.cos(i * 1.1 - t * 0.2) * 1.9, -1.8]} rotation={[0, 0, t * 0.07 + i * 0.4]}>
              <planeGeometry args={[2.4 + beatValue(i) * (1.2 + impactAccent * 0.34), 1.2 + beatValue(i + 8) * (0.8 + impactAccent * 0.28)]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.glow)} transparent opacity={0.07 + energy * 0.08 + pulse * 0.03 + impactAccent * 0.055} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      );
      break;
    case 'dot-matrix':
      content = (
        <group>
          {samples.map(({ i }) => {
            const col = i % 16;
            const row = Math.floor(i / 16);
            const v = beatValue(i, 1.3);
            return (
              <mesh key={i} position={[-5.25 + col * 0.7, -2.1 + row * 0.7, -1.65]}>
                <circleGeometry args={[0.035 + v * (0.055 + impactAccent * 0.016), 20]} />
                <meshBasicMaterial color={rgb(i % 4 < 2 ? palette.glow : palette.accent)} transparent opacity={0.2 + v * 0.34 + impactAccent * 0.08} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
        </group>
      );
      break;
    case 'lissajous-lasers':
      content = (
        <group>
          {samples.slice(0, 70).map(({ i }) => {
            const count = 71;
            const p1 = i / count * Math.PI * 2;
            const p2 = (i + 1) / count * Math.PI * 2;
            const point = (p: number): [number, number, number] => [
              Math.sin(p * (2 + journey.chapter) + t * 0.75) * (4.6 + impactAccent * 0.42),
              Math.sin(p * 3 + t * 0.52 + journey.turn) * (2.35 + impactAccent * 0.25),
              -1.7,
            ];
            return <ConnectionLine key={i} from={point(p1)} to={point(p2)} color={rgb(i % 2 ? palette.primary : palette.accent)} opacity={0.18 + energy * 0.18 + pulse * 0.08 + impactAccent * 0.12} />;
          })}
        </group>
      );
      break;
    case 'vinyl-rings':
      content = (
        <group rotation={[0, 0, t * 0.18]}>
          {samples.slice(0, 18).map(({ i }) => (
            <mesh key={i} position={[0, 0, -1.7]}>
              <torusGeometry args={[0.35 + i * 0.22 + Math.sin(t + i) * 0.018 + impactAccent * 0.025, 0.007 + (i % 4 === beat.barIndex % 4 ? pulse * 0.02 : 0) + impactAccent * 0.004, 5, 160]} />
              <meshBasicMaterial color={rgb(i % 3 === 0 ? palette.primary : i % 3 === 1 ? palette.secondary : palette.glow)} transparent opacity={0.08 + energy * 0.06 + (i % 4 === beat.barIndex % 4 ? pulse * 0.16 : 0) + impactAccent * 0.07} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
          <mesh position={[0, 0, -1.55]}>
            <circleGeometry args={[0.32 + pulse * 0.1 + impactAccent * 0.045, 48]} />
            <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.45 + energy * 0.2 + impactAccent * 0.12} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </mesh>
        </group>
      );
      break;
    case 'skyline-eq':
      content = (
        <group>
          {samples.slice(0, 44).map(({ i }) => {
            const h = 0.5 + beatValue(i, 1.1) * (1.8 + (i % 5) * 0.25 + impactAccent * 0.45) * stageGain;
            const x = -5.5 + i * 0.255;
            return (
              <mesh key={i} position={[x, -2.45 + h / 2, -1.75]}>
                <planeGeometry args={[0.19 + impactAccent * 0.018, h]} />
                <meshBasicMaterial color={rgb(i % 4 < 2 ? palette.primary : palette.secondary)} transparent opacity={0.28 + energy * 0.24 + impactAccent * 0.1} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
              </mesh>
            );
          })}
          {samples.slice(0, 12).map(({ i }) => (
            <mesh key={`window-${i}`} position={[-4.6 + i * 0.84, -1.2 + Math.sin(t + i) * 0.18, -1.55]}>
              <planeGeometry args={[0.22 + impactAccent * 0.08, 0.04 + impactAccent * 0.018]} />
              <meshBasicMaterial color={rgb(palette.accent)} transparent opacity={0.35 + pulse * 0.16 + impactAccent * 0.1} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      );
      break;
    default:
      content = <group />;
  }

  return (
    <group ref={root} scale={[fitScale.x, fitScale.y, 1]}>
      {baseBackdrop}
      <MoodEffects2D visualStyle={props.visualStyle} palette={palette} intensity={intensity} beat={beat} progressMs={progressMs} params={props.params} music={music} />
      {musicOverlay}
      {content}
      <MoodEffects2D visualStyle={props.visualStyle} palette={palette} intensity={intensity} beat={beat} progressMs={progressMs} params={props.params} music={music} foreground />
      {variant !== 'lyric-poster' && lyric && (section === 'chorus' || section === 'drop') && (
        <Text position={[0, 2.85, -1.45]} fontSize={0.22 + intensity * 0.05} maxWidth={8} textAlign="center" anchorX="center" anchorY="middle" color={rgb(palette.glow)} outlineColor={rgb(palette.background)} outlineWidth={0.008}>
          {lyric}
        </Text>
      )}
    </group>
  );
}

function paletteHue([r, g, b]: [number, number, number]) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return (h * 60 + 360) % 360;
}

function makeCanvasVisualizerScene(kind: CanvasVisualizerKind) {
  return function CanvasVisualizerScene(props: SceneProps) {
    const { palette, beat, intensity, progressMs, params } = props;
    const plane = useViewportPlaneBounds(0.985, -1.9);
    const renderSize = useMemo(() => {
      const aspect = Math.max(0.25, Math.min(4, plane.pixelWidth / Math.max(1, plane.pixelHeight)));
      if (aspect >= 1) {
        return { width: 1120, height: Math.round(1120 / aspect) };
      }
      return { width: Math.round(1120 * aspect), height: 1120 };
    }, [plane.pixelHeight, plane.pixelWidth]);
    const canvas = useMemo(() => {
      const c = document.createElement('canvas');
      c.width = 960;
      c.height = 540;
      return c;
    }, []);
    const texture = useMemo(() => {
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      return tex;
    }, [canvas]);
    const stateRef = useRef<any>(null);
    const previousBeatRef = useRef(-1);
    const smoothedEnergyRef = useRef(0);
    const dataArray = useMemo(() => new Uint8Array(128), []);

    useEffect(() => {
      stateRef.current = makeCanvasVisualizerState(kind, Math.floor(paletteHue(palette.primary) * 1000) + kind.length * 97);
    }, [palette.primary]);

    useEffect(() => {
      return () => texture.dispose();
    }, [texture]);

    useFrame(() => {
      if (!stateRef.current) return;
      if (canvas.width !== renderSize.width || canvas.height !== renderSize.height) {
        canvas.width = renderSize.width;
        canvas.height = renderSize.height;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const micEnergy = typeof params.micEnergy === 'number' ? params.micEnergy : 0;
      const micOnset = typeof params.micOnset === 'number' ? params.micOnset : 0;
      const mic = micReactivityFromParams(params);
      const beatAttack = Math.pow(Math.max(0, 1 - beat.beatPhase), 1.45);
      const pulseEnergy = Math.max(beatAttack * 0.36 * intensity, micEnergy * 1.55, micOnset * 2.25, mic.level * 0.54, mic.hit * 0.78);
      smoothedEnergyRef.current = THREE.MathUtils.lerp(smoothedEnergyRef.current, pulseEnergy, mic.hit > 0.12 ? 0.28 : 0.18);
      const energy = Math.min(1, smoothedEnergyRef.current);
      const bass = Math.min(1, Math.max(micEnergy * 1.85, mic.level * 0.72, beatAttack * intensity * 0.62));
      const mid = Math.min(1, Math.max(micOnset * 2.25, mic.hit * 0.62, Math.sin(progressMs * 0.004) * 0.18 + 0.42 + energy * 0.32));
      for (let i = 0; i < dataArray.length; i++) {
        const wave = Math.sin(i * 0.19 + progressMs * 0.007) * 0.5 + 0.5;
        const harmonic = Math.sin(i * 0.73 - progressMs * 0.004 + beat.beatIndex * 0.25) * 0.5 + 0.5;
        const laneAccent = i % 8 === beat.beatIndex % 8 ? Math.max(beatAttack, micOnset * 2, mic.hit * 0.72) : mic.level * (0.08 + seededUnit(i, 992) * 0.16);
        dataArray[i] = Math.max(0, Math.min(255, 72 + wave * 72 + harmonic * 48 + laneAccent * 110 + energy * 52 + mic.level * 32));
      }
      const hue = (paletteHue(palette.primary) + progressMs * 0.012 + energy * 42 + mic.hit * 16) % 360;
      const shared = {
        visualEnergy: energy,
        visualBass: bass,
        visualMid: mid,
        hue,
        colorIdx: Math.abs(beat.barIndex) % 7,
        isBeat: beat.isBeat || beat.beatIndex !== previousBeatRef.current || mic.hit > 0.22,
        beatStrength: Math.max(beat.beatStrength, 1 + mic.hit * 1.65 + mic.level * 0.35),
        phase: progressMs * 0.06,
      };
      previousBeatRef.current = beat.beatIndex;
      drawCanvasVisualizer(kind, ctx, canvas.width, canvas.height, dataArray, shared, stateRef.current);
      texture.needsUpdate = true;
    });

    return (
      <mesh position={[0, 0, -1.9]}>
        <planeGeometry args={[plane.width, plane.height]} />
        <meshBasicMaterial map={texture} toneMapped={false} depthWrite={false} />
      </mesh>
    );
  };
}

const SpectrumBars2D = makeTwoDScene('spectrum-bars');
const RadialEq2D = makeTwoDScene('radial-eq');
const Oscilloscope2D = makeTwoDScene('oscilloscope');
const WaveformRibbon2D = makeTwoDScene('waveform-ribbon');
const BeatKaleidoscope2D = makeTwoDScene('beat-kaleidoscope');
const LyricPoster2D = makeTwoDScene('lyric-poster');
const AlbumSlices2D = makeTwoDScene('album-slices');
const BassOrbit2D = makeTwoDScene('bass-orbit');
const GridPulse2D = makeTwoDScene('grid-pulse');
const ColorFields2D = makeTwoDScene('color-fields');
const DotMatrix2D = makeTwoDScene('dot-matrix');
const LissajousLasers2D = makeTwoDScene('lissajous-lasers');
const VinylRings2D = makeTwoDScene('vinyl-rings');
const SkylineEq2D = makeTwoDScene('skyline-eq');
const PlasmaCanvas2D = makeCanvasVisualizerScene('plasma');
const VortexCanvas2D = makeCanvasVisualizerScene('vortex');
const StrobeCanvas2D = makeCanvasVisualizerScene('strobe');
const FlashCanvas2D = makeCanvasVisualizerScene('flash');
const JourneyCanvas2D = makeCanvasVisualizerScene('journey');
const JourneyTwoCanvas2D = makeCanvasVisualizerScene('journey2');

export const SCENE_COMPONENTS = {
  'neon-tunnel': NeonTunnel,
  'rainbow-tunnel': RainbowTunnel,
  'particle-galaxy': ParticleGalaxy,
  'kinetic-typography': KineticTypography,
  'laser-grid': LaserGrid,
  'starfield-warp': StarfieldWarp,
  'sacred-geometry': SacredGeometry,
  'bass-crystal': BassCrystal,
  'festival-crowd': FestivalCrowd,
  'cyberpunk-city': CyberpunkCity,
  'lyric-constellation': LyricConstellation,
  'album-art-explosion': AlbumArtExplosion,
  'waveform-terrain': WaveformTerrain,
  'cathedral-of-light': CathedralOfLight,
  'bird-flight': BirdFlight,
  'spectrum-bars-2d': SpectrumBars2D,
  'radial-eq-2d': RadialEq2D,
  'oscilloscope-2d': Oscilloscope2D,
  'waveform-ribbon-2d': WaveformRibbon2D,
  'beat-kaleidoscope-2d': BeatKaleidoscope2D,
  'lyric-poster-2d': LyricPoster2D,
  'album-slices-2d': AlbumSlices2D,
  'bass-orbit-2d': BassOrbit2D,
  'grid-pulse-2d': GridPulse2D,
  'color-fields-2d': ColorFields2D,
  'dot-matrix-2d': DotMatrix2D,
  'lissajous-lasers-2d': LissajousLasers2D,
  'vinyl-rings-2d': VinylRings2D,
  'skyline-eq-2d': SkylineEq2D,
  'plasma-visualizer-2d': PlasmaCanvas2D,
  'vortex-visualizer-2d': VortexCanvas2D,
  'strobe-visualizer-2d': StrobeCanvas2D,
  'flash-visualizer-2d': FlashCanvas2D,
  'journey-visualizer-2d': JourneyCanvas2D,
  'journey-2-visualizer-2d': JourneyTwoCanvas2D,
  'bird-journey-2d': BirdJourney2D,
  'doodle-quest-2d': DoodleQuestScene,
} as const;
